/**
 * PaymentPackagesPage — moduł Paczki płatności.
 *
 * Architektura per-kontrahent (Agent Płatności) — 4 zakładki (2026-02 UX):
 *   Tab 1 "Przelewy"   — lista vendorów z metodą Nieokreślona/Przelew
 *                        + karta AI "Analizuj kontrahenta". Wewnętrzny
 *                        klucz stanu: `contractors` (kompatybilność testów).
 *   Tab 2 "Pozostali"  — lista vendorów z metodą Gotówka/Karta/BLIK/Online.
 *                        Bez AI. User może zmienić metodę z powrotem na
 *                        Nieokreślona/Przelew — kontrahent wraca do Przelewów.
 *   Tab 3 "Paczki"     — propozycje paczek (na górze) + paczki (na dole)
 *   Tab 4 "Bank"       — placeholder pod integrację z bankiem (mBank PSD2).
 *                        Bez logiki backendu.
 *
 * Filtr Przelewy/Pozostali jest wyłącznie UI-owy — backend nadal zwraca
 * pełną listę `GET /api/payment-packages/contractors`. Zmiana metody
 * płatności idzie przez istniejący `PUT /api/vendors/{nip}/payment-method`.
 *
 * Workflow AI (bez zmian):
 *   Wybór kontrahenta (tylko Przelewy) → Analiza AI → Propozycja paczki →
 *   user akceptuje/odrzuca → paczka draft (lub rejected)
 *
 * BEZ AI LLM w Pozostali. BEZ eksportu poza Tab "Paczki". BEZ przelewów.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronUp, ChevronRight,
  XCircle, Sparkles, Users, Wallet, ListChecks, Copy, Bug,
  HelpCircle, Banknote, CreditCard, Smartphone, Globe, Download,
  Landmark, Hammer, FileText, Trash2, MoreVertical, Search, X as XIcon,
  Eye, RefreshCw, Archive, Minus, Plus, Lock, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import InvoiceDocumentPreview from "../invoices/InvoiceDocumentPreview";
import { useInvoicePreview } from "../invoices/useInvoicePreview";
import { usePageContainer } from "@/hooks/usePageContainer";
import { usePaymentAgentReadiness } from "@/hooks/usePaymentAgentReadiness";
import PaymentAgentReadinessStatus from "@/components/payment-agent/PaymentAgentReadinessStatus";
import {
  computeReadinessVisualState,
  describeBlockingReason,
} from "@/lib/paymentAgentReadinessMessages";
import { formatDate as fmtDate, formatDueDate as fmtDueDate, formatAmount as fmtAmount } from "@/lib/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pakiet 1 (2026-02-24): centralne formattery zastępują lokalne.
// `formatAmount` obsługuje ujemne wartości (dopisek "-") i pomija currency
// gdy jest null/undefined — zachowanie zgodne z historycznym API.
const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  const isNeg = Number(amount) < 0;
  // fmtAmount z jawnym `""` nie dokleja waluty, z `undefined` (pominięcie)
  // dokleja domyślnie „PLN". Zachowujemy semantykę oryginalnego API:
  // brak `currency` = brak sufiksu.
  const body = fmtAmount(Math.abs(Number(amount)), currency ?? "");
  return `${isNeg ? "-" : ""}${body}`;
};

const formatDate = fmtDate;
const formatDueDate = fmtDueDate;

const STATUS_LABELS = {
  never_analyzed: { label: "Nieprzeanalizowany", cls: "border-gray-200 bg-gray-50 text-gray-600" },
  analyzed: { label: "Przeanalizowany", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  needs_review: { label: "Wymaga przeglądu", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  rejected: { label: "Odrzucona", cls: "border-rose-200 bg-rose-50 text-rose-700" },
  error: { label: "Błąd", cls: "border-red-200 bg-red-50 text-red-700" },
};

// --------------------------------------------------------------------------
// KPI card
// --------------------------------------------------------------------------
const KpiCard = ({ icon: Icon, label, value, sub, testId, accentClass }) => (
  <Card data-testid={testId} className="border-gray-200">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-md ${accentClass}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="font-semibold text-gray-900 text-base">{value}</div>
        {sub ? <div className="text-xs text-gray-500 mt-0.5">{sub}</div> : null}
      </div>
    </CardContent>
  </Card>
);

const BankBadge = ({ has }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
      has
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-gray-200 bg-gray-50 text-gray-500"
    }`}
    data-testid={has ? "pkg-bank-yes" : "pkg-bank-no"}
  >
    {has ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {has ? "Wyciągi" : "Brak wyciągów"}
  </span>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || STATUS_LABELS.never_analyzed;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}
      data-testid={`pkg-status-${status}`}
    >
      {s.label}
    </span>
  );
};

// --------------------------------------------------------------------------
// Panel: Log ostatniej analizy AI (Tab 1 only — Kontrahenci)
// --------------------------------------------------------------------------
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success("Skopiowano do schowka");
      return;
    }
    // Fallback: textarea + execCommand
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) toast.success("Skopiowano do schowka");
    else toast.error("Nie udało się skopiować — brak dostępu do schowka.");
  } catch (e) {
    toast.error("Nie udało się skopiować — brak dostępu do schowka.");
  }
};

const stringifyForCopy = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return String(val);
  }
};

const DebugSection = ({ title, content, testId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const text = stringifyForCopy(content);
  const empty = !text || text === "{}" || text === "[]" || text === '""';
  return (
    <div className="border border-gray-200 rounded-md bg-white" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900"
          data-testid={`${testId}-toggle`}
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {title}
          {empty ? <span className="text-xs text-gray-400 ml-1">(pusto)</span> : null}
        </button>
        <button
          type="button"
          onClick={() => copyToClipboard(text)}
          className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-indigo-700 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-indigo-50"
          data-testid={`${testId}-copy`}
          title="Kopiuj do schowka"
        >
          <Copy className="w-3 h-3" /> Kopiuj
        </button>
      </div>
      {open ? (
        <pre
          className="text-xs font-mono text-gray-800 px-3 py-2 max-h-72 overflow-auto whitespace-pre-wrap break-all bg-white"
          data-testid={`${testId}-body`}
        >
{text || "(pusto)"}
        </pre>
      ) : null}
    </div>
  );
};

const LastAnalysisDebugPanel = ({ debugInfo }) => {
  const [open, setOpen] = useState(false);
  if (!debugInfo) return null;

  const ts = debugInfo.timestamp ? formatDate(debugInfo.timestamp) : "—";
  const tsTime = debugInfo.timestamp
    ? new Date(debugInfo.timestamp).toLocaleTimeString("pl-PL")
    : "";

  return (
    <Card className="border-gray-200 bg-white" data-testid="pkg-debug-panel">
      <CardContent className="p-3 sm:p-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left"
          data-testid="pkg-debug-toggle"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-md bg-gray-100 shrink-0">
              <Bug className="w-4 h-4 text-gray-700" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">Log ostatniej analizy AI</div>
              <div className="text-xs text-gray-500 truncate">
                {debugInfo.vendor_name || "—"} · NIP {debugInfo.vendor_nip || "—"} · {ts} {tsTime}
              </div>
            </div>
          </div>
          {open ? (
            <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
          )}
        </button>

        {open ? (
          <div className="mt-3 space-y-3" data-testid="pkg-debug-body">
            {/* Skrót */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
                <div className="text-xs text-gray-500">Provider / model</div>
                <div className="font-medium text-gray-900 truncate" data-testid="pkg-debug-provider">
                  {debugInfo.provider || "—"} / {debugInfo.model || "—"}
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
                <div className="text-xs text-gray-500">Czas wykonania</div>
                <div className="font-medium text-gray-900 font-mono" data-testid="pkg-debug-time">
                  {debugInfo.execution_time_ms ?? 0} ms
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
                <div className="text-xs text-gray-500">LLM użyty</div>
                <div
                  className={`font-medium ${debugInfo.llm_used ? "text-emerald-700" : "text-gray-500"}`}
                  data-testid="pkg-debug-llm-used"
                >
                  {debugInfo.llm_used ? "tak" : "nie"}
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
                <div className="text-xs text-gray-500">Fallback</div>
                <div
                  className={`font-medium ${debugInfo.fallback_used ? "text-amber-700" : "text-gray-500"}`}
                  data-testid="pkg-debug-fallback"
                >
                  {debugInfo.fallback_used ? "tak" : "nie"}
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100 col-span-2 sm:col-span-2">
                <div className="text-xs text-gray-500">Rekomendacja</div>
                <div className="font-mono text-xs text-gray-900" data-testid="pkg-debug-recommendation">
                  {debugInfo.recommendation || "—"}
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100 col-span-2 sm:col-span-2">
                <div className="text-xs text-gray-500">Błąd parsowania</div>
                <div className="font-mono text-xs text-rose-700 break-words" data-testid="pkg-debug-parse-error">
                  {debugInfo.parse_error || "—"}
                </div>
              </div>
              <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100 col-span-2 sm:col-span-4">
                <div className="text-xs text-gray-500">Błąd LLM</div>
                <div className="font-mono text-xs text-rose-700 break-words" data-testid="pkg-debug-llm-error">
                  {debugInfo.llm_error || "—"}
                </div>
              </div>
            </div>

            {/* Rozwijane sekcje */}
            <div className="space-y-2">
              <DebugSection
                title="Prompt systemowy"
                content={debugInfo.prompt_system}
                testId="pkg-debug-prompt-system"
              />
              <DebugSection
                title="Prompt użytkownika"
                content={debugInfo.prompt_user}
                testId="pkg-debug-prompt-user"
              />
              <DebugSection
                title="Dane wejściowe"
                content={debugInfo.input_payload}
                testId="pkg-debug-input"
              />
              <DebugSection
                title="Surowa odpowiedź LLM"
                content={debugInfo.raw_response}
                testId="pkg-debug-raw"
              />
              <DebugSection
                title="Sparsowany JSON"
                content={debugInfo.parsed_json}
                testId="pkg-debug-parsed"
              />
              <DebugSection
                title="Cały log"
                content={debugInfo}
                testId="pkg-debug-full"
              />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------------------
// Karta AI "Analiza AI płatności"
// --------------------------------------------------------------------------
// Sprint 2 / E5.1 (final cleanup): mapowanie unified readiness → badge shape.
// UI Payment Agenta ma JEDNO źródło prawdy: `GET /api/payment-agent/readiness`
// (via `usePaymentAgentReadiness`). Poniższy adapter przekształca ten kontrakt
// w kompaktowy `{status, label, reasons[]}` konsumowany przez `ReadinessBadge`.
// Semantyka statusów opiera się na `computeReadinessVisualState` z
// `lib/paymentAgentReadinessMessages.js` (single source of truth dla mapowania
// kodów blockerów na wizualne bucketa preparing/not_ready/no_new_data/ready).
const BADGE_STATUS_MAP = {
  ready:              { status: "ready",     label: "Gotowe" },
  preparing:          { status: "preparing", label: "W przygotowaniu" },
  automation_waiting: { status: "preparing", label: "W przygotowaniu" },
  no_new_data:        { status: "preparing", label: "Brak nowych danych" },
  not_ready:          { status: "not_ready", label: "Nie gotowe" },
  loading:            null,
};

function derivePaxBadgeReadiness(paxReadiness) {
  if (!paxReadiness) return null;
  const { state } = computeReadinessVisualState(paxReadiness);
  const mapping = BADGE_STATUS_MAP[state];
  if (!mapping) return null;
  // Konwersja kodów blockerów na czytelne komunikaty przez centralne
  // `paymentAgentReadinessMessages.js` — bez rekonstrukcji logiki w komponencie.
  const codes = Array.isArray(paxReadiness.blocking_reasons) ? paxReadiness.blocking_reasons : [];
  const reasons = codes.map((code) => describeBlockingReason(code).message);
  return { status: mapping.status, label: mapping.label, reasons };
}

// Meta statusów readiness — mapowanie na label/kolor kropki i klasy tooltipu.
// Backend zwraca `status ∈ {ready, preparing, not_ready}` + `label` + `reasons[]`.
const READINESS_META = {
  ready:     { dotCls: "bg-emerald-500", textCls: "text-emerald-700" },
  preparing: { dotCls: "bg-amber-500",   textCls: "text-amber-700" },
  not_ready: { dotCls: "bg-rose-500",    textCls: "text-rose-700" },
};

// Popover otwierany hover (desktop) + click/tap (mobile). Radix Popover
// out-of-the-box działa tylko na click, więc dokładamy delikatny hover-open
// dla myszy — bez łamania touch-flow.
const ReadinessBadge = ({ readiness }) => {
  const [open, setOpen] = useState(false);
  if (!readiness) return null;
  const meta = READINESS_META[readiness.status] || READINESS_META.preparing;
  const label = readiness.label || "—";
  const reasons = Array.isArray(readiness.reasons) ? readiness.reasons : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          className={`inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 px-2 py-0.5 text-xs font-medium ${meta.textCls} hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-200`}
          aria-label={`Status gotowości: ${label}`}
          data-testid="pkg-analysis-readiness-badge"
          data-status={readiness.status}
        >
          <span className={`inline-block w-2 h-2 rounded-full ${meta.dotCls}`} aria-hidden="true" />
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-64 text-xs p-3"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        data-testid="pkg-analysis-readiness-popover"
      >
        {readiness.status === "ready" ? (
          <div className="text-gray-700">
            Wszystkie wymagane dane są gotowe do analizy płatności.
          </div>
        ) : reasons.length > 0 ? (
          <>
            <div className="text-gray-800 font-semibold mb-1.5">
              {readiness.status === "not_ready"
                ? "Brakuje danych do analizy:"
                : "Dane są częściowe:"}
            </div>
            <ul className="list-disc list-outside pl-4 space-y-0.5 text-gray-700">
              {reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </>
        ) : (
          <div className="text-gray-700">
            Status: <strong>{label}</strong>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const AnalysisCard = ({
  selectedCount, skippedCount, onAnalyze, analyzing, readiness, currentJob, onCancel,
  autoAnalyzeEnabled, autoAnalyzeBusy, onToggleAutoAnalyze,
}) => {
  const hasSelection = selectedCount > 0;
  const willAnalyze = Math.max(0, selectedCount - skippedCount);
  const notReady = readiness?.status === "not_ready";
  const disabled = !hasSelection || analyzing || willAnalyze === 0 || notReady;

  // 2026-07 v5: Progress z aktywnego joba (może być z refreshu strony).
  const jobProgress = currentJob && ["queued", "running"].includes(currentJob.status)
    ? { done: currentJob.done_count || 0, total: currentJob.total_count || 0, current: currentJob.current_vendor_nip }
    : null;

  const handleClick = () => {
    if (notReady) {
      const first = readiness?.reasons?.[0];
      toast.warning("Analiza niemożliwa", {
        description: first || "Brakuje wymaganych danych.",
      });
      return;
    }
    onAnalyze();
  };

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/30" data-testid="pkg-analysis-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="p-2.5 rounded-lg bg-white shadow-sm border border-indigo-100 shrink-0">
            <Sparkles className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-base font-semibold text-gray-900">Analiza AI płatności</div>
              <ReadinessBadge readiness={readiness} />
            </div>
            <div className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Twórz propozycje paczek na podstawie historii faktur i wyciągów.
            </div>
          </div>
          {analyzing ? (
            <Button
              onClick={onCancel}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 w-full sm:w-auto"
              data-testid="pkg-analyze-cancel-btn"
              disabled={currentJob?.cancel_requested}
            >
              {currentJob?.cancel_requested
                ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Anulowanie…</>
                : <>Anuluj analizę</>}
            </Button>
          ) : (
            <Button
              onClick={handleClick}
              disabled={disabled}
              className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
              data-testid="pkg-analyze-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Analizuj zaznaczonych ({willAnalyze})
            </Button>
          )}
        </div>
        {jobProgress ? (
          <div className="mt-3" data-testid="pkg-analyze-progress">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>
                <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                Analizowanie kontrahenta {jobProgress.done + 1} z {jobProgress.total}
                {jobProgress.current ? ` — NIP ${jobProgress.current}` : ""}
              </span>
              <span className="font-mono tabular-nums" data-testid="pkg-analyze-progress-counter">
                {jobProgress.done}/{jobProgress.total}
              </span>
            </div>
            <div className="w-full h-1.5 bg-indigo-100 rounded overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all"
                style={{
                  width: `${jobProgress.total > 0 ? Math.round((jobProgress.done / jobProgress.total) * 100) : 0}%`,
                }}
                data-testid="pkg-analyze-progress-bar"
              />
            </div>
          </div>
        ) : null}
        {skippedCount > 0 ? (
          <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="pkg-analyze-skipped-hint">
            Pominiętych z analizy: <strong>{skippedCount}</strong> — kontrahenci oznaczeni jako Gotówka / Karta / BLIK / Online nigdy nie trafią do paczki przelewów, więc nie wysyłamy ich do AI.
          </div>
        ) : null}
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-700" data-testid="pkg-autoanalyze-toggle-wrap">
          <input
            type="checkbox"
            id="pkg-autoanalyze-toggle"
            data-testid="pkg-autoanalyze-toggle"
            checked={!!autoAnalyzeEnabled}
            disabled={!!autoAnalyzeBusy}
            onChange={(e) => onToggleAutoAnalyze?.(e.target.checked)}
            className="cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          />
          <label htmlFor="pkg-autoanalyze-toggle" className="cursor-pointer select-none">
            Automatycznie analizuj nowych kontrahentów
          </label>
          {autoAnalyzeBusy ? (
            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------------------
// Inline panel: szybki wybór metody płatności (widoczny po zaznaczeniu
// kontrahenta w ContractorsTab). REUSE backendu — wysyła to samo żądanie
// `PUT /api/vendors/{nip}/payment-method` co `VendorAnalysisDialog`.
// --------------------------------------------------------------------------
const PAYMENT_METHOD_OPTIONS = [
  { value: "unknown",  label: "Nieokreślona", Icon: HelpCircle,
    activeCls: "bg-stone-100 border-stone-500 text-stone-800 ring-2 ring-stone-200" },
  { value: "transfer", label: "Przelew",      Icon: Banknote,
    activeCls: "bg-emerald-50 border-emerald-500 text-emerald-800 ring-2 ring-emerald-200" },
  { value: "cash",     label: "Gotówka",      Icon: Wallet,
    activeCls: "bg-amber-50 border-amber-500 text-amber-800 ring-2 ring-amber-200" },
  { value: "card",     label: "Karta",        Icon: CreditCard,
    activeCls: "bg-blue-50 border-blue-500 text-blue-800 ring-2 ring-blue-200" },
  { value: "blik",     label: "BLIK",         Icon: Smartphone,
    activeCls: "bg-violet-50 border-violet-500 text-violet-800 ring-2 ring-violet-200" },
  // 2026-02 UX polish — Online (AutoPay / eBOK / panel klienta / operatora).
  // Klasyfikacja, BEZ integracji. Agent AI traktuje analogicznie do cash/card/blik.
  { value: "online",   label: "Online",       Icon: Globe,
    activeCls: "bg-cyan-50 border-cyan-500 text-cyan-800 ring-2 ring-cyan-200" },
];

const InlinePaymentMethodPanel = ({ contractor, onChanged }) => {
  // Aktualna metoda = pole `effective_payment_method` z resolvera.
  // Pierwszy render bierze stan z serwera, kolejne — z optimistic-update.
  const initial = contractor.effective_payment_method || "unknown";
  const [current, setCurrent] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Synchronizacja gdy parent odświeży listę po PUT (props się zmienia).
  useEffect(() => {
    setCurrent(contractor.effective_payment_method || "unknown");
  }, [contractor.effective_payment_method]);

  const setMethod = async (method) => {
    if (saving || method === current) return;
    const prev = current;
    setCurrent(method); // optimistic
    setSaving(true);
    try {
      await axios.put(
        `${API}/vendors/${encodeURIComponent(contractor.vendor_nip)}/payment-method`,
        {
          payment_method: method,
          ...(contractor.vendor_name ? { vendor_name: contractor.vendor_name } : {}),
        },
        { withCredentials: true },
      );
      // Toast: jeżeli zmiana metody przeniosła kontrahenta między grupami
      // (Przelewy ↔ Pozostali), pokazujemy komunikat o przeniesieniu.
      // W przeciwnym razie — neutralny toast o zapisie.
      const prevGroup = groupLabel(prev);
      const nextGroup = groupLabel(method);
      if (prevGroup !== nextGroup) {
        toast.success(`Kontrahent został przeniesiony do zakładki ${nextGroup}.`);
      } else {
        toast.success("Metoda płatności zapisana");
      }
      if (onChanged) onChanged(contractor.vendor_nip, method);
    } catch (e) {
      setCurrent(prev); // rollback
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Nie udało się zapisać metody płatności.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-3 pt-3 border-t border-indigo-200/60"
      data-testid={`pkg-pm-panel-${contractor.vendor_nip}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2 text-xs font-medium text-gray-600">
        <span>Preferowana metoda płatności</span>
        {saving ? <Loader2 className="w-3 h-3 animate-spin text-indigo-500" /> : null}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
        {PAYMENT_METHOD_OPTIONS.map((opt) => {
          const active = current === opt.value;
          const Icon = opt.Icon;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMethod(opt.value); }}
              disabled={saving}
              data-testid={`pkg-pm-pill-${contractor.vendor_nip}-${opt.value}`}
              aria-pressed={active}
              className={`min-h-[52px] rounded-lg border-2 px-1.5 py-1.5 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors ${
                active
                  ? opt.activeCls
                  : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:bg-indigo-50/40"
              } ${saving ? "opacity-60 cursor-wait" : ""}`}
            >
              <Icon className="w-4 h-4" />
              <span className="leading-tight text-center">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// ManualPackageBuilder — tryb awaryjny ręcznego budowania paczki (2026-02)
// --------------------------------------------------------------------------
// Reużywa styl komponentu VendorInvoices z Agenta Faktur (grid 12-col,
// Round 24: Sekcja „Ręcznie oznaczone jako zapłacone" w rozwiniętym
// ManualPackageBuilder. Grupuje faktury po `manual_paid_batch_id`
// (każdy batch = jedna świadoma operacja użytkownika). Umożliwia
// cofnięcie konkretnego batcha z modalem potwierdzenia. Faktury
// korzystają z globalnego `useInvoicePreview` — pełny podgląd KSeF.
const ManuallyMarkedPaidSection = ({ batches, vendorNip, defaultCurrency, openInvoice, onUnmarked }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [unmarkModal, setUnmarkModal] = useState(null);  // { batch }
  const [unmarkSubmitting, setUnmarkSubmitting] = useState(false);
  const totalCount = batches.reduce((s, b) => s + (b.invoice_count || 0), 0);

  const confirmUnmark = useCallback(async () => {
    if (!unmarkModal?.batch || unmarkSubmitting) return;
    setUnmarkSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/payment-packages/contractors/${vendorNip}/unmark-invoices-paid`,
        { batch_id: unmarkModal.batch.batch_id },
        { withCredentials: true },
      );
      const body = r.data || {};
      if (body.unmarked_count === 0) {
        toast.info("Ta operacja została już cofnięta albo nie jest już dostępna.");
      } else {
        toast.success(`Przywrócono ${body.unmarked_count} ${body.unmarked_count === 1 ? "fakturę" : "faktur"} do przelewów.`);
      }
      setUnmarkModal(null);
      if (typeof onUnmarked === "function") await onUnmarked();
    } catch (_e) {
      toast.error("Nie udało się zmienić statusu faktur. Spróbuj ponownie.");
    } finally {
      setUnmarkSubmitting(false);
    }
  }, [unmarkModal, unmarkSubmitting, vendorNip, onUnmarked]);

  const formatBatchDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="mt-3 pt-2 border-t border-gray-200" data-testid={`pkg-manual-marked-paid-section-${vendorNip}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
        className="flex items-center justify-between w-full text-xs text-gray-500 uppercase tracking-wide mb-1.5 hover:text-gray-700"
        data-testid={`pkg-manual-marked-paid-toggle-${vendorNip}`}
      >
        <span>Ręcznie oznaczone jako zapłacone ({totalCount})</span>
        {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
      </button>
      {!collapsed && (
        <div className="space-y-3">
          {batches.map((batch) => (
            <div
              key={batch.batch_id}
              className="rounded border border-emerald-200 bg-emerald-50/40 p-2"
              data-testid={`pkg-manual-marked-batch-${batch.batch_id}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                <div className="text-xs text-gray-700">
                  <div className="font-medium">
                    {formatBatchDate(batch.marked_at)} — {batch.invoice_count} {batch.invoice_count === 1 ? "faktura" : "faktur"} — {formatAmount(batch.total_gross, batch.currency || defaultCurrency)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setUnmarkModal({ batch }); }}
                  className="text-xs px-2 py-1 rounded border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 focus:ring-2 focus:ring-emerald-400"
                  data-testid={`pkg-manual-marked-unmark-btn-${batch.batch_id}`}
                >
                  Przywróć faktury z tej operacji
                </button>
              </div>
              <div className="space-y-1">
                {batch.invoices.map((inv) => (
                  <div
                    key={inv.invoice_id}
                    className="grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-emerald-100 last:border-b-0"
                    data-testid={`pkg-manual-marked-invoice-${inv.invoice_id}`}
                  >
                    <div className="col-span-1" />
                    <div className="col-span-2 text-gray-600 tabular-nums self-center">
                      {inv.issue_date?.slice(0, 10) || "—"}
                    </div>
                    <div className="col-span-3 font-medium break-all self-center text-gray-800" title={inv.invoice_number}>
                      {inv.invoice_number ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); openInvoice(inv); }}
                          className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-0.5 -mx-0.5 break-all text-left"
                          data-testid={`pkg-manual-marked-preview-${inv.invoice_id}`}
                        >
                          <Eye className="w-3 h-3 shrink-0 opacity-70" />
                          <span>{inv.invoice_number}</span>
                        </button>
                      ) : "—"}
                    </div>
                    <div className="col-span-2 text-right font-medium tabular-nums self-center text-gray-900">
                      {formatAmount(inv.gross_amount, inv.currency || defaultCurrency)}
                    </div>
                    <div className="col-span-3 self-center">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded ring-1 text-xs font-medium bg-emerald-50 text-emerald-800 ring-emerald-200"
                        data-testid={`pkg-manual-marked-badge-${inv.invoice_id}`}
                      >
                        Ręcznie oznaczona jako zapłacona
                      </span>
                    </div>
                    <div className="col-span-1 text-xs text-gray-500 text-right self-center whitespace-nowrap" title={inv.paid_at || ""}>
                      {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("pl-PL") : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal potwierdzenia cofnięcia batcha. */}
      <Dialog open={!!unmarkModal} onOpenChange={(open) => { if (!open && !unmarkSubmitting) setUnmarkModal(null); }}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="pkg-unmark-batch-dialog"
          onEscapeKeyDown={(e) => { if (unmarkSubmitting) e.preventDefault(); }}
          onInteractOutside={(e) => { if (unmarkSubmitting) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Przywrócić faktury do przelewów?</DialogTitle>
          </DialogHeader>
          {unmarkModal?.batch && (
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                Przywrócone zostaną <strong>{unmarkModal.batch.invoice_count}</strong>{" "}
                {unmarkModal.batch.invoice_count === 1 ? "faktura" : "faktur"} należąca do operacji z dnia{" "}
                <strong>{formatBatchDate(unmarkModal.batch.marked_at)}</strong>{" "}
                o łącznej wartości{" "}
                <strong>{formatAmount(unmarkModal.batch.total_gross, unmarkModal.batch.currency || defaultCurrency)}</strong>.
              </p>
              <p className="text-gray-500">
                Nowsze i starsze operacje ręcznego oznaczenia pozostaną bez zmian.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { if (!unmarkSubmitting) setUnmarkModal(null); }}
              disabled={unmarkSubmitting}
              data-testid="pkg-unmark-batch-cancel"
            >
              Anuluj
            </Button>
            <Button
              type="button"
              onClick={confirmUnmark}
              disabled={unmarkSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
              data-testid="pkg-unmark-batch-confirm"
            >
              {unmarkSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Przywracanie…</>
              ) : "Przywróć faktury"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Manual Package Builder — inline w rozwiniętym wierszu kontrahenta.
// Renderuje listę faktur otwartych + checkbox do ręcznego wyboru + akcję
// utworzenia paczki. Kod celowo bardzo podobny do `ProposalCard` (wygląd i
// klasy Tailwind identyczne) — ale operuje na własnym endpoincie
// `GET /payment-packages/pending-invoices?seller_nip=X`, który już
// wykonuje pełną filtrację biznesową (otwarte: brak statusu / to_decide /
// urgent / partial, nie in_batch, nie paid_ksef,
// bank_account, sortowanie po due_date ASC).
//
// Nie dubluje Payment Agenta. AI nadal działa jak wcześniej — Manual
// Builder to alternatywna ścieżka dla użytkownika.
const ManualPackageBuilder = ({ vendorNip, defaultCurrency = "PLN", onCreated }) => {
  // Round 23 (Etap 1: jawna prezentacja pominiętych) — zamiast 2 grup
  // (positive/credits) budujemy 5 sekcji jawnie widocznych dla użytkownika.
  // Żadna otwarta faktura nie może już zniknąć z UI bez wyjaśnienia:
  //   1. payable         — dodatnia z IBAN (jedyne z checkboxem, dodawane do paczki)
  //   2. paidInKsef      — pominięte przez backend (Fa.Platnosc.Zaplacono='1')
  //   3. missingBank     — dodatnia bez IBAN (frontend przestał je cicho ukrywać)
  //   4. creditNotes     — kwota ujemna (korekty)
  //   5. unclassified    — bezpieczny fallback dla nieznanego kształtu
  const [groups, setGroups] = useState(null);  // null = loading
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);
  // 2026-07 v2: In-flight toggles — invoice_id currently being toggled.
  // Blokuje wielokrotne kliknięcia menu podczas request'u.
  const [toggling, setToggling] = useState(new Set());

  // Round 21: JEDYNY globalny mechanizm — context, jeden Dialog w MainLayout.
  const { openInvoice } = useInvoicePreview();

  const fetchList = useCallback(async () => {
    setError(null);
    setGroups(null);
    try {
      const r = await axios.get(
        `${API}/payment-packages/pending-invoices`,
        { params: { seller_nip: vendorNip, limit: 500 }, withCredentials: true },
      );
      const raw = r.data?.invoices || [];
      const skippedPaidItems = r.data?.skipped_paid_in_ksef?.items || [];

      // Podział `raw` na 4 grupy. `unclassified` służy jako bezpieczny fallback
      // gdyby w przyszłości backend zwrócił fakturę nie pasującą do żadnej
      // (obecnie kontrakt gwarantuje że każda faktura spełni jeden z warunków).
      const payable = [];
      const missingBank = [];
      const credits = [];
      const unclassified = [];
      for (const inv of raw) {
        const gross = Number(inv.gross_amount || 0);
        const iban = (inv.bank_account || "").trim();
        if (gross < 0) {
          credits.push(inv);
        } else if (gross >= 0 && iban !== "") {
          payable.push(inv);
        } else if (gross >= 0 && iban === "") {
          missingBank.push(inv);
        } else {
          unclassified.push({ ...inv, _reason: "unknown_shape" });
        }
      }

      setGroups({
        payable,
        paidInKsef: skippedPaidItems,
        missingBank,
        creditNotes: credits,
        unclassified,
        // Round 24 (Etap 2): sekcja „Ręcznie oznaczone jako zapłacone" —
        // grupowana po `manual_paid_batch_id` (każdy batch = jedna świadoma
        // operacja użytkownika, cofana osobno).
        manuallyMarkedBatches: r.data?.manually_marked_paid?.batches || [],
      });
      setSelected(new Set());
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać faktur.");
      setGroups({ payable: [], paidInKsef: [], missingBank: [], creditNotes: [], unclassified: [], manuallyMarkedBatches: [] });
    }
  }, [vendorNip]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // R-PAY-BULK-SELECT (2026-02-22): sync busa z bieżącym `selected` po refetch
  // oraz cleanup przy odmontowaniu — menu ⋮ nie może widzieć stale danych.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__paxSelectedInvoicesByNip = window.__paxSelectedInvoicesByNip || {};
    window.__paxSelectedInvoicesByNip[vendorNip] = selected;
    try {
      window.dispatchEvent(new CustomEvent("pax:invoice-selection-changed", {
        detail: { vendorNip, count: selected.size },
      }));
    } catch { /* noop */ }
    return () => {
      if (typeof window !== "undefined" && window.__paxSelectedInvoicesByNip) {
        delete window.__paxSelectedInvoicesByNip[vendorNip];
        try {
          window.dispatchEvent(new CustomEvent("pax:invoice-selection-changed", {
            detail: { vendorNip, count: 0 },
          }));
        } catch { /* noop */ }
      }
    };
  }, [vendorNip, selected]);

  const toggleInvoice = useCallback((invoiceId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      // R-PAY-BULK-SELECT (2026-02-22): publikuj do event busa
      // aby menu ⋮ na karcie kontrahenta widziało zaznaczenia rozwiniętej sekcji.
      if (typeof window !== "undefined") {
        window.__paxSelectedInvoicesByNip = window.__paxSelectedInvoicesByNip || {};
        window.__paxSelectedInvoicesByNip[vendorNip] = next;
        try {
          window.dispatchEvent(new CustomEvent("pax:invoice-selection-changed", {
            detail: { vendorNip, count: next.size },
          }));
        } catch { /* noop */ }
      }
      return next;
    });
  }, [vendorNip]);

  // 2026-07 v2 → 2026-02-17 upgrade (R-PAY-EXCLUSION-001):
  // Toggle trwałej flagi `excluded_from_ai_analysis`.
  // - Gdy jesteśmy w kontekście propozycji AI (`context_package_id`) →
  //   używa endpointu z audit source "payment_proposal_manual_exclusion".
  // - Inaczej (menu ⋮ z listy) → używa toggle endpointu (source "manual_menu").
  //   Oba operują na TEJ SAMEJ globalnej fladze — jedno źródło prawdy.
  const toggleAiExclusion = useCallback(async (invoiceId, contextPackageId = null, isExcluded = null) => {
    setToggling((prev) => new Set(prev).add(invoiceId));
    try {
      let res;
      let newVal;
      if (contextPackageId) {
        // Kontekst propozycji — wybierz endpoint zgodnie z bieżącym stanem.
        const endpoint = isExcluded ? "restore-invoice" : "exclude-invoice";
        res = await axios.post(
          `${API}/payment-packages/proposals/${contextPackageId}/${endpoint}`,
          { invoice_id: invoiceId },
          { withCredentials: true },
        );
        newVal = res.data?.excluded_from_ai_analysis;
      } else {
        res = await axios.post(
          `${API}/invoices/${invoiceId}/exclude-from-ai`,
          {},
          { withCredentials: true },
        );
        newVal = res.data?.excluded_from_ai_analysis;
      }
      const updater = (list) => list.map((inv) =>
        inv.invoice_id === invoiceId
          ? { ...inv, excluded_from_ai_analysis: newVal }
          : inv
      );
      setGroups((cur) => cur ? {
        ...cur,
        payable: updater(cur.payable),
        creditNotes: updater(cur.creditNotes),
        missingBank: updater(cur.missingBank),
      } : cur);
      toast.success(
        newVal
          ? "Wykluczona z analiz płatności."
          : "Przywrócona do analiz płatności.",
      );
    } catch (_e) {
      toast.error("Nie udało się zmienić stanu.");
      await fetchList();
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  }, [fetchList]);

  const selectedInvoices = useMemo(
    () => (groups?.payable || []).filter((inv) => selected.has(inv.invoice_id)),
    [groups, selected],
  );

  // 2026-02: Toggle ręcznego oznaczenia faktury jako zapłaconej.
  // Semantyka:
  //   - Endpoint idempotentny (toggle) — `status="paid"` ↔ brak statusu (null).
  //     Po 2026-02 waiting jest kompletnie usunięte; unmark = $unset status.
  //   - Niezależne od `excluded_from_ai_analysis`.
  //   - Faktura zapłacona nie idzie do Agenta Płatności ani do paczek
  //     (filtr `_match_open_invoices` w backendzie akceptuje brak statusu).
  const toggleInvoicePaid = useCallback(async (invoiceId) => {
    setToggling((prev) => new Set(prev).add(invoiceId));
    try {
      const res = await axios.post(
        `${API}/invoices/${invoiceId}/toggle-paid`,
        {},
        { withCredentials: true },
      );
      const isPaid = !!res.data?.is_paid;
      const paidAt = res.data?.paid_at || null;
      // Backend zwraca `null` po unmark (2026-02). Fallback: gdy pole
      // niezdefiniowane, przyjmujemy null (brak statusu) zamiast waiting.
      const newStatus = res.data?.status !== undefined
        ? res.data.status
        : (isPaid ? "paid" : null);
      const removedFromPackages = res.data?.removed_from_packages || [];
      const removedCount = removedFromPackages.length;
      const updater = (list) => list.map((inv) =>
        inv.invoice_id === invoiceId
          ? { ...inv, status: newStatus, paid_at: paidAt, paid_confirmation_source: res.data?.paid_confirmation_source || null }
          : inv
      );
      setGroups((cur) => cur ? {
        ...cur,
        payable: updater(cur.payable),
        creditNotes: updater(cur.creditNotes),
        missingBank: updater(cur.missingBank),
      } : cur);
      toast.success(
        isPaid
          ? (removedCount > 0
              ? `Faktura oznaczona jako zapłacona. Usunięto z ${removedCount} paczki(-ek) niewyeksportowanej(-ych).`
              : "Faktura oznaczona jako zapłacona. Nie trafi do analizy ani paczek.")
          : "Cofnięto oznaczenie „Zapłacona”. Faktura wraca do listy otwartych.",
      );
      // Faktura po zapłaceniu znika z widoku Agenta (filtr backend), więc
      // odświeżamy listę żeby ją usunąć optymistycznie.
      if (isPaid) {
        await fetchList();
      }
    } catch (_e) {
      toast.error("Nie udało się zmienić stanu „Zapłacona”.");
      await fetchList();
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  }, [fetchList]);

  const totalSelected = useMemo(
    () => selectedInvoices.reduce((s, inv) => s + Number(inv.gross_amount || 0), 0),
    [selectedInvoices],
  );

  const uniqueIbans = useMemo(
    () => new Set(selectedInvoices.map((inv) => (inv.bank_account || "").trim()).filter(Boolean)),
    [selectedInvoices],
  );
  const mixedIbans = uniqueIbans.size > 1;

  const currency = selectedInvoices[0]?.currency || defaultCurrency;

  const handleCreate = async () => {
    if (selected.size === 0) {
      toast.error("Zaznacz przynajmniej jedną fakturę.");
      return;
    }
    if (mixedIbans) {
      toast.error("Zaznaczone faktury mają różne rachunki bankowe. Wybierz faktury z jednym IBAN-em.");
      return;
    }
    setCreating(true);
    try {
      const invoice_ids = Array.from(selected);
      await axios.post(
        `${API}/payment-packages`,
        { invoice_ids },
        { withCredentials: true },
      );
      toast.success(`Utworzono paczkę ręcznie (${invoice_ids.length} faktur).`);
      setSelected(new Set());
      await fetchList();
      if (onCreated) await onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "already_paid_in_ksef_xml") {
        toast.error("Część faktur jest oznaczona w KSeF jako opłacone i została odrzucona.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (e?.response?.status === 409) {
        toast.error("Część faktur jest już w innej paczce. Odśwież listę.");
      } else {
        toast.error("Nie udało się utworzyć paczki.");
      }
      await fetchList();
    } finally {
      setCreating(false);
    }
  };

  if (groups === null) {
    return (
      <div
        className="px-3 py-2 border-t border-gray-100 bg-gray-50/60"
        data-testid={`pkg-manual-builder-loading-${vendorNip}`}
      >
        <div className="text-xs text-gray-500">
          <Loader2 className="w-3 h-3 inline animate-spin mr-1.5" />Ładowanie faktur…
        </div>
      </div>
    );
  }

  const { payable, paidInKsef, missingBank, creditNotes, unclassified, manuallyMarkedBatches } = groups;
  const anyRows = payable.length + paidInKsef.length + missingBank.length + creditNotes.length + unclassified.length + (manuallyMarkedBatches?.length || 0);

  if (error && anyRows === 0) {
    return (
      <div
        className="px-3 py-2 border-t border-gray-100 bg-gray-50/60"
        data-testid={`pkg-manual-builder-error-${vendorNip}`}
      >
        <div className="text-xs text-red-600">{error}</div>
      </div>
    );
  }

  // Wspólny helper: wiersz oznaczony jako wykluczony z analizy AI.
  // Reguła UX: delikatny czerwony ring + napis, bez przesadnego koloru.
  const rowClassForExcluded = (isExc, extraActive = false) =>
    isExc
      ? "bg-red-50/40 ring-1 ring-red-200 rounded"
      : (extraActive ? "bg-indigo-50/40" : "hover:bg-white");

  // Wspólny dropdown ⋮ (używany dla faktur dodatnich i korekt).
  // Dwa niezależne stany:
  //  1. Wyklucz z analiz płatności ↔ Przywróć do analiz płatności (`excluded_from_ai_analysis`).
  //  2. Oznacz jako zapłaconą ↔ Cofnij oznaczenie „Zapłacona" (`status=paid` toggle).
  // Zapłacona faktura znika z widoku (filtr `_match_open_invoices` w backendzie),
  // więc opcja „Cofnij" pokazuje się tylko dla faktur, które faktycznie mają
  // `status=paid` (rzadko widoczne w tym widoku, ale kompletność UI).
  const renderRowMenu = (inv) => {
    const isExc = !!inv.excluded_from_ai_analysis;
    const isPaid = String(inv.status || "").toLowerCase() === "paid";
    const busy = toggling.has(inv.invoice_id);
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={busy}
            data-testid={`pkg-manual-row-menu-${inv.invoice_id}`}
            aria-label="Menu wiersza"
          >
            {busy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <MoreVertical className="w-3.5 h-3.5" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isExc ? (
            <DropdownMenuItem
              onClick={() => toggleAiExclusion(inv.invoice_id)}
              data-testid={`pkg-manual-menu-restore-${inv.invoice_id}`}
            >
              Przywróć do analiz płatności
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => toggleAiExclusion(inv.invoice_id)}
              className="text-red-700 focus:text-red-800 focus:bg-red-50"
              data-testid={`pkg-manual-menu-exclude-${inv.invoice_id}`}
            >
              Wyklucz z analiz płatności
            </DropdownMenuItem>
          )}
          {isPaid ? (
            <DropdownMenuItem
              onClick={() => toggleInvoicePaid(inv.invoice_id)}
              data-testid={`pkg-manual-menu-unmark-paid-${inv.invoice_id}`}
            >
              Cofnij oznaczenie „Zapłacona”
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => toggleInvoicePaid(inv.invoice_id)}
              className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
              data-testid={`pkg-manual-menu-mark-paid-${inv.invoice_id}`}
            >
              Oznacz jako zapłaconą
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Etykieta „Wykluczona z analiz płatności" — używana pod wierszem.
  const renderExcludedIndicator = (isExc) => isExc ? (
    <div
      className="col-span-12 text-xs text-red-700 px-2 pb-1"
      data-testid="pkg-manual-row-excluded-label"
    >
      Wykluczona z analiz płatności
    </div>
  ) : null;

  // Round 23 (Etap 1): wspólny wiersz info-only dla sekcji "OZNACZONE
  // W KSEF JAKO ZAPŁACONE", "BRAK RACHUNKU BANKOWEGO", "KOREKTY" i
  // "WYMAGAJĄ WERYFIKACJI". Bez checkboxa (faktura nie może być dodana
  // do paczki). Klik na numer faktury otwiera globalny podgląd przez
  // `openInvoice` z `InvoicePreviewContext` — tak samo jak dla payable.
  const renderInfoRow = (inv, {
    testidPrefix,
    badgeText,
    badgeTone,
    thirdCol,
  }) => {
    const isExc = !!inv.excluded_from_ai_analysis;
    const badgeCls = ({
      emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200",
      amber:   "bg-amber-50 text-amber-800 ring-amber-200",
      rose:    "bg-rose-50 text-rose-800 ring-rose-200",
      slate:   "bg-slate-50 text-slate-700 ring-slate-200",
    })[badgeTone] || "bg-slate-50 text-slate-700 ring-slate-200";
    return (
      <div
        key={inv.invoice_id}
        className={`grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0 ${rowClassForExcluded(isExc)}`}
        data-testid={`${testidPrefix}-${inv.invoice_id}`}
      >
        <div className="col-span-1" />
        <div className="col-span-2 text-gray-600 tabular-nums self-center">
          {inv.issue_date?.slice(0, 10) || "—"}
        </div>
        <div className={`col-span-3 font-medium break-all self-center ${isExc ? "text-gray-700" : "text-gray-800"}`} title={inv.invoice_number}>
          {inv.invoice_number ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openInvoice(inv);
              }}
              className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-0.5 -mx-0.5 break-all text-left"
              title="Pełny podgląd faktury"
              aria-label={`Pokaż podgląd faktury ${inv.invoice_number}`}
              data-testid={`${testidPrefix}-preview-${inv.invoice_id}`}
            >
              <Eye className="w-3 h-3 shrink-0 opacity-70" />
              <span>{inv.invoice_number}</span>
            </button>
          ) : (
            <span>—</span>
          )}
        </div>
        <div className={`col-span-2 text-right font-medium tabular-nums self-center ${isExc ? "text-gray-700" : "text-gray-900"}`}>
          {formatAmount(inv.gross_amount, inv.currency || defaultCurrency)}
        </div>
        <div className="col-span-3 self-center">
          {thirdCol !== undefined ? thirdCol : (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded ring-1 text-xs font-medium ${badgeCls}`}
              data-testid={`${testidPrefix}-badge-${inv.invoice_id}`}
            >
              {badgeText}
            </span>
          )}
        </div>
        <div className="col-span-1" />
        {renderExcludedIndicator(isExc)}
      </div>
    );
  };

  return (
    <div
      className="px-3 py-2 border-t border-gray-100 bg-gray-50/60"
      data-testid={`pkg-manual-builder-${vendorNip}`}
    >
      {/* SEKCJA 1: FAKTURY DO PRZELEWU (payable). Jedyne z checkboxem. */}
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
        Faktury do przelewu ({payable.length})
      </div>

      {payable.length === 0 ? (
        <div className="text-xs text-gray-500 py-2" data-testid={`pkg-manual-builder-empty-${vendorNip}`}>
          Brak faktur oczekujących na przelew dla tego kontrahenta.
        </div>
      ) : (
        <div className="space-y-1">
          {payable.map((inv) => {
            const isSel = selected.has(inv.invoice_id);
            const isExc = !!inv.excluded_from_ai_analysis;
            return (
              <div
                key={inv.invoice_id}
                className={`grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0 ${rowClassForExcluded(isExc, isSel)}`}
                data-testid={`pkg-manual-invoice-${inv.invoice_id}`}
              >
                <div className="col-span-1 flex items-center">
                  {/* Checkbox — WYŁĄCZNIE ręczna paczka. Niezależny od wykluczenia AI. */}
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleInvoice(inv.invoice_id)}
                    className="cursor-pointer"
                    data-testid={`pkg-manual-invoice-cb-${inv.invoice_id}`}
                    title="Dodaj do ręcznej paczki"
                  />
                </div>
                <div className="col-span-2 text-gray-600 tabular-nums self-center">
                  {inv.issue_date?.slice(0, 10) || "—"}
                </div>
                <div className={`col-span-3 font-medium break-all self-center ${isExc ? "text-gray-700" : "text-gray-800"}`} title={inv.invoice_number}>
                  {inv.invoice_number ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openInvoice(inv);
                      }}
                      className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-0.5 -mx-0.5 break-all text-left"
                      title="Pełny podgląd faktury"
                      aria-label={`Pokaż podgląd faktury ${inv.invoice_number}`}
                      data-testid={`pkg-manual-invoice-preview-${inv.invoice_id}`}
                    >
                      <Eye className="w-3 h-3 shrink-0 opacity-70" />
                      <span>{inv.invoice_number}</span>
                    </button>
                  ) : (
                    <span>—</span>
                  )}
                </div>
                <div className={`col-span-2 text-right font-medium tabular-nums self-center ${isExc ? "text-gray-700" : "text-gray-900"}`}>
                  {formatAmount(inv.gross_amount, inv.currency || defaultCurrency)}
                </div>
                <div
                  className="col-span-3 text-gray-500 font-mono text-xs break-all self-center"
                  title={inv.bank_account || ""}
                >
                  {inv.bank_account || "—"}
                </div>
                <div className="col-span-1 flex items-center justify-end self-center">
                  {renderRowMenu(inv)}
                </div>
                {renderExcludedIndicator(isExc)}
              </div>
            );
          })}
        </div>
      )}

      {/* SEKCJA 2: OZNACZONE W KSEF JAKO ZAPŁACONE.
          Round 23 Etap 1: backend zwraca `skipped_paid_in_ksef.items`
          (do tej pory ignorowane przez frontend). Wyświetlamy jawnie
          z badge "Zapłacono wg KSeF" i opisem. Bez checkboxa. */}
      {paidInKsef.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200" data-testid={`pkg-manual-paid-ksef-section-${vendorNip}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Oznaczone w KSeF jako zapłacone ({paidInKsef.length})
          </div>
          <div className="text-xs text-gray-600 mb-1.5 italic">
            Ta faktura została oznaczona w KSeF jako zapłacona i dlatego nie została dodana do listy przelewów.
          </div>
          <div className="space-y-1">
            {paidInKsef.map((inv) => renderInfoRow(inv, {
              testidPrefix: "pkg-manual-paid-ksef",
              badgeText: "Zapłacono wg KSeF",
              badgeTone: "emerald",
            }))}
          </div>
        </div>
      )}

      {/* SEKCJA 3: BRAK RACHUNKU BANKOWEGO.
          Round 23 Etap 1: frontend do tej pory cicho ukrywał faktury
          z pustym IBAN. Teraz są widoczne z badge i opisem. Bez checkboxa. */}
      {missingBank.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200" data-testid={`pkg-manual-missing-bank-section-${vendorNip}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Brak rachunku bankowego ({missingBank.length})
          </div>
          <div className="text-xs text-gray-600 mb-1.5 italic">
            Faktura nie może zostać dodana do paczki ponieważ nie posiada rachunku bankowego.
          </div>
          <div className="space-y-1">
            {missingBank.map((inv) => renderInfoRow(inv, {
              testidPrefix: "pkg-manual-missing-bank",
              badgeText: "Brak rachunku bankowego",
              badgeTone: "amber",
            }))}
          </div>
        </div>
      )}

      {/* SEKCJA 4: KOREKTY / NIE PŁACIĆ. Bez checkboxa. */}
      {creditNotes.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200" data-testid={`pkg-manual-credits-section-${vendorNip}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
            Korekty / nie płacić ({creditNotes.length})
          </div>
          <div className="space-y-1">
            {creditNotes.map((inv) => {
              const isExc = !!inv.excluded_from_ai_analysis;
              return (
                <div
                  key={inv.invoice_id}
                  className={`grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0 ${rowClassForExcluded(isExc)}`}
                  data-testid={`pkg-manual-creditnote-${inv.invoice_id}`}
                >
                  <div className="col-span-1" />
                  <div className="col-span-2 text-gray-600 tabular-nums self-center">
                    {inv.issue_date?.slice(0, 10) || "—"}
                  </div>
                  <div className={`col-span-3 font-medium break-all self-center ${isExc ? "text-gray-700" : "text-gray-800"}`} title={inv.invoice_number}>
                    {inv.invoice_number ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openInvoice(inv);
                        }}
                        className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-0.5 -mx-0.5 break-all text-left"
                        title="Pełny podgląd faktury"
                        aria-label={`Pokaż podgląd faktury ${inv.invoice_number}`}
                        data-testid={`pkg-manual-creditnote-preview-${inv.invoice_id}`}
                      >
                        <Eye className="w-3 h-3 shrink-0 opacity-70" />
                        <span>{inv.invoice_number}</span>
                      </button>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div className={`col-span-2 text-right font-medium tabular-nums self-center ${isExc ? "text-gray-700" : "text-amber-700"}`}>
                    {formatAmount(inv.gross_amount, inv.currency || defaultCurrency)}
                  </div>
                  <div className="col-span-3 self-center">
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded ring-1 text-xs font-medium bg-rose-50 text-rose-800 ring-rose-200"
                      data-testid={`pkg-manual-creditnote-badge-${inv.invoice_id}`}
                    >
                      Korekta
                    </span>
                  </div>
                  <div className="col-span-1 flex items-center justify-end self-center">
                    {renderRowMenu(inv)}
                  </div>
                  {renderExcludedIndicator(isExc)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SEKCJA 5: WYMAGAJĄ WERYFIKACJI (bezpieczny fallback).
          Round 23 Etap 1: nigdy nie ukrywamy faktury bez przypisania do
          konkretnej sekcji. W praktyce kontrakt gwarantuje że każda
          faktura trafi do payable/paidInKsef/missingBank/creditNotes,
          ale gdyby w przyszłości backend zwrócił nieznany kształt,
          użytkownik ją zobaczy zamiast domysłów. */}
      {unclassified.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200" data-testid={`pkg-manual-unclassified-section-${vendorNip}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Wymagają weryfikacji ({unclassified.length})
          </div>
          <div className="text-xs text-gray-600 mb-1.5 italic">
            Backend zwrócił fakturę, której nie udało się jednoznacznie przypisać do żadnej z powyższych sekcji.
          </div>
          <div className="space-y-1">
            {unclassified.map((inv) => renderInfoRow(inv, {
              testidPrefix: "pkg-manual-unclassified",
              badgeText: "Wymaga weryfikacji",
              badgeTone: "slate",
            }))}
          </div>
        </div>
      )}

      {/* Round 24: SEKCJA „RĘCZNIE OZNACZONE JAKO ZAPŁACONE" — grupy po batchId. */}
      {(manuallyMarkedBatches?.length || 0) > 0 && (
        <ManuallyMarkedPaidSection
          batches={manuallyMarkedBatches}
          vendorNip={vendorNip}
          defaultCurrency={defaultCurrency}
          openInvoice={openInvoice}
          onUnmarked={fetchList}
        />
      )}

      {payable.length > 0 ? (
        <div className="mt-2.5 pt-2 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-xs text-gray-700" data-testid={`pkg-manual-summary-${vendorNip}`}>
            Wybrano: <strong data-testid={`pkg-manual-count-${vendorNip}`}>{selected.size} faktur</strong>
            {selected.size > 0 ? (
              <>
                {" · Łączna kwota: "}
                <strong className="font-mono tabular-nums" data-testid={`pkg-manual-total-${vendorNip}`}>
                  {formatAmount(totalSelected, currency)}
                </strong>
              </>
            ) : null}
            {mixedIbans ? (
              <div className="mt-1 text-xs text-rose-700" data-testid={`pkg-manual-mixed-iban-${vendorNip}`}>
                Uwaga: zaznaczone faktury mają różne rachunki bankowe.
              </div>
            ) : null}
          </div>
          <Button
            onClick={handleCreate}
            disabled={creating || selected.size === 0 || mixedIbans}
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300 w-full sm:w-auto"
            data-testid={`pkg-manual-create-${vendorNip}`}
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Package className="w-4 h-4 mr-1.5" />}
            Utwórz paczkę ręcznie
          </Button>
        </div>
      ) : null}

      {/* Round 21: Dialog podglądu faktury renderowany globalnie w
          `InvoicePreviewProvider` (MainLayout). Tu tylko wywołujemy
          `openInvoice(inv)` z contextu. */}
    </div>
  );
};

// --------------------------------------------------------------------------
// Tab 1: Kontrahenci
// --------------------------------------------------------------------------
// Grupy metod płatności — kanoniczny podział używany do filtrowania listy
// pomiędzy zakładkami „Przelewy" (TRANSFER_METHODS) a „Pozostali" (OTHERS_METHODS).
// AI analizuje WYŁĄCZNIE `transfer` oraz `unknown` (te ostatnie wymagają
// klasyfikacji), stąd `NON_TRANSFER_METHODS === OTHERS_METHODS`.
const TRANSFER_METHODS = new Set(["unknown", "transfer"]);
const OTHERS_METHODS = new Set(["cash", "card", "blik", "online"]);
// Alias historyczny — zachowany żeby nie ruszać innych referencji w pliku.
const NON_TRANSFER_METHODS = OTHERS_METHODS;

// 2026-02 unify: kanoniczny predykat „kontrahent poza workflow przelewów".
// Bierze pod uwagę OBA źródła klasyfikacji, których dostarcza backend
// (`services.vendor_workflow_status`):
//   1) `workflow_status === "excluded_by_payment_method"` — pokrywa zarówno
//      klasyczny case (metoda cash/card/blik/online) jak i nową regułę
//      „wszystkie faktury zapłacone w KSeF" (bez modyfikacji payment_method).
//   2) `effective_payment_method ∈ OTHERS_METHODS` — fallback dla starych
//      response'ów bez pola `workflow_status`.
const isInOthersTab = (c) => {
  if (c?.workflow_status === "excluded_by_payment_method") return true;
  return OTHERS_METHODS.has(c?.effective_payment_method || "");
};

// Etykieta zakładki dla toastu po przeniesieniu kontrahenta między grupami.
const groupLabel = (method) =>
  TRANSFER_METHODS.has(method) ? "Przelewy" : "Pozostali";

// ─────────────────────────────────────────────────────────────────────────
// Search + Sort — utilities (2026-02, feature „Wyszukiwarka + sortowanie
// w zakładce Przelewy"). Wyłącznie funkcje pomocnicze, żadnych requestów
// do backendu. Filtracja/sortowanie odbywa się w pamięci frontendu.
// ─────────────────────────────────────────────────────────────────────────

// Normalizuje tekst pod porównanie odporne na wielkość liter i polskie
// znaki diakrytyczne. Przykład: "Żabka" → "zabka", "ĆMA" → "cma".
const normalizeSearchText = (val) => {
  if (val === null || val === undefined) return "";
  return String(val).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
};

// Kanoniczna postać numeru rachunku do porównywania — same znaki
// alfanumeryczne, wielkie litery. Obsługuje warianty ze spacjami i
// prefiksem PL / bez.
const normalizeIbanForSearch = (val) => {
  if (val === null || val === undefined) return "";
  return String(val).replace(/[^0-9A-Za-z]/g, "").toUpperCase();
};

// Zwraca `true` gdy kontrahent pasuje do zapytania (nazwa | NIP | IBAN).
// Zapytanie normalizujemy dwukrotnie: raz tekstowo (nazwa) i raz do
// postaci IBAN-owej (NIP / rachunek).
const contractorMatchesQuery = (contractor, query) => {
  const q = normalizeSearchText(query);
  if (!q) return true;
  const nameHit = normalizeSearchText(contractor.vendor_name || "").includes(q);
  if (nameHit) return true;
  const qIban = normalizeIbanForSearch(query);
  if (qIban) {
    const nipNorm = normalizeIbanForSearch(contractor.vendor_nip || "");
    if (nipNorm.includes(qIban)) return true;
    const ibans = Array.isArray(contractor.known_ibans) ? contractor.known_ibans : [];
    if (ibans.some((ib) => normalizeIbanForSearch(ib).includes(qIban))) return true;
  }
  return false;
};

// Stabilne sortowanie alfabetyczne wg lokalizacji polskiej. `direction`
// przyjmuje "asc" (A→Z, default) lub "desc" (Z→A).
const sortContractorsByName = (arr, direction = "asc") => {
  const copy = [...arr];
  copy.sort((a, b) => {
    const nameA = (a.vendor_name || "").trim();
    const nameB = (b.vendor_name || "").trim();
    const cmp = nameA.localeCompare(nameB, "pl", { sensitivity: "base" });
    return direction === "desc" ? -cmp : cmp;
  });
  return copy;
};

// Toolbar (search + sort) — widoczny tylko w zakładce „Przelewy".
// Wysyła zmiany do rodzica przez callbacki. Nie wykonuje żadnych requestów.
const ContractorsSearchToolbar = ({
  search, onSearchChange, sortOrder, onSortOrderChange, suggestions, onPickSuggestion,
}) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const blurTimerRef = useRef(null);

  const showSuggestions = focused && normalizeSearchText(search).length >= 3 && suggestions.length > 0;

  const handleClear = () => {
    onSearchChange("");
    // Fokus zostaje w polu (spec pkt 4).
    if (inputRef.current) inputRef.current.focus();
  };

  const handleBlur = () => {
    // Delay zamknięcia by kliknięcie na sugestię zdążyło się zarejestrować.
    blurTimerRef.current = setTimeout(() => setFocused(false), 120);
  };
  const handleFocus = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setFocused(true);
  };

  return (
    <div className="relative">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="pkg-transfer-search-toolbar">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Szukaj kontrahenta"
            aria-label="Szukaj kontrahenta"
            className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            data-testid="pkg-transfer-search-input"
          />
          {search ? (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 rounded"
              aria-label="Wyczyść wyszukiwanie"
              data-testid="pkg-transfer-search-clear"
            >
              <XIcon className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-500 shrink-0 sm:min-w-[180px]">
          <span className="hidden sm:inline">Sortowanie</span>
          <select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value)}
            className="w-full sm:w-auto h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            aria-label="Sortowanie"
            data-testid="pkg-transfer-sort-select"
          >
            <option value="asc">A → Z</option>
            <option value="desc">Z → A</option>
          </select>
        </label>
      </div>

      {showSuggestions ? (
        <div
          className="absolute z-30 left-0 right-0 sm:right-auto sm:w-[min(100%,32rem)] mt-1 rounded-lg border border-gray-200 bg-white shadow-lg max-h-72 overflow-y-auto"
          data-testid="pkg-transfer-search-suggestions"
        >
          <ul className="py-1">
            {suggestions.slice(0, 8).map((c) => (
              <li key={c.vendor_nip}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onPickSuggestion(c);
                    if (inputRef.current) inputRef.current.blur();
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none"
                  data-testid={`pkg-transfer-search-suggestion-${c.vendor_nip}`}
                >
                  <div className="text-sm text-gray-900 truncate">{c.vendor_name || "—"}</div>
                  <div className="text-xs text-gray-500 font-mono truncate">NIP: {c.vendor_nip}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const ContractorsTab = ({ mode = "transfer", contractors, loading, onAnalyze, analyzing, lastAnalysisDebugInfo, onMethodChanged, onPackageCreated, readiness, currentJob, onCancelAnalysis, autoAnalyzeEnabled, autoAnalyzeBusy, onToggleAutoAnalyze }) => {
  const [selected, setSelected] = useState(new Set());
  const isTransferMode = mode === "transfer";

  // Round 21: JEDYNY mechanizm podglądu faktury — globalny context.
  const { openInvoice } = useInvoicePreview();

  // R-PAY-BULK-SELECT (2026-02-22): re-render menu ⋮ przy zmianie zaznaczenia
  // faktur w rozwiniętej sekcji (`PayableList` publikuje do event busa).
  const [, setSelectionTick] = useState(0);
  useEffect(() => {
    const handler = () => setSelectionTick((t) => t + 1);
    if (typeof window !== "undefined") {
      window.addEventListener("pax:invoice-selection-changed", handler);
      return () => window.removeEventListener("pax:invoice-selection-changed", handler);
    }
    return undefined;
  }, []);

  // Search + Sort — wyłącznie zakładka „Przelewy". Filtracja/sortowanie
  // odbywa się w pamięci na `contractors` już pobranych z backendu.
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Lista przefiltrowana + posortowana. Kolejność (spec pkt 2):
  //   1) filtr — aktywny gdy `search.length >= 3` (spec pkt 1),
  //   2) sort — zawsze wg wybranego kierunku (default „asc" = A→Z).
  const visibleContractors = useMemo(() => {
    if (!isTransferMode) return contractors;
    const q = normalizeSearchText(search);
    const filtered = q.length >= 3
      ? contractors.filter((c) => contractorMatchesQuery(c, search))
      : contractors;
    return sortContractorsByName(filtered, sortOrder);
  }, [contractors, isTransferMode, search, sortOrder]);

  // Podpowiedzi widoczne w toolbarze — pochodzą z tej samej filtrowanej listy,
  // ale przed sortowaniem (spec: pole działa podobnie do wyszukiwarki bankowej,
  // pokazujemy najbliższe trafienia). Wystarczy top-8 z widocznej listy.
  const searchSuggestions = useMemo(() => {
    if (!isTransferMode) return [];
    if (normalizeSearchText(search).length < 3) return [];
    return visibleContractors.slice(0, 8);
  }, [isTransferMode, search, visibleContractors]);

  const toggle = (nip) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nip)) next.delete(nip);
      else next.add(nip);
      return next;
    });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Round 24 — bulk mark faktur kontrahenta jako zapłaconych (menu ⋮).
  // ══════════════════════════════════════════════════════════════════════════
  // Stan modala: { contractor, eligibleCount, eligibleTotal, currency, loading }.
  const [markModal, setMarkModal] = useState(null);
  const [markSubmitting, setMarkSubmitting] = useState(false);

  const openMarkModal = useCallback(async (contractor, options = {}) => {
    // R-PAY-BULK-SELECT (2026-02-22): `options.invoiceIds` — tryb selektywny.
    // Brak → tryb legacy (wszystkie kwalifikujące się).
    const selectiveIds = Array.isArray(options.invoiceIds) ? options.invoiceIds : null;
    setMarkModal({
      contractor, loading: true,
      eligibleCount: 0, eligibleTotal: 0, currency: "PLN",
      selectiveIds,
    });
    try {
      const r = await axios.get(
        `${API}/payment-packages/pending-invoices`,
        { params: { seller_nip: contractor.vendor_nip, limit: 500 }, withCredentials: true },
      );
      const raw = r.data?.invoices || [];
      // Zaawężenie do wybranych ID (tylko tryb selektywny) — inaczej wszystkie.
      const filtered = selectiveIds
        ? raw.filter((inv) => selectiveIds.includes(inv.invoice_id))
        : raw;
      const eligible = filtered.filter((inv) => Number(inv.gross_amount || 0) >= 0);
      const total = eligible.reduce((s, inv) => s + Number(inv.gross_amount || 0), 0);
      const currency = eligible[0]?.currency || contractor.currencies?.[0] || "PLN";
      setMarkModal({
        contractor, loading: false,
        eligibleCount: eligible.length,
        eligibleTotal: total,
        currency,
        selectiveIds,
      });
    } catch (_e) {
      setMarkModal({ contractor, loading: false, eligibleCount: 0, eligibleTotal: 0, currency: "PLN", error: true, selectiveIds });
    }
  }, []);

  const closeMarkModal = useCallback(() => {
    if (markSubmitting) return;  // ochrona: nie zamykaj podczas request'u
    setMarkModal(null);
  }, [markSubmitting]);

  const confirmMark = useCallback(async () => {
    if (!markModal?.contractor || markSubmitting) return;
    setMarkSubmitting(true);
    try {
      const selectiveIds = markModal.selectiveIds || null;
      const payload = selectiveIds ? { invoice_ids: selectiveIds } : {};
      const r = await axios.post(
        `${API}/payment-packages/contractors/${markModal.contractor.vendor_nip}/mark-invoices-paid`,
        payload,
        { withCredentials: true },
      );
      const body = r.data || {};
      if (body.marked_count === 0) {
        toast.info("Brak aktualnych faktur możliwych do oznaczenia jako zapłacone.");
      } else {
        toast.success(`Oznaczono ${body.marked_count} ${body.marked_count === 1 ? "fakturę" : "faktur"} jako zapłacone.`);
      }
      setMarkModal(null);
      // Odświeżenie listy kontrahentów oraz sekcji w rozwiniętym MPB.
      if (typeof onPackageCreated === "function") onPackageCreated();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        toast.error("Stan faktur zmienił się. Odświeżono dane kontrahenta.");
        if (typeof onPackageCreated === "function") onPackageCreated();
        setMarkModal(null);
      } else {
        toast.error("Nie udało się zmienić statusu faktur. Spróbuj ponownie.");
      }
    } finally {
      setMarkSubmitting(false);
    }
  }, [markModal, markSubmitting, onPackageCreated]);

  // Menu ⋮ na WIERSZU KONTRAHENTA — WYŁĄCZNIE w zakładce Przelewy.
  // `variant` odróżnia render desktop/mobile aby data-testid był unikalny.
  //
  // R-PAY-BULK-SELECT (2026-02-22):
  //   * DropdownMenu jest `modal={false}` — nie ustawia scroll locka na
  //     body. Scroll strony może zamknąć menu naturalnie (bez lockdown).
  //   * Nowa akcja „Oznacz wybrane" wykorzystuje event bus (`window.__paxSelectedInvoicesByNip`)
  //     synchronizowany z rozwiniętą listą faktur (PayableList).
  //     Ilość zaznaczeń per-NIP odczytywana on-render.
  const renderContractorMenu = (c, variant = "desktop") => {
    // Kwalifikacja opcji „Oznacz jako zapłacone": karta ma otwarte faktury
    // (`invoice_count_open > 0`). Backend zweryfikuje ponownie.
    const hasCandidates = (c.invoice_count_open || 0) > 0;
    const suffix = variant === "mobile" ? "-mobile" : "";
    // Odczyt aktualnie zaznaczonych faktur tego kontrahenta z event busa.
    const bus = (typeof window !== "undefined" && window.__paxSelectedInvoicesByNip) || {};
    const selectedForNip = (bus[c.vendor_nip] instanceof Set) ? bus[c.vendor_nip] : new Set();
    const selectedCount = selectedForNip.size;
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); }}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            data-testid={`pkg-contractor-menu${suffix}-${c.vendor_nip}`}
            aria-label={`Menu kontrahenta ${c.vendor_name || c.vendor_nip}`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            disabled={selectedCount === 0}
            onSelect={(e) => {
              e.preventDefault();
              if (selectedCount > 0) {
                openMarkModal(c, { invoiceIds: Array.from(selectedForNip) });
              }
            }}
            data-testid={`pkg-contractor-menu-mark-selected${suffix}-${c.vendor_nip}`}
            title={selectedCount === 0 ? "Zaznacz faktury w rozwiniętej sekcji." : undefined}
          >
            {selectedCount > 0
              ? `Oznacz wybrane jako zapłacone (${selectedCount})`
              : "Oznacz wybrane jako zapłacone"}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!hasCandidates}
            onSelect={(e) => {
              e.preventDefault();
              if (hasCandidates) openMarkModal(c);
            }}
            data-testid={`pkg-contractor-menu-mark-paid${suffix}-${c.vendor_nip}`}
            title={hasCandidates ? undefined : "Brak aktualnych faktur do oznaczenia jako zapłacone."}
          >
            Oznacz aktualne faktury jako zapłacone
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Po zmianie metody w inline-panelu odznacz cash/card/blik z analizy
  // (zostaje zaznaczony, ale licznik "Analizuj zaznaczonych" go pominie).
  const selectedContractors = useMemo(
    () => contractors.filter((c) => selected.has(c.vendor_nip)),
    [contractors, selected],
  );

  const skippedCount = useMemo(
    () => selectedContractors.filter((c) => isInOthersTab(c)).length,
    [selectedContractors],
  );

  const handleAnalyze = async () => {
    const toAnalyze = selectedContractors.filter((c) => !isInOthersTab(c));
    if (toAnalyze.length === 0) return;
    if (skippedCount > 0) {
      toast.info(
        `Pominięto ${skippedCount} kontrahentów spoza workflow przelewów (Gotówka/Karta/BLIK/Online lub bez niezapłaconych faktur).`
      );
    }
    await onAnalyze(toAnalyze.map((c) => c.vendor_nip));
    setSelected(new Set());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid={`pkg-${mode}-loading`}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Wczytywanie kontrahentów…
      </div>
    );
  }

  if (contractors.length === 0) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50" data-testid={`pkg-${mode}-empty`}>
        <CardContent className="p-8 text-center text-gray-500">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          {isTransferMode
            ? "Brak kontrahentów do wyświetlenia."
            : "Brak kontrahentów oznaczonych jako Gotówka, Karta, BLIK lub Online."}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {isTransferMode ? (
        <>
          <AnalysisCard
            selectedCount={selected.size}
            skippedCount={skippedCount}
            onAnalyze={handleAnalyze}
            analyzing={analyzing}
            readiness={readiness}
            currentJob={currentJob}
            onCancel={onCancelAnalysis}
            autoAnalyzeEnabled={autoAnalyzeEnabled}
            autoAnalyzeBusy={autoAnalyzeBusy}
            onToggleAutoAnalyze={onToggleAutoAnalyze}
          />
          <LastAnalysisDebugPanel debugInfo={lastAnalysisDebugInfo} />
          <ContractorsSearchToolbar
            search={search}
            onSearchChange={setSearch}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
            suggestions={searchSuggestions}
            onPickSuggestion={(c) => setSearch(c.vendor_name || "")}
          />
        </>
      ) : null}

      {isTransferMode && visibleContractors.length === 0 ? (
        <Card className="border-dashed border-gray-300 bg-gray-50" data-testid="pkg-transfer-search-empty">
          <CardContent className="p-8 text-center text-gray-500">
            <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            Nie znaleziono kontrahenta.
          </CardContent>
        </Card>
      ) : null}

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5 landscape-card-grid" data-testid={`pkg-${mode}-mobile-list`}>
        {visibleContractors.map((c) => {
          const isSel = selected.has(c.vendor_nip);
          return (
            <div
              key={c.vendor_nip}
              className={`rounded-xl border ${isSel ? "border-indigo-400 bg-indigo-50/40" : "border-gray-200 bg-white"} shadow-sm p-3`}
              data-testid={`pkg-contractor-mobile-${c.vendor_nip}`}
            >
              {isTransferMode ? (
                <div className="flex items-start gap-2">
                  <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(c.vendor_nip)}
                      className="mt-1"
                      data-testid={`pkg-contractor-cb-mobile-${c.vendor_nip}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`font-medium text-gray-900 text-sm min-w-0 ${isSel ? "whitespace-normal break-words leading-snug" : "truncate"}`}>{c.vendor_name || "—"}</div>
                        <div className="font-mono text-sm tabular-nums shrink-0">
                          {formatAmount(c.active_total_amount ?? c.total_gross, c.active_currency || c.currencies?.[0] || "PLN")}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">NIP: <span className="font-mono">{c.vendor_nip}</span></div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span data-testid={`pkg-contractor-active-count-mobile-${c.vendor_nip}`}>
                          {(c.active_invoice_count ?? 0)} {(c.active_invoice_count === 1 ? "faktura" : "faktur")} do zapłaty
                        </span>
                        {c.next_due_date ? <span>· Termin: {formatDate(c.next_due_date)}</span> : null}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <BankBadge has={c.has_bank_profile} />
                        <StatusBadge status={c.payment_analysis_status} />
                      </div>
                    </div>
                  </label>
                  <div className="shrink-0 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {isTransferMode ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!analyzing}
                        onClick={(e) => { e.stopPropagation(); onAnalyze([c.vendor_nip]); }}
                        data-testid={`pkg-contractor-reanalyze-mobile-${c.vendor_nip}`}
                        className="h-7 px-2 text-xs"
                        title="Sprawdź ponownie tego kontrahenta"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                    ) : null}
                    {renderContractorMenu(c, "mobile")}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(c.vendor_nip)}
                  className="w-full text-left flex items-start gap-2"
                  data-testid={`pkg-contractor-toggle-mobile-${c.vendor_nip}`}
                  aria-expanded={isSel}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`font-medium text-gray-900 text-sm min-w-0 ${isSel ? "whitespace-normal break-words leading-snug" : "truncate"}`}>{c.vendor_name || "—"}</div>
                      <div className="font-mono text-sm tabular-nums shrink-0">
                        {formatAmount(c.active_total_amount ?? c.total_gross, c.active_currency || c.currencies?.[0] || "PLN")}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">NIP: <span className="font-mono">{c.vendor_nip}</span></div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span data-testid={`pkg-contractor-active-count-mobile2-${c.vendor_nip}`}>
                        {(c.active_invoice_count ?? 0)} {(c.active_invoice_count === 1 ? "faktura" : "faktur")} do zapłaty
                      </span>
                      {c.next_due_date ? <span>· Termin: {formatDate(c.next_due_date)}</span> : null}
                    </div>
                  </div>
                  {isSel
                    ? <ChevronUp className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 mt-1 shrink-0" />}
                </button>
              )}
              {isSel ? (
                <>
                  <InlinePaymentMethodPanel contractor={c} onChanged={onMethodChanged} />
                  {isTransferMode ? (
                    <div className="mt-2 -mx-3 -mb-3">
                      <ManualPackageBuilder
                        vendorNip={c.vendor_nip}
                        defaultCurrency={c.currencies?.[0] || "PLN"}
                        onCreated={onPackageCreated}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Desktop table — Pakiet 1: bez overflow-x-auto; adaptacyjne kolumny. */}
      <div className="hidden md:block rounded-md border border-gray-200">
        <table className="w-full text-sm table-responsive" data-testid={`pkg-${mode}-table`}>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left w-8"></th>
              <th className="px-3 py-2 text-left font-medium">Kontrahent</th>
              <th className="px-3 py-2 text-left font-medium">NIP</th>
              <th className="px-3 py-2 text-right font-medium">Do zapłaty</th>
              <th className="px-3 py-2 text-right font-medium">Kwota do zapłaty</th>
              <th className="px-3 py-2 text-left font-medium">Najbliższy termin</th>
              <th className="px-3 py-2 text-left font-medium">Wyciągi</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              {isTransferMode ? <th className="px-3 py-2 w-40"></th> : null}
            </tr>
          </thead>
          <tbody>
            {visibleContractors.map((c) => {
              const isSel = selected.has(c.vendor_nip);
              return (
                <Fragment key={c.vendor_nip}>
                  <tr
                    className={`border-t border-gray-100 hover:bg-gray-50 ${isSel ? "bg-indigo-50/40" : ""} ${isTransferMode ? "" : "cursor-pointer"}`}
                    data-testid={`pkg-contractor-row-${c.vendor_nip}`}
                    onClick={isTransferMode ? undefined : () => toggle(c.vendor_nip)}
                  >
                    <td className="px-3 py-2">
                      {isTransferMode ? (
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggle(c.vendor_nip)}
                          data-testid={`pkg-contractor-cb-${c.vendor_nip}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggle(c.vendor_nip); }}
                          className="text-gray-400 hover:text-gray-700"
                          data-testid={`pkg-contractor-toggle-${c.vendor_nip}`}
                          aria-expanded={isSel}
                        >
                          {isSel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-900 max-w-[32ch] truncate" title={c.vendor_name || ""}>
                      {c.vendor_name || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700 font-mono text-xs">{c.vendor_nip}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap" data-testid={`pkg-contractor-active-count-${c.vendor_nip}`}>
                      {(c.active_invoice_count ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                      {formatAmount(c.active_total_amount ?? c.total_gross, c.active_currency || c.currencies?.[0] || "PLN")}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {c.next_due_date ? formatDate(c.next_due_date) : "—"}
                    </td>
                    <td className="px-3 py-2"><BankBadge has={c.has_bank_profile} /></td>
                    <td className="px-3 py-2"><StatusBadge status={c.payment_analysis_status} /></td>
                    {isTransferMode ? (
                      <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!!analyzing}
                            onClick={(e) => { e.stopPropagation(); onAnalyze([c.vendor_nip]); }}
                            data-testid={`pkg-contractor-reanalyze-${c.vendor_nip}`}
                            className="h-7 px-2 text-xs"
                            title="Sprawdź ponownie tego kontrahenta"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Sprawdź
                          </Button>
                          {renderContractorMenu(c)}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  {isSel ? (
                    <tr className="bg-indigo-50/40 border-t border-indigo-100" data-testid={`pkg-contractor-row-expanded-${c.vendor_nip}`}>
                      <td colSpan={isTransferMode ? 9 : 8} className="px-3 pb-3 pt-0">
                        <InlinePaymentMethodPanel contractor={c} onChanged={onMethodChanged} />
                        {isTransferMode ? (
                          <div className="mt-3 -mx-3 -mb-3">
                            <ManualPackageBuilder
                              vendorNip={c.vendor_nip}
                              defaultCurrency={c.currencies?.[0] || "PLN"}
                              onCreated={onPackageCreated}
                            />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Round 24: Modal potwierdzenia mark-invoices-paid.
          Widoczny wyłącznie w zakładce Przelewy (menu ⋮ na wierszu kontrahenta). */}
      <Dialog open={!!markModal} onOpenChange={(open) => { if (!open) closeMarkModal(); }}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="pkg-contractor-mark-paid-dialog"
          onEscapeKeyDown={(e) => { if (markSubmitting) e.preventDefault(); }}
          onInteractOutside={(e) => { if (markSubmitting) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle data-testid="pkg-mark-paid-dialog-title">
              {markModal?.selectiveIds
                ? ((markModal?.eligibleCount || 0) === 1
                  ? "Oznaczyć wybraną fakturę jako zapłaconą?"
                  : `Oznaczyć ${markModal?.eligibleCount || 0} wybrane faktury jako zapłacone?`)
                : "Oznaczyć aktualne faktury jako zapłacone?"}
            </DialogTitle>
          </DialogHeader>
          {markModal?.loading ? (
            <div className="text-sm text-gray-600 py-4">
              <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
              Sprawdzam faktury kontrahenta…
            </div>
          ) : markModal?.error ? (
            <div className="text-sm text-red-700 py-2">
              Nie udało się pobrać danych. Spróbuj ponownie.
            </div>
          ) : (
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                Oznaczysz{" "}
                <strong data-testid="pkg-mark-paid-count">{markModal?.eligibleCount || 0}</strong>{" "}
                {(markModal?.eligibleCount || 0) === 1
                  ? (markModal?.selectiveIds ? "wybraną fakturę" : "aktualną fakturę")
                  : (markModal?.selectiveIds ? "wybranych faktur" : "aktualnych faktur")}{" "}
                kontrahenta{" "}
                <strong>{markModal?.contractor?.vendor_name || markModal?.contractor?.vendor_nip}</strong>{" "}
                o łącznej wartości{" "}
                <strong data-testid="pkg-mark-paid-total">
                  {formatAmount(markModal?.eligibleTotal || 0, markModal?.currency || "PLN")}
                </strong>{" "}
                jako zapłacone.
              </p>
              <p className="text-gray-500">
                Operacja dotyczy wyłącznie faktur istniejących w tej chwili. Nowe faktury zaimportowane później pozostaną aktywne.
              </p>
              <p className="text-gray-500">Kontrahent pozostanie w zakładce Przelewy.</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeMarkModal}
              disabled={markSubmitting}
              data-testid="pkg-mark-paid-cancel"
            >
              Anuluj
            </Button>
            <Button
              type="button"
              onClick={confirmMark}
              disabled={markSubmitting || markModal?.loading || (markModal?.eligibleCount || 0) === 0 || markModal?.error}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-500"
              data-testid="pkg-mark-paid-confirm"
            >
              {markSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Oznaczanie…</>
              ) : "Oznacz jako zapłacone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --------------------------------------------------------------------------
// Tab 2: Paczki — propozycje + paczki
// --------------------------------------------------------------------------

/**
 * Konfiguracja wizualna dla 4 finalnych statusów Agenta Płatności
 * (`invoice_decisions.final_status`). Klucz = spec biznesowa.
 */
const DECISION_STATUS_CONFIG = {
  payable: {
    label: "Do zapłaty",
    borderClass: "border-emerald-200 bg-emerald-50/60",
    badgeClass: "border-emerald-300 bg-emerald-100 text-emerald-800",
    itemBorderClass: "border-emerald-200",
    showEvidence: false,
  },
  already_paid: {
    label: "Już zapłacone",
    borderClass: "border-gray-200 bg-gray-50",
    badgeClass: "border-gray-300 bg-gray-100 text-gray-700",
    itemBorderClass: "border-gray-200",
    showEvidence: false,
  },
  needs_review: {
    label: "Do weryfikacji",
    borderClass: "border-amber-200 bg-amber-50/60",
    badgeClass: "border-amber-300 bg-amber-100 text-amber-800",
    itemBorderClass: "border-amber-200",
    showEvidence: true,   // spec: pokaż confidence + reason + evidence
  },
  credit_note_do_not_pay: {
    label: "Korekty / nie płacić",
    borderClass: "border-rose-200 bg-rose-50/60",
    badgeClass: "border-rose-300 bg-rose-100 text-rose-800",
    itemBorderClass: "border-rose-200",
    showEvidence: false,
  },
  // 2026-02 (R-PAY-MANUAL-OVERRIDE-001): nowa grupa dla faktur ręcznie
  // oznaczonych przez usera jako zapłacone. Pojawia się tylko gdy
  // manualPaidIds ma ≥1 fakturę. Wiersze renderują przycisk `+` do cofnięcia.
  manually_paid: {
    label: "Ręcznie oznaczone jako zapłacone",
    borderClass: "border-emerald-200 bg-emerald-50/40",
    badgeClass: "border-emerald-300 bg-emerald-100 text-emerald-800",
    itemBorderClass: "border-emerald-200",
    showEvidence: false,
  },
};

const DECISION_STATUS_ORDER = ["payable", "already_paid", "needs_review", "credit_note_do_not_pay", "manually_paid"];

/**
 * Sekcja per `final_status`. Renderuje tylko gdy są decyzje danego statusu.
 * Evidence + confidence + reason pokazywane wyłącznie dla `needs_review`
 * (spec: „Nie pokazuj ich przy fakturach zapłaconych ani gotowych do płatności").
 * Kwota + reason pokazywane dla `credit_note_do_not_pay`.
 *
 * `onInvoicePreview(decision)` — callback klikany na numerze faktury (2026-02
 * feature „Payment Agent — pełny podgląd faktury z propozycji paczki").
 * Klik wywołuje `stopPropagation()`, żeby nie zwinąć/rozwinąć sekcji ani
 * nie klikać nadrzędnej karty propozycji.
 */
const DecisionsGroup = ({ status, decisions, currency, packageId, onInvoicePreview,
  onMarkPaidManual, onRestoreUnpaid,
  // R-PAY-DEDUCTIONS (2026-02-18): akcje dla korekt.
  onApplyCreditNote, appliedCreditNoteIds, busyCreditNoteId,
  busyInvoiceId }) => {
  const [expanded, setExpanded] = useState(true);
  if (!decisions || decisions.length === 0) return null;
  const cfg = DECISION_STATUS_CONFIG[status];
  if (!cfg) return null;
  // R-PAY-MANUAL-OVERRIDE-001 + R-PAY-DEDUCTIONS: przycisk akcji zależy od sekcji.
  //   payable → `−` (mark_paid_manual)
  //   manually_paid → `+` (restore_unpaid)
  //   credit_note_do_not_pay → `+` (apply_credit_note) — domyślnie NIE
  //     uwzględniona; user świadomie włącza. Wykluczamy korekty które są
  //     już zastosowane (renderowane w osobnej sekcji „Uwzględnione korekty").
  //   inne sekcje → brak akcji (only preview).
  const rowAction = status === "payable"
    ? "mark"
    : status === "manually_paid"
    ? "restore"
    : status === "credit_note_do_not_pay" && typeof onApplyCreditNote === "function"
    ? "apply_credit_note"
    : null;
  const appliedSet = appliedCreditNoteIds instanceof Set
    ? appliedCreditNoteIds
    : new Set(appliedCreditNoteIds || []);
  // Filtracja: korekty już zastosowane NIE pokazujemy w tej sekcji.
  const visibleDecisions = status === "credit_note_do_not_pay"
    ? decisions.filter((d) => !appliedSet.has(d.invoice_id))
    : decisions;
  if (visibleDecisions.length === 0) return null;
  return (
    <div
      className={`mt-2 rounded-md border p-2 ${cfg.borderClass}`}
      data-testid={`pkg-proposal-decisions-${status}-${packageId}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-800"
        data-testid={`pkg-proposal-decisions-${status}-toggle-${packageId}`}
      >
        <span className="inline-flex items-center gap-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <span className={`text-xs px-1.5 py-0.5 rounded-full border ${cfg.badgeClass}`}>
            {cfg.label}
          </span>
          <span className="text-gray-600">({decisions.length})</span>
        </span>
      </button>
      {expanded ? (
        <ul className="mt-1.5 space-y-1.5 text-xs text-gray-900">
          {visibleDecisions.map((d) => (
            <li
              key={d.invoice_id}
              className={`rounded border bg-white p-1.5 ${cfg.itemBorderClass}`}
              data-testid={`pkg-proposal-decision-${status}-${packageId}-${d.invoice_id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {onInvoicePreview && d.invoice_id ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      // Zatrzymaj propagację — nie zwijaj sekcji, nie klikaj
                      // nadrzędnej karty propozycji, nie zmieniaj zaznaczenia.
                      e.stopPropagation();
                      e.preventDefault();
                      onInvoicePreview(d);
                    }}
                    className="inline-flex items-center gap-1 min-h-[28px] font-mono font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-0.5 -mx-0.5 break-all text-left"
                    title="Pełny podgląd faktury"
                    aria-label={`Pokaż podgląd faktury ${d.invoice_number || d.invoice_id}`}
                    data-testid={`pkg-decision-preview-${status}-${packageId}-${d.invoice_id}`}
                  >
                    <Eye className="w-3.5 h-3.5 shrink-0 opacity-70" />
                    <span>{d.invoice_number || d.invoice_id}</span>
                  </button>
                ) : (
                  <span className="font-mono font-medium">{d.invoice_number || d.invoice_id}</span>
                )}
                <div className="inline-flex items-center gap-2 shrink-0">
                  <span className="font-mono text-xs text-gray-700">
                    {formatAmount(d.amount, d.currency || currency)}
                  </span>
                  {status === "manually_paid" ? (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full border border-emerald-300 bg-emerald-100 text-emerald-800"
                      data-testid={`pkg-decision-status-manual-${packageId}-${d.invoice_id}`}
                    >
                      Zapłacona ręcznie
                    </span>
                  ) : null}
                  {rowAction === "mark" ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onMarkPaidManual?.(d.invoice_id);
                      }}
                      disabled={busyInvoiceId === d.invoice_id || !d.invoice_id}
                      className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] min-h-[36px] rounded-md border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Oznacz jako zapłaconą"
                      aria-label={`Oznacz fakturę ${d.invoice_number || d.invoice_id} jako zapłaconą`}
                      data-testid={`pkg-decision-mark-paid-${packageId}-${d.invoice_id}`}
                    >
                      {busyInvoiceId === d.invoice_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Minus className="w-4 h-4" />}
                    </button>
                  ) : null}
                  {rowAction === "restore" ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRestoreUnpaid?.(d.invoice_id);
                      }}
                      disabled={busyInvoiceId === d.invoice_id || !d.invoice_id}
                      className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] min-h-[36px] rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Przywróć jako niezapłaconą"
                      aria-label={`Przywróć fakturę ${d.invoice_number || d.invoice_id} jako niezapłaconą`}
                      data-testid={`pkg-decision-restore-unpaid-${packageId}-${d.invoice_id}`}
                    >
                      {busyInvoiceId === d.invoice_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Plus className="w-4 h-4" />}
                    </button>
                  ) : null}
                  {/* R-PAY-DEDUCTIONS (2026-02-18): przycisk `+` dla korekty
                      = „Uwzględnij korektę w paczce". Nie używa tekstu
                      „Zapłacona ręcznie" (semantyczne oddzielenie). */}
                  {rowAction === "apply_credit_note" ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onApplyCreditNote?.(d.invoice_id);
                      }}
                      disabled={busyCreditNoteId === d.invoice_id || !d.invoice_id}
                      className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] min-h-[36px] rounded-md border border-indigo-400 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Uwzględnij korektę w paczce"
                      aria-label={`Uwzględnij korektę ${d.invoice_number || d.invoice_id} w paczce`}
                      data-testid={`pkg-decision-apply-credit-note-${packageId}-${d.invoice_id}`}
                    >
                      {busyCreditNoteId === d.invoice_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Plus className="w-4 h-4" />}
                    </button>
                  ) : null}
                </div>
              </div>
              {/* Evidence + confidence + reason: wyłącznie dla needs_review. */}
              {cfg.showEvidence ? (
                <div className="mt-0.5 text-xs text-gray-700 space-y-0.5">
                  {typeof d.confidence === "number" ? (
                    <div>
                      <span className="text-gray-500">confidence: </span>
                      <span className="font-mono">{d.confidence.toFixed(2)}</span>
                    </div>
                  ) : null}
                  {d.reason ? (
                    <div className="italic text-gray-700">{d.reason}</div>
                  ) : null}
                  {Array.isArray(d.evidence) && d.evidence.length > 0 ? (
                    <ul className="text-xs text-gray-500 list-disc list-inside space-y-0.5">
                      {d.evidence.map((ev, i) => (
                        <li key={i} className="truncate" title={JSON.stringify(ev)}>
                          {ev.source || "evidence"}
                          {ev.matched_transaction_title ? ` — ${ev.matched_transaction_title}` : ""}
                          {ev.paid_hint?.matched_transaction_amount !== undefined
                            ? ` — przelew ${formatAmount(ev.paid_hint.matched_transaction_amount, currency)}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              {/* Korekta: pokaż tylko krótki reason (bez evidence stack). */}
              {status === "credit_note_do_not_pay" && d.reason ? (
                <div className="mt-0.5 text-xs italic text-gray-700">{d.reason}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const ProposalCard = ({ proposal, onAccept, onReject, busyId, onInvoicePreview, mode = "proposal", onLock, onUnlock }) => {
  const [expanded, setExpanded] = useState(false);
  const busy = busyId === proposal.package_id;
  const isRejected = proposal.status === "rejected";
  const llmUsed = !!proposal.llm_used;
  const recommendation = proposal.recommendation || "";
  const needsReview = proposal.status === "needs_review"
    || recommendation === "needs_review"
    || (proposal.status === "proposal" && proposal.bank_match_found === false);
  const warnings = Array.isArray(proposal.warnings) ? proposal.warnings : [];
  // 2026-02: Frontend przełączony na `invoice_decisions` (spec:
  // PAYMENT_AGENT_FRONTEND_INVOICE_DECISIONS_MIGRATION.md). Legacy pola
  // (already_paid_invoice_candidates, payable_invoice_candidates,
  // needs_review_invoice_candidates) nadal są w response backendu dla
  // kompatybilności, ale frontend ich nie używa.
  const decisions = useMemo(
    () => (Array.isArray(proposal.invoice_decisions) ? proposal.invoice_decisions : []),
    [proposal.invoice_decisions],
  );

  // ── R-PAY-MANUAL-OVERRIDE-001 (2026-02, frontend etap) ──────────────────
  // Ręczne oznaczanie pojedynczej faktury w propozycji jako zapłaconej.
  // Model stanu (client-side):
  //   manualPaidIds : Set<invoice_id> — invoice_id-y ręcznie zaznaczone
  //                                     jako paid_manual w tej sesji lub
  //                                     zsynchronizowane z backendem przy
  //                                     mount (excluded_from_ai_analysis).
  //   overrideCount / overrideTotal — źródło prawdy dla nagłówka po
  //                                    ostatniej odpowiedzi API. Jeśli
  //                                    backend nie zwrócił — spadamy na
  //                                    lokalne przeliczenie.
  //   busyInvoiceId — mutex per row (spinner tylko w klikniętym wierszu).
  // ────────────────────────────────────────────────────────────────────────
  // Inicjalny zestaw: faktury z decisions final_status=payable, które
  // backend już oznaczył `excluded_from_ai_analysis=true`. Pole nie zawsze
  // jest w payloadzie decisions — jeśli brak, mamy pusty Set (poprawi się
  // po pierwszym mark/restore).
  const initialManualPaid = useMemo(() => {
    const s = new Set();
    for (const d of decisions) {
      if (d && d.invoice_id && d.excluded_from_ai_analysis === true) s.add(d.invoice_id);
    }
    return s;
  }, [decisions]);
  const [manualPaidIds, setManualPaidIds] = useState(initialManualPaid);
  const [overrideCount, setOverrideCount] = useState(null);
  const [overrideTotal, setOverrideTotal] = useState(null);
  const [busyInvoiceId, setBusyInvoiceId] = useState(null);

  // R-PAY-DEDUCTIONS (2026-02-18): dwa niezależne mechanizmy odliczeń.
  //   appliedCreditNoteIds — Set<invoice_id> korekt świadomie włączonych.
  //   manualDeductions — lista {deduction_id, amount, description, currency, created_at, created_by}.
  //   totalsFromBackend — snapshot sums z ostatniej odpowiedzi endpointu
  //     (backend jest source of truth dla kwot).
  //   busyCreditNoteId / busyDeductionId — mutex per row.
  const initialAppliedCreditNoteIds = useMemo(() => {
    return new Set(Array.isArray(proposal.applied_credit_note_ids) ? proposal.applied_credit_note_ids : []);
  }, [proposal.applied_credit_note_ids]);
  const initialManualDeductions = useMemo(() => {
    return Array.isArray(proposal.manual_deductions) ? proposal.manual_deductions : [];
  }, [proposal.manual_deductions]);
  const [appliedCreditNoteIds, setAppliedCreditNoteIds] = useState(initialAppliedCreditNoteIds);
  const [manualDeductions, setManualDeductions] = useState(initialManualDeductions);
  const [totalsFromBackend, setTotalsFromBackend] = useState(null);
  const [busyCreditNoteId, setBusyCreditNoteId] = useState(null);
  const [busyDeductionId, setBusyDeductionId] = useState(null);
  const [newDeductionAmount, setNewDeductionAmount] = useState("");
  const [newDeductionDescription, setNewDeductionDescription] = useState("");
  const [addingDeduction, setAddingDeduction] = useState(false);
  const [deductionError, setDeductionError] = useState("");

  // Reset stanu gdy zmienia się propozycja (nowy package_id).
  useEffect(() => {
    setManualPaidIds(initialManualPaid);
    setOverrideCount(null);
    setOverrideTotal(null);
    setBusyInvoiceId(null);
    setAppliedCreditNoteIds(initialAppliedCreditNoteIds);
    setManualDeductions(initialManualDeductions);
    setTotalsFromBackend(null);
    setBusyCreditNoteId(null);
    setBusyDeductionId(null);
    setNewDeductionAmount("");
    setNewDeductionDescription("");
    setDeductionError("");
  }, [proposal.package_id, initialManualPaid, initialAppliedCreditNoteIds, initialManualDeductions]);

  // Mapowanie kodów błędów z backendu na komunikaty PL.
  const ERROR_MSG = {
    INVOICE_NOT_FOUND: "Nie znaleziono faktury.",
    INVOICE_ALREADY_EXPORTED:
      "Nie można zmienić statusu faktury, ponieważ znajduje się w wyeksportowanej paczce.",
    INVOICE_ALREADY_PAID_IN_KSEF:
      "Nie można zmienić statusu faktury, ponieważ została oznaczona jako zapłacona w KSeF.",
    INVOICE_IN_LOCKED_PACKAGE:
      "Nie można zmienić statusu faktury, ponieważ znajduje się w zablokowanej paczce.",
    CANNOT_RESTORE_NON_MANUAL_PAYMENT:
      "Nie można przywrócić tej faktury jako niezapłaconej, ponieważ jej status nie pochodzi z ręcznej decyzji.",
    INVALID_PAYMENT_STATUS_TRANSITION: "Ta zmiana statusu nie jest dozwolona.",
  };

  const _extractErrorCode = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object") return detail.code;
    return null;
  };

  const _mapErrorMessage = (fallback, err) => {
    const code = _extractErrorCode(err);
    if (code && ERROR_MSG[code]) return ERROR_MSG[code];
    return fallback;
  };

  const _applyProposalCounts = (resp) => {
    if (typeof resp?.proposal_invoice_count === "number") {
      setOverrideCount(resp.proposal_invoice_count);
    }
    if (typeof resp?.proposal_total_amount === "number") {
      setOverrideTotal(resp.proposal_total_amount);
    }
  };

  const handleMarkPaidManual = useCallback(async (invoiceId) => {
    if (!invoiceId || busyInvoiceId) return;
    setBusyInvoiceId(invoiceId);
    // Optymistyczne przeniesienie (rollback na błędzie).
    const prev = new Set(manualPaidIds);
    setManualPaidIds((old) => new Set(old).add(invoiceId));
    try {
      const r = await axios.post(
        `${API}/invoices/${invoiceId}/mark-paid-manual`, {},
        { withCredentials: true },
      );
      _applyProposalCounts(r.data);
      toast.success("Faktura oznaczona jako zapłacona");
    } catch (err) {
      // Rollback UI.
      setManualPaidIds(prev);
      toast.error(_mapErrorMessage(
        "Nie udało się zmienić statusu faktury. Spróbuj ponownie.",
        err,
      ));
    } finally {
      setBusyInvoiceId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyInvoiceId, manualPaidIds]);

  const handleRestoreUnpaid = useCallback(async (invoiceId) => {
    if (!invoiceId || busyInvoiceId) return;
    setBusyInvoiceId(invoiceId);
    const prev = new Set(manualPaidIds);
    setManualPaidIds((old) => {
      const next = new Set(old);
      next.delete(invoiceId);
      return next;
    });
    try {
      const r = await axios.post(
        `${API}/invoices/${invoiceId}/restore-unpaid`, {},
        { withCredentials: true },
      );
      _applyProposalCounts(r.data);
      // Backend zwraca `state` — może być "restored_unpaid" (wróciła do puli)
      // lub "already_unpaid" (nic się nie zmieniło).
      // Spec: nie dopisujemy w ciemno, ale w tej propozycji faktura była
      // pierwotnie w `payable`, więc powrót do „Do zapłaty” jest naturalny.
      toast.success("Faktura przywrócona jako niezapłacona");
    } catch (err) {
      setManualPaidIds(prev);
      toast.error(_mapErrorMessage(
        "Nie udało się zmienić statusu faktury. Spróbuj ponownie.",
        err,
      ));
    } finally {
      setBusyInvoiceId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyInvoiceId, manualPaidIds]);

  // ── R-PAY-DEDUCTIONS (2026-02-18): apply/unapply korekty + manual deductions.
  // Wszystkie 4 handlery używają tego samego kontraktu response:
  //   { applied_credit_note_ids, manual_deductions, payable_sum,
  //     credit_notes_applied_sum, manual_deductions_sum, total_amount }
  // Backend jest source of truth — po każdej akcji zapisujemy `totalsFromBackend`.
  const _applyDeductionsResp = (resp) => {
    if (!resp || typeof resp !== "object") return;
    if (Array.isArray(resp.applied_credit_note_ids)) {
      setAppliedCreditNoteIds(new Set(resp.applied_credit_note_ids));
    }
    if (Array.isArray(resp.manual_deductions)) {
      setManualDeductions(resp.manual_deductions);
    }
    setTotalsFromBackend({
      payable_sum: resp.payable_sum,
      credit_notes_applied_sum: resp.credit_notes_applied_sum,
      manual_deductions_sum: resp.manual_deductions_sum,
      total_amount: resp.total_amount,
    });
  };

  const handleApplyCreditNote = useCallback(async (invoiceId) => {
    if (!invoiceId || busyCreditNoteId) return;
    setBusyCreditNoteId(invoiceId);
    try {
      // Pakiet A Etap 1: endpoint prefix zależy od trybu (proposal vs draft package).
      const prefix = mode === "draft" ? "packages" : "proposals";
      const r = await axios.post(
        `${API}/payment-packages/${prefix}/${proposal.package_id}/credit-notes/apply`,
        { invoice_id: invoiceId },
        { withCredentials: true },
      );
      _applyDeductionsResp(r.data);
      toast.success("Korekta uwzględniona w paczce");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nie udało się uwzględnić korekty.";
      toast.error(msg);
    } finally {
      setBusyCreditNoteId(null);
    }
  }, [busyCreditNoteId, proposal.package_id, mode]);

  const handleUnapplyCreditNote = useCallback(async (invoiceId) => {
    if (!invoiceId || busyCreditNoteId) return;
    setBusyCreditNoteId(invoiceId);
    try {
      const prefix = mode === "draft" ? "packages" : "proposals";
      const r = await axios.post(
        `${API}/payment-packages/${prefix}/${proposal.package_id}/credit-notes/unapply`,
        { invoice_id: invoiceId },
        { withCredentials: true },
      );
      _applyDeductionsResp(r.data);
      toast.success("Korekta usunięta z paczki");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nie udało się usunąć korekty.";
      toast.error(msg);
    } finally {
      setBusyCreditNoteId(null);
    }
  }, [busyCreditNoteId, proposal.package_id, mode]);

  const handleAddManualDeduction = useCallback(async () => {
    setDeductionError("");
    const amt = parseFloat(newDeductionAmount);
    const desc = (newDeductionDescription || "").trim();
    if (!isFinite(amt) || amt <= 0) {
      setDeductionError("Kwota musi być większa od 0.");
      return;
    }
    if (Math.round(amt * 100) !== amt * 100) {
      setDeductionError("Kwota może mieć maksymalnie 2 miejsca po przecinku.");
      return;
    }
    if (!desc) {
      setDeductionError("Opis jest wymagany.");
      return;
    }
    if (desc.length > 200) {
      setDeductionError("Opis może mieć maksymalnie 200 znaków.");
      return;
    }
    setAddingDeduction(true);
    try {
      const prefix = mode === "draft" ? "packages" : "proposals";
      const r = await axios.post(
        `${API}/payment-packages/${prefix}/${proposal.package_id}/manual-deductions`,
        { amount: amt, description: desc },
        { withCredentials: true },
      );
      _applyDeductionsResp(r.data);
      setNewDeductionAmount("");
      setNewDeductionDescription("");
      toast.success("Odliczenie dodane");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nie udało się dodać odliczenia.";
      setDeductionError(msg);
    } finally {
      setAddingDeduction(false);
    }
  }, [newDeductionAmount, newDeductionDescription, proposal.package_id, mode]);

  const handleDeleteManualDeduction = useCallback(async (deductionId) => {
    if (!deductionId || busyDeductionId) return;
    setBusyDeductionId(deductionId);
    try {
      const prefix = mode === "draft" ? "packages" : "proposals";
      const r = await axios.delete(
        `${API}/payment-packages/${prefix}/${proposal.package_id}/manual-deductions/${deductionId}`,
        { withCredentials: true },
      );
      _applyDeductionsResp(r.data);
      toast.success("Odliczenie usunięte");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nie udało się usunąć odliczenia.";
      toast.error(msg);
    } finally {
      setBusyDeductionId(null);
    }
  }, [busyDeductionId, proposal.package_id, mode]);

  // Klasyfikacja decisions po sekcjach.
  // - payable: standardowe (do zapłaty), MINUS filter manualPaidIds.
  // - manually_paid: faktury z manualPaidIds, pobrane z listy payable
  //                  (żeby zachować numer, kwotę, currency).
  // - already_paid / needs_review / credit_note_do_not_pay: bez zmian.
  const decisionsByStatus = DECISION_STATUS_ORDER.reduce((acc, s) => {
    if (s === "payable") {
      acc[s] = decisions.filter(
        (d) => d.final_status === "payable" && !manualPaidIds.has(d.invoice_id),
      );
    } else if (s === "manually_paid") {
      // Pobierz decisions dla faktur zaznaczonych ręcznie (mogą pochodzić
      // z payable lub innej sekcji — najczęściej payable).
      acc[s] = decisions.filter((d) => manualPaidIds.has(d.invoice_id));
    } else {
      acc[s] = decisions.filter((d) => d.final_status === s);
    }
    return acc;
  }, {});

  // Effective count/total dla nagłówka.
  // R-PAY-CREATE-PACKAGE-CONDITION (2026-02-18): jedynym źródłem prawdy dla
  // liczby faktur, sumy paczki oraz aktywności przycisku „Utwórz paczkę"
  // jest lista `payable` w `decisionsByStatus` (po odjęciu manualPaidIds).
  // Backend `accept_proposal` filtruje `excluded_from_ai_analysis=true`
  // (odpowiednik manualPaidIds), więc `payable.length` = liczba faktur,
  // które faktycznie trafią do paczki po akceptacji. Poprzedni wzór
  // `proposal.invoice_ids.length - manualPaidIds.size` był błędny — zliczał
  // również faktury credit_note_do_not_pay / already_paid, blokując przycisk
  // mimo że w `payable` pozostawały jeszcze faktury do zapłaty.
  //
  // R-PAY-DEDUCTIONS (2026-02-18): dodatkowa warunkowość — `total_amount > 0`.
  // Podgląd frontendowy: liczy total wg tego samego wzoru co backend,
  // ale wartości z ostatniej odpowiedzi backendu są nadrzędne
  // (`totalsFromBackend`).
  const _sumBy = (arr, key) => arr.reduce((s, d) => s + (Number(d?.[key]) || 0), 0);
  const payableForHeader = decisionsByStatus.payable || [];
  const localCount = payableForHeader.length;
  const payableSum = totalsFromBackend?.payable_sum ?? _sumBy(payableForHeader, "amount");

  // Suma korekt zastosowanych (ujemna). Podgląd na żywo:
  const _appliedCreditNoteAmounts = (decisionsByStatus.credit_note_do_not_pay || [])
    .filter((d) => appliedCreditNoteIds.has(d.invoice_id));
  const creditNotesAppliedSum = totalsFromBackend?.credit_notes_applied_sum
    ?? _sumBy(_appliedCreditNoteAmounts, "amount");

  // Suma odliczeń manualnych (dodatnia, ODEJMOWANA):
  const manualDeductionsSum = totalsFromBackend?.manual_deductions_sum
    ?? manualDeductions.reduce((s, m) => s + (Number(m?.amount) || 0), 0);

  const totalAmount = totalsFromBackend?.total_amount
    ?? (payableSum + creditNotesAppliedSum - manualDeductionsSum);

  const localTotal = overrideTotal ?? _sumBy(payableForHeader, "amount");
  const manualPaidSum = _sumBy(decisionsByStatus.manually_paid || [], "amount");
  const canCreatePackage = localCount > 0 && totalAmount > 0;
  const totalAmountBlocked = localCount > 0 && totalAmount <= 0;
  return (
    <div
      className={`rounded-xl border shadow-sm ${isRejected ? "border-gray-200 bg-gray-50" : needsReview ? "border-amber-200 bg-amber-50/40" : "border-indigo-200 bg-white"}`}
      data-testid={`pkg-proposal-${proposal.package_id}`}
    >
      <div className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold text-gray-900">
                Propozycja paczki
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${
                  isRejected
                    ? "border-rose-200 bg-rose-50 text-rose-700"
                    : needsReview
                    ? "border-amber-300 bg-amber-100 text-amber-800"
                    : "border-indigo-200 bg-indigo-50 text-indigo-700"
                }`}
                data-testid={`pkg-proposal-status-${proposal.package_id}`}
              >
                {isRejected ? "Odrzucona" : needsReview ? "Wymaga przeglądu" : "Gotowa do decyzji"}
              </span>
              {llmUsed ? (
                <span
                  className="text-xs px-2 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-700 inline-flex items-center gap-0.5"
                  data-testid={`pkg-proposal-ai-${proposal.package_id}`}
                  title="Analiza wykonana przez Agenta Płatności (LLM)"
                >
                  <Sparkles className="w-3 h-3" /> AI
                </span>
              ) : null}
              {recommendation ? (
                <span
                  className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-600 font-mono"
                  data-testid={`pkg-proposal-recommendation-${proposal.package_id}`}
                  title="Rekomendacja LLM"
                >
                  {recommendation}
                </span>
              ) : null}
            </div>
            <div className="mt-2 text-sm text-gray-900">
              <span className="font-medium">{proposal.vendor_name || "—"}</span>
              <span className="text-gray-500"> · NIP {proposal.vendor_nip || "—"}</span>
            </div>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-gray-500">Faktur</div>
                <div className="font-medium text-gray-900" data-testid={`pkg-proposal-count-${proposal.package_id}`}>{localCount}</div>
              </div>
              <div>
                <div className="text-gray-500">
                  {proposal.credit_note_offset_amount && proposal.credit_note_offset_amount < 0
                    ? "Kwota przelewu"
                    : "Łączna kwota"}
                </div>
                <div className="font-mono font-medium text-gray-900" data-testid={`pkg-proposal-total-${proposal.package_id}`}>
                  {formatAmount(localTotal, proposal.currency)}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <div className="text-gray-500">IBAN</div>
                <div className="font-mono text-xs text-gray-700 truncate" title={proposal.vendor_iban || ""}>
                  {proposal.vendor_iban || "—"}
                </div>
              </div>
            </div>
            {/* Podsumowanie z potrąceniami (2026-02:
                PAYMENT_AGENT_CREDIT_NOTE_OFFSET). Widoczne tylko gdy paczka
                ma potrącone korekty. */}
            {proposal.credit_note_offset_amount && proposal.credit_note_offset_amount < 0 ? (
              <div
                className="mt-2 p-2 rounded-md border border-gray-200 bg-gray-50 text-xs"
                data-testid={`pkg-proposal-offset-summary-${proposal.package_id}`}
              >
                <div className="flex justify-between">
                  <span className="text-gray-600">Do zapłaty</span>
                  <span className="font-mono">{formatAmount(proposal.payable_gross_amount, proposal.currency)}</span>
                </div>
                <div className="flex justify-between text-rose-700">
                  <span>Potrącenia</span>
                  <span className="font-mono">{formatAmount(proposal.credit_note_offset_amount, proposal.currency)}</span>
                </div>
                <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-semibold text-gray-900">
                  <span>Kwota przelewu</span>
                  <span className="font-mono">{formatAmount(proposal.total_amount, proposal.currency)}</span>
                </div>
                {Array.isArray(proposal.credit_note_offset_invoice_numbers)
                  && proposal.credit_note_offset_invoice_numbers.length > 0 ? (
                  <div className="mt-1.5 text-xs text-gray-600">
                    <span className="text-gray-500">Korekty: </span>
                    <span className="font-mono">
                      {proposal.credit_note_offset_invoice_numbers.join(", ")}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <BankBadge has={proposal.bank_match_found} />
              {proposal.bank_match_method && proposal.bank_match_method !== "none" ? (
                <span className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-600">
                  match: {proposal.bank_match_method}
                </span>
              ) : null}
            </div>
            {/* Warnings z LLM */}
            {warnings.length > 0 ? (
              <div
                className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2"
                data-testid={`pkg-proposal-warnings-${proposal.package_id}`}
              >
                <div className="text-xs font-semibold text-amber-800 mb-0.5">Ostrzeżenia agenta:</div>
                <ul className="text-xs text-amber-900 space-y-0.5 list-disc list-inside">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            ) : null}
            {/* Decyzje Agenta Płatności — 5 sekcji per final_status
                (payable, already_paid, needs_review, credit_note_do_not_pay,
                manually_paid). Sekcja `manually_paid` pojawia się tylko
                gdy manualPaidIds.size > 0. */}
            {DECISION_STATUS_ORDER.map((status) => (
              <DecisionsGroup
                key={status}
                status={status}
                decisions={decisionsByStatus[status]}
                currency={proposal.currency}
                packageId={proposal.package_id}
                onInvoicePreview={onInvoicePreview}
                onMarkPaidManual={handleMarkPaidManual}
                onRestoreUnpaid={handleRestoreUnpaid}
                onApplyCreditNote={handleApplyCreditNote}
                appliedCreditNoteIds={appliedCreditNoteIds}
                busyCreditNoteId={busyCreditNoteId}
                busyInvoiceId={busyInvoiceId}
              />
            ))}
            {/* R-PAY-DEDUCTIONS (2026-02-18): sekcja „Uwzględnione korekty".
                Renderowana tylko gdy user opt-in ≥ 1 korekta. */}
            {appliedCreditNoteIds.size > 0 ? (
              <div
                className="mt-2 rounded-md border border-indigo-200 bg-indigo-50/40 p-2"
                data-testid={`pkg-applied-credit-notes-${proposal.package_id}`}
              >
                <div className="text-xs font-semibold text-indigo-800 mb-1.5 inline-flex items-center gap-1">
                  <Minus className="w-3 h-3" />
                  <span>Uwzględnione korekty ({appliedCreditNoteIds.size})</span>
                </div>
                <ul className="space-y-1.5 text-xs text-gray-900">
                  {(decisions || [])
                    .filter((d) => appliedCreditNoteIds.has(d.invoice_id))
                    .map((d) => (
                      <li
                        key={d.invoice_id}
                        className="rounded border border-indigo-200 bg-white p-1.5"
                        data-testid={`pkg-applied-credit-note-${proposal.package_id}-${d.invoice_id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-mono font-medium">{d.invoice_number || d.invoice_id}</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="font-mono text-xs text-rose-700">
                              {formatAmount(d.amount, d.currency || proposal.currency)}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleUnapplyCreditNote(d.invoice_id);
                              }}
                              disabled={busyCreditNoteId === d.invoice_id}
                              className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] min-h-[36px] rounded-md border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Usuń korektę z paczki"
                              aria-label={`Usuń korektę ${d.invoice_number || d.invoice_id} z paczki`}
                              data-testid={`pkg-decision-unapply-credit-note-${proposal.package_id}-${d.invoice_id}`}
                            >
                              {busyCreditNoteId === d.invoice_id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Minus className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
                <div className="mt-1.5 flex justify-between text-xs font-semibold text-indigo-900 border-t border-indigo-200 pt-1">
                  <span>Łączne odliczenie</span>
                  <span className="font-mono" data-testid={`pkg-applied-credit-notes-sum-${proposal.package_id}`}>
                    {formatAmount(creditNotesAppliedSum, proposal.currency)}
                  </span>
                </div>
              </div>
            ) : null}
            {/* R-PAY-DEDUCTIONS (2026-02-18): sekcja „Ręczne odliczenia".
                Zawsze widoczna gdy propozycja jest edytowalna — inline form. */}
            {!isRejected ? (
              <div
                className="mt-2 rounded-md border border-amber-200 bg-amber-50/30 p-2"
                data-testid={`pkg-manual-deductions-${proposal.package_id}`}
              >
                <div className="text-xs font-semibold text-amber-900 mb-1.5 inline-flex items-center gap-1">
                  <Minus className="w-3 h-3" />
                  <span>Ręczne odliczenia ({manualDeductions.length})</span>
                </div>
                {manualDeductions.length > 0 ? (
                  <ul className="space-y-1.5 text-xs text-gray-900 mb-1.5">
                    {manualDeductions.map((m) => (
                      <li
                        key={m.deduction_id}
                        className="rounded border border-amber-200 bg-white p-1.5"
                        data-testid={`pkg-manual-deduction-${proposal.package_id}-${m.deduction_id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-gray-800 break-words min-w-0 flex-1">{m.description}</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="font-mono text-xs text-rose-700">
                              −{formatAmount(m.amount, m.currency || proposal.currency)}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteManualDeduction(m.deduction_id);
                              }}
                              disabled={busyDeductionId === m.deduction_id}
                              className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] min-h-[36px] rounded-md border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Usuń odliczenie"
                              aria-label={`Usuń odliczenie ${m.description}`}
                              data-testid={`pkg-manual-deduction-delete-${proposal.package_id}-${m.deduction_id}`}
                            >
                              {busyDeductionId === m.deduction_id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Minus className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {/* Inline form */}
                <div className="flex flex-col sm:flex-row gap-1.5">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={newDeductionAmount}
                    onChange={(e) => setNewDeductionAmount(e.target.value)}
                    placeholder="Kwota (PLN)"
                    className="w-full sm:w-32 rounded border border-amber-300 bg-white px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid={`pkg-manual-deduction-amount-input-${proposal.package_id}`}
                    disabled={addingDeduction}
                  />
                  <input
                    type="text"
                    value={newDeductionDescription}
                    onChange={(e) => setNewDeductionDescription(e.target.value)}
                    maxLength={200}
                    placeholder="Opis (np. Nadpłata z 10.07.2026)"
                    className="w-full flex-1 rounded border border-amber-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid={`pkg-manual-deduction-desc-input-${proposal.package_id}`}
                    disabled={addingDeduction}
                  />
                  <Button
                    type="button"
                    onClick={handleAddManualDeduction}
                    disabled={addingDeduction || !newDeductionAmount || !newDeductionDescription.trim()}
                    className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-xs h-9 whitespace-nowrap"
                    data-testid={`pkg-manual-deduction-add-${proposal.package_id}`}
                  >
                    {addingDeduction ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    Dodaj
                  </Button>
                </div>
                {deductionError ? (
                  <div
                    className="mt-1 text-xs text-rose-700"
                    data-testid={`pkg-manual-deduction-error-${proposal.package_id}`}
                  >
                    {deductionError}
                  </div>
                ) : null}
                {manualDeductions.length > 0 ? (
                  <div className="mt-1.5 flex justify-between text-xs font-semibold text-amber-900 border-t border-amber-200 pt-1">
                    <span>Łączne odliczenie</span>
                    <span className="font-mono" data-testid={`pkg-manual-deductions-sum-${proposal.package_id}`}>
                      −{formatAmount(manualDeductionsSum, proposal.currency)}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            {/* R-PAY-DEDUCTIONS (2026-02-18): jedno podsumowanie kwot
                (payable / applied credit notes / manual deductions / total).
                „Do przelewu" jest wizualnie najważniejsze. */}
            {!isRejected ? (
              <div
                className="mt-2 rounded-md border border-gray-300 bg-white p-2.5 text-xs"
                data-testid={`pkg-deductions-summary-${proposal.package_id}`}
              >
                <div className="flex justify-between text-gray-700">
                  <span>Faktury do zapłaty</span>
                  <span className="font-mono">{formatAmount(payableSum, proposal.currency)}</span>
                </div>
                <div className={`flex justify-between mt-0.5 ${creditNotesAppliedSum < 0 ? "text-rose-700" : "text-gray-500"}`}>
                  <span>Uwzględnione korekty</span>
                  <span className="font-mono">{formatAmount(creditNotesAppliedSum, proposal.currency)}</span>
                </div>
                <div className={`flex justify-between mt-0.5 ${manualDeductionsSum > 0 ? "text-rose-700" : "text-gray-500"}`}>
                  <span>Ręczne odliczenia</span>
                  <span className="font-mono">
                    {manualDeductionsSum > 0 ? "−" : ""}{formatAmount(manualDeductionsSum, proposal.currency)}
                  </span>
                </div>
                <div className="border-t border-gray-300 mt-1.5 pt-1.5 flex justify-between text-base font-bold text-gray-900">
                  <span>Do przelewu</span>
                  <span className={`font-mono ${totalAmount <= 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid={`pkg-deductions-total-${proposal.package_id}`}>
                    {formatAmount(totalAmount, proposal.currency)}
                  </span>
                </div>
                {totalAmountBlocked ? (
                  <div
                    className="mt-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-1.5"
                    data-testid={`pkg-deductions-total-blocked-${proposal.package_id}`}
                  >
                    Suma korekt i odliczeń jest równa lub wyższa od sumy faktur do zapłaty.
                    Zmniejsz odliczenia lub odznacz korekty, aby utworzyć paczkę.
                  </div>
                ) : null}
              </div>
            ) : null}
            {/* Komunikat gdy WSZYSTKIE payable zostały ręcznie oznaczone
                jako zapłacone — nie można utworzyć pustej paczki. */}
            {!isRejected && localCount === 0 && manualPaidIds.size > 0 ? (
              <div
                className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600 flex items-start gap-2"
                data-testid={`pkg-proposal-empty-payable-${proposal.package_id}`}
              >
                <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-800">Brak faktur do utworzenia paczki</div>
                  <div className="mt-0.5">
                    Wszystkie faktury z tej propozycji zostały ręcznie oznaczone jako zapłacone.
                    Cofnij dowolne oznaczenie plusem, aby utworzyć paczkę.
                  </div>
                </div>
              </div>
            ) : null}
            {manualPaidIds.size > 0 && manualPaidSum > 0 ? (
              <div
                className="mt-1 text-xs text-gray-500 text-right"
                data-testid={`pkg-proposal-manual-paid-sum-${proposal.package_id}`}
              >
                Ręcznie oznaczone: <span className="font-mono">{formatAmount(manualPaidSum, proposal.currency)}</span>
              </div>
            ) : null}
            {/* Uzasadnienie */}
            {proposal.rationale && proposal.rationale.length > 0 ? (
              <div className="mt-2">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center"
                  data-testid={`pkg-proposal-toggle-${proposal.package_id}`}
                >
                  {expanded ? <ChevronUp className="w-3 h-3 mr-0.5" /> : <ChevronDown className="w-3 h-3 mr-0.5" />}
                  Uzasadnienie
                </button>
                {expanded ? (
                  <ul className="mt-1.5 text-xs text-gray-700 space-y-0.5 list-disc list-inside" data-testid={`pkg-proposal-rationale-${proposal.package_id}`}>
                    {proposal.rationale.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
          {!isRejected ? (
            <div className="flex sm:flex-col gap-2">
              {mode === "draft" ? (
                // Pakiet A Etap 1: przycisk zablokowania paczki do eksportu
                // (draft → ready_for_export). Zamiast „Utwórz paczkę".
                <>
                  <Button
                    onClick={() => onLock && onLock(proposal.package_id)}
                    disabled={busy || !canCreatePackage}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
                    data-testid={`pkg-draft-lock-${proposal.package_id}`}
                    title={
                      !canCreatePackage
                        ? (totalAmountBlocked
                            ? "Suma korekt i odliczeń jest równa lub wyższa od sumy faktur do zapłaty."
                            : "Brak faktur do zablokowania paczki")
                        : "Zablokuj paczkę i przygotuj do eksportu"
                    }
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Zablokuj i wyślij do eksportu
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => onAccept(proposal.package_id)}
                    disabled={busy || !canCreatePackage}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300"
                    data-testid={`pkg-proposal-accept-${proposal.package_id}`}
                    title={
                      !canCreatePackage
                        ? (totalAmountBlocked
                            ? "Suma korekt i odliczeń jest równa lub wyższa od sumy faktur do zapłaty."
                            : "Brak faktur do utworzenia paczki")
                        : undefined
                    }
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Utwórz paczkę
                  </Button>
                  <Button
                    onClick={() => onReject(proposal.package_id)}
                    variant="outline"
                    disabled={busy}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    data-testid={`pkg-proposal-reject-${proposal.package_id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Odrzuć
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const PackageRow = ({ pkg, onCancel, busyId }) => {
  const isCancelled = pkg.status === "cancelled";
  const busy = busyId === pkg.package_id;
  const isAiGenerated = pkg.created_by === "ai_payment_agent";

  // R-PAY-CARDS (2026-02-22): tytuł karty = nazwa kontrahenta.
  // R-PAY-MANUAL-VENDOR (2026-02-22): paczki ręczne multi-vendor pokazują
  // „Paczka zbiorcza (N kontrahentów)" zamiast wymyślonego głównego vendora.
  const contractors = Array.isArray(pkg.contractors) ? pkg.contractors : [];
  const isMultiVendor = pkg.is_multi_vendor === true || (contractors.length > 1);
  const primaryVendor =
    contractors[0]?.vendor_name
    || pkg.vendor_name
    || null;
  const displayTitle = isMultiVendor
    ? `Paczka zbiorcza${(pkg.vendors_count || contractors.length) > 1 ? ` (${pkg.vendors_count || contractors.length} kontrahentów)` : ""}`
    : (primaryVendor || pkg.name || pkg.package_id);
  const invoicesCount = pkg.invoices_count ?? (pkg.invoice_ids?.length || 0);

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 flex flex-col gap-2"
      data-testid={`pkg-row-${pkg.package_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-medium text-gray-900 truncate"
              title={displayTitle}
              data-testid={`pkg-row-title-${pkg.package_id}`}
            >
              {displayTitle}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${
                isCancelled
                  ? "border-gray-200 bg-gray-50 text-gray-500"
                  : "border-indigo-200 bg-indigo-50 text-indigo-700"
              }`}
              data-testid={`pkg-row-status-${pkg.package_id}`}
            >
              {isCancelled ? "Anulowana" : "Draft"}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5" data-testid={`pkg-row-meta-${pkg.package_id}`}>
            {invoicesCount} {invoicesCount === 1 ? "faktura" : "faktur"} · {formatAmount(pkg.total_amount, pkg.currency)} · utworzono {formatDate(pkg.created_at)}
            {pkg.name && primaryVendor ? (
              <>
                {" "}·{" "}
                <span className="text-gray-400" title={pkg.name}>{pkg.name}</span>
              </>
            ) : null}
          </div>
        </div>
        {/* 2026-02 UX refactor: brak dużego przycisku „Anuluj" na karcie
            (był mylący — sugerował główną akcję na widoku, który tak naprawdę
            jest read-only audytem). Zamiast tego: menu 3-kropek z akcją
            administracyjną „Anuluj paczkę". Zgodnie z briefem UX 2026-02. */}
        {!isCancelled ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={busy}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 self-end sm:self-center disabled:opacity-50"
                data-testid={`pkg-row-menu-${pkg.package_id}`}
                aria-label="Menu paczki"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem
                onClick={() => onCancel(pkg.package_id)}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                data-testid={`pkg-row-cancel-${pkg.package_id}`}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Anuluj paczkę
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {/* 2026-02: Akordeon „Uzasadnienie AI" pomiędzy info-blokiem a menu.
          Zachowany jako audit trail (spec 2026-02 UX refactor). NIE uruchamia
          ponownej analizy — pokazuje tylko zapisane wyniki. */}
      {isAiGenerated ? (
        <PackageAiExplanation pkg={pkg} />
      ) : null}
    </div>
  );
};

/**
 * Zwijany akordeon „Uzasadnienie AI" — pokazuje 3 sekcje diagnostyki
 * (dlaczego utworzono paczkę, analiza faktur, dane wykorzystane).
 * Wszystkie dane pochodzą z zapisanego dokumentu paczki (bez ponownej analizy).
 */
const PackageAiExplanation = ({ pkg }) => {
  const rationale = Array.isArray(pkg.rationale) ? pkg.rationale : [];
  const invoiceDecisions = Array.isArray(pkg.invoice_decisions) ? pkg.invoice_decisions : [];
  const matching = Array.isArray(pkg.invoice_number_matching) ? pkg.invoice_number_matching : [];
  const meta = pkg.ai_analysis_metadata || {};
  // Zbuduj mapę invoice_id → matching dla szybkiego lookupu.
  const matchingById = new Map(matching.map((m) => [m.invoice_id, m]));
  return (
    <details
      className="rounded-lg border border-indigo-100 bg-indigo-50/30 open:bg-indigo-50/50 transition-colors group"
      data-testid={`pkg-ai-explanation-${pkg.package_id}`}
    >
      <summary
        className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-indigo-800 flex items-center gap-2 list-none"
        data-testid={`pkg-ai-explanation-toggle-${pkg.package_id}`}
      >
        <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90" />
        <span>Uzasadnienie AI</span>
        <span className="text-xs text-indigo-500 font-normal ml-auto">
          {pkg.invoice_ids?.length || 0} faktur analizowanych
        </span>
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-3 text-xs">
        {/* Sekcja 1 — Dlaczego utworzono paczkę. */}
        <section data-testid={`pkg-ai-section-rationale-${pkg.package_id}`}>
          <h4 className="text-xs uppercase tracking-wide font-semibold text-indigo-900 mb-1.5">
            Dlaczego utworzono paczkę
          </h4>
          {rationale.length > 0 ? (
            <ul className="space-y-1 text-gray-800">
              {rationale.map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-emerald-600 shrink-0 mt-0.5">✓</span>
                  <span className="break-words">{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 italic">Brak zapisanego uzasadnienia dla tej paczki.</p>
          )}
          <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>Liczba faktur: <b>{pkg.invoice_ids?.length || 0}</b></span>
            <span>Łączna kwota: <b>{formatAmount(pkg.total_amount, pkg.currency)}</b></span>
            {pkg.created_at ? (
              <span>Termin analizy: <b>{formatDate(pkg.created_at)}</b></span>
            ) : null}
          </div>
        </section>

        {/* Sekcja 2 — Analiza faktur. */}
        <section data-testid={`pkg-ai-section-invoices-${pkg.package_id}`}>
          <h4 className="text-xs uppercase tracking-wide font-semibold text-indigo-900 mb-1.5">
            Analiza faktur
          </h4>
          {invoiceDecisions.length > 0 || matching.length > 0 ? (
            <ul className="space-y-2">
              {(invoiceDecisions.length > 0 ? invoiceDecisions : matching).map((d, i) => {
                const invId = d.invoice_id;
                const invNumber = d.invoice_number || matchingById.get(invId)?.invoice_number_raw;
                const m = matchingById.get(invId) || {};
                const wasFound = m.match_found;
                const isPaidCandidate = d.final_status === "already_paid";
                return (
                  <li
                    key={invId || i}
                    className="rounded border border-indigo-100 bg-white/70 px-2 py-1.5"
                    data-testid={`pkg-ai-invoice-${invId || i}`}
                  >
                    <div className="font-medium text-gray-900 break-all">
                      {invNumber || `Faktura ${invId || i + 1}`}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-700 space-y-0.5">
                      {m.year_detected ? (
                        <div>✓ Numer rozpoznany · Rok: <b>{m.year_full}</b></div>
                      ) : m.normalizer_warnings?.length ? (
                        <div className="text-amber-700">⚠ {m.normalizer_warnings[0]}</div>
                      ) : null}
                      {typeof m.transactions_scanned === "number" ? (
                        <div>✓ Przeszukano: <b>{m.transactions_scanned} operacji</b></div>
                      ) : null}
                      {wasFound && m.matched_transaction ? (
                        <div className="text-emerald-800">
                          ✓ Znaleziono przelew {formatDate(m.matched_transaction.booking_date || m.matched_transaction.transaction_date)}
                          {" · "}Tytuł: <span className="font-mono">{m.matched_transaction.title}</span>
                        </div>
                      ) : m.match_found === false ? (
                        <div className="text-gray-600">✓ Nie znaleziono wcześniejszego przelewu</div>
                      ) : null}
                      {isPaidCandidate ? (
                        <div className="text-amber-800">⚠ AI oznaczyło jako prawdopodobnie już opłaconą</div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-500 italic">Brak zapisanych danych o analizie faktur.</p>
          )}
        </section>

        {/* Sekcja 3 — Dane wykorzystane podczas analizy. */}
        <section data-testid={`pkg-ai-section-meta-${pkg.package_id}`}>
          <h4 className="text-xs uppercase tracking-wide font-semibold text-indigo-900 mb-1.5">
            Dane wykorzystane podczas analizy
          </h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-700">
            {pkg.vendor_iban ? (
              <div className="break-all">
                <dt className="inline text-gray-500">IBAN kontrahenta: </dt>
                <dd className="inline font-mono">{pkg.vendor_iban}</dd>
              </div>
            ) : null}
            {typeof meta.transactions_scanned === "number" ? (
              <div>
                <dt className="inline text-gray-500">Przeanalizowanych operacji: </dt>
                <dd className="inline font-medium">{meta.transactions_scanned}</dd>
              </div>
            ) : null}
            {meta.analyzed_at ? (
              <div>
                <dt className="inline text-gray-500">Data analizy: </dt>
                <dd className="inline">{formatDate(meta.analyzed_at)}</dd>
              </div>
            ) : null}
            {meta.agent_version ? (
              <div>
                <dt className="inline text-gray-500">Wersja Agenta: </dt>
                <dd className="inline font-mono">{meta.agent_version}</dd>
              </div>
            ) : null}
          </dl>
        </section>
      </div>
    </details>
  );
};

/**
 * BankExportDialog — prosty inline-panel/modal wyboru banku.
 *
 * Świadomie minimalistyczny — bez selektora wielu banków, bez konfiguracji.
 * MVP obsługuje wyłącznie mBank. Plik pobierany jako blob (FE NIE komunikuje
 * się z bankiem). Po sukcesie toast + reset selekcji.
 *
 * Błędy walidacji backendu (`EXPORT_VALIDATION_FAILED`) są renderowane jako
 * czytelna lista per paczka.
 */
/**
 * CreatePaymentFileDialog — generacja pliku importowego z zaznaczonych paczek.
 *
 * PAX nie komunikuje się z bankiem. Ten modal tworzy plik XML/CSV, który user
 * pobiera na dysk i wgrywa ręcznie do bankowości elektronicznej.
 *
 * 2026-02 uproszczenie:
 *   - Modal ma być maksymalnie prosty. Konfiguracja (nazwa firmy, IBAN,
 *     domyślny bank, domyślny format eksportu) jest zapisana w
 *     Profil → Dane firmy i **NIE JEST** pokazywana per eksport.
 *   - Widoczne pola: tytuł, licznik paczek, opcjonalna data realizacji,
 *     przyciski Anuluj / Utwórz plik płatności.
 *   - Format brany z `default_export_format` w `company_settings` (domyślnie
 *     `xml`). CSV legacy nadal wspierany backendowo — user zmienia w profilu.
 *   - Brak selektora firmy — dziś model 1:1 (unique index user_id).
 *     Architektura NIE zablokowana pod przyszły selektor N firm.
 *   - Brak danych firmy → komunikat blokujący: „Uzupełnij Dane firmy w
 *     Profilu, aby utworzyć plik płatności." Bez technicznych błędów.
 */
const CreatePaymentFileDialog = ({ open, onClose, selectedCount, selectedIds, onSuccess }) => {
  // 2026-02: ELIXIR-0 TXT (mBank) domyślny dla PLN, XML (SEPA) dla walut obcych.
  // `userTouchedFormat` = user ręcznie kliknął w radio → nie nadpisujemy z preview.
  const [format, setFormat] = useState("elixir");
  const [userTouchedFormat, setUserTouchedFormat] = useState(false);
  const [executionDate, setExecutionDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState(null);  // {message, invalid_packages}
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [company, setCompany] = useState(null);  // {company_name, debtor_iban, ...}
  // Basic manual workflow (2026-02): preview danych do potwierdzenia.
  const [preview, setPreview] = useState(null);  // {packages, total_amount, ...}
  const [previewLoading, setPreviewLoading] = useState(false);

  // Pobierz dane firmy z profilu. `company_settings` to dziś 1 rekord per user.
  // Selektor firmy pominięty w MVP (spec: 1 firma → auto-wybór).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCompanyLoaded(false);
    setPreview(null);
    setUserTouchedFormat(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
        if (cancelled) return;
        const data = res.data || {};
        setCompany(data);
        // Legacy: gdy user ma zapisane `csv`, nie wymuszamy go w UI (nie ma
        // opcji CSV w tym modalu). Uszanujemy tylko `xml`/`elixir`.
        if (data.default_export_format === "xml" || data.default_export_format === "elixir") {
          setFormat(data.default_export_format);
        }
      } catch (_e) {
        setCompany(null);
      } finally {
        if (!cancelled) setCompanyLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Preview zawartości paczek — pobierany osobno (widok „za co płacisz").
  useEffect(() => {
    if (!open || !selectedIds || selectedIds.length === 0) return;
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const res = await axios.post(
          `${API}/payment-packages/export/preview`,
          {
            bank: "mbank",
            package_ids: selectedIds,
            execution_date: executionDate || undefined,
          },
          { withCredentials: true },
        );
        if (!cancelled) {
          setPreview(res.data);
          // Auto-adjust formatu wg waluty preview — o ile user sam nie
          // wybrał inaczej. PLN → ELIXIR-0 domyślnie, non-PLN → wymuszony XML.
          const ccy = String(res.data?.currency || "PLN").toUpperCase();
          if (!userTouchedFormat) {
            if (ccy !== "PLN") {
              setFormat("xml");
            } else if (format !== "xml" && format !== "elixir") {
              setFormat("elixir");
            }
          } else if (ccy !== "PLN" && format === "elixir") {
            // Waluta obca — ELIXIR-0 nie obsługuje, wymuszamy XML.
            setFormat("xml");
          }
        }
      } catch (_e) {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIds, executionDate]);

  if (!open) return null;

  const hasCompany = Boolean(company?.company_name && company?.debtor_iban);
  const missingCompany = companyLoaded && !hasCompany;

  const handleCreate = async () => {
    if (missingCompany) return;  // guard
    setBusy(true);
    setErrors(null);
    try {
      const requestBody = {
        bank: "mbank",  // MVP: jedyny obsługiwany bank
        package_ids: selectedIds,
        format,
        return_mode: "json",  // Basic manual workflow — plik do pobrania z archiwum
      };
      if (executionDate) {
        // Data realizacji dopuszczalna dla XML (pain.001 ReqdExctnDt),
        // ELIXIR-0 (pole 2 = data przelewu) i CSV — trafi też jako
        // `execution_date` na paczki po zakończeniu eksportu.
        requestBody.execution_date = executionDate;
      }
      const res = await axios.post(
        `${API}/payment-packages/export`,
        requestBody,
        { withCredentials: true },
      );
      // Basic manual workflow: NIE pobieramy pliku automatycznie. User pobiera
      // go z zakładki „Eksport → Do pobrania".

      // 2026-02 sanitize-first: jeśli backend zsanityzował dane (polskie
      // znaki, `&`, obcięcia), zwraca warnings w headerze
      // `X-PAX-Sanitization-Warnings` (base64 JSON). NIE blokujemy eksportu —
      // pokazujemy user-friendly modal informacyjny z listą zmian.
      let sanitizationWarnings = null;
      try {
        const warnB64 = res.headers?.["x-pax-sanitization-warnings"];
        if (warnB64) {
          const raw = typeof atob === "function"
            ? atob(warnB64)
            : Buffer.from(warnB64, "base64").toString("utf-8");
          const decoded = decodeURIComponent(escape(raw));
          sanitizationWarnings = JSON.parse(decoded);
        }
      } catch (decErr) {
        // Niekrytyczne — plik już wygenerowany.
        console.warn("Sanitization warnings decode failed", decErr);
      }

      if (sanitizationWarnings && sanitizationWarnings.length > 0) {
        // Powiadomienie użytkownika o auto-sanitizacji.
        const totalChanges = sanitizationWarnings.reduce(
          (acc, p) => acc + (p.sanitizations?.length || 0), 0,
        );
        toast.success(
          `Plik płatności utworzony. Automatycznie dostosowano ${totalChanges} pól do wymagań ELIXIR-0 TXT.`,
          {
            description: "Pobierz plik z zakładki „Eksport → Do pobrania”. Szczegóły zmian znajdziesz w widoku archiwum.",
            duration: 8000,
          },
        );
        onSuccess?.({ ...res.data, sanitization_warnings: sanitizationWarnings });
      } else {
        toast.success("Plik płatności utworzony. Pobierz go z zakładki „Eksport → Do pobrania”.");
        onSuccess?.(res.data);
      }
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.invalid_packages) {
        setErrors(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Nie udało się utworzyć pliku płatności.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="bank-export-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md mx-4 p-5">
        <div className="flex items-start gap-2 mb-3">
          <FileText className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">Utwórz plik płatności</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Wybrano {selectedCount} {selectedCount === 1 ? "paczkę" : "paczek"}. Wygenerujemy jeden plik importowy do bankowości elektronicznej. PAX nie wykonuje przelewów.
            </p>
          </div>
        </div>

        {/* 2026-02: Zwięzła informacja o wielu paczkach — bez dużego bloku
            ostrzegawczego. Zgodnie z wymaganiem: modal ma być prosty. */}
        {selectedCount > 1 ? (
          <p
            className="mb-3 text-xs text-gray-600"
            data-testid="create-file-multi-package-warning"
          >
            Wybrano {selectedCount} paczki. Zostanie wygenerowany jeden plik importowy.
          </p>
        ) : null}

        {/* Panel firmy / format / bank są ustawiane w Profil → Dane firmy.
            Modal używa ich niewidocznie — bez pól wyboru per eksport.
            Jedyny widoczny stan: komunikat blokujący gdy profil pusty. */}
        {companyLoaded && missingCompany ? (
          <div
            className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="create-file-company-missing-block"
          >
            <p className="text-sm text-amber-900 leading-relaxed">
              Uzupełnij Dane firmy w Profilu, aby utworzyć plik płatności.
            </p>
            <a
              href="/profil"
              className="inline-block mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
              data-testid="create-file-goto-profile-link"
            >
              Przejdź do profilu →
            </a>
          </div>
        ) : null}

        {companyLoaded && hasCompany ? (
          <div className="mb-4" data-testid="export-format-picker">
            <label className="block text-xs text-gray-600 mb-1.5">Format pliku</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setFormat("elixir"); setUserTouchedFormat(true); }}
                disabled={busy || (preview && String(preview.currency || "PLN").toUpperCase() !== "PLN")}
                className={
                  "text-left rounded-lg border p-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed " +
                  (format === "elixir"
                    ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200"
                    : "border-gray-200 bg-white hover:border-gray-300")
                }
                data-testid="format-radio-elixir"
                aria-pressed={format === "elixir"}
              >
                <div className="text-sm font-semibold text-gray-900">ELIXIR-0 TXT (mBank)</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Standard krajowy · tylko PLN · mBank Small Business
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setFormat("xml"); setUserTouchedFormat(true); }}
                disabled={busy}
                className={
                  "text-left rounded-lg border p-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed " +
                  (format === "xml"
                    ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200"
                    : "border-gray-200 bg-white hover:border-gray-300")
                }
                data-testid="format-radio-xml"
                aria-pressed={format === "xml"}
              >
                <div className="text-sm font-semibold text-gray-900">ISO 20022 XML (SEPA)</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Standard międzynarodowy · pain.001.001.09 · dowolna waluta
                </div>
              </button>
            </div>
            {preview && String(preview.currency || "PLN").toUpperCase() !== "PLN" ? (
              <p className="text-xs text-amber-700 mt-1.5" data-testid="format-non-pln-note">
                Waluta ≠ PLN — ELIXIR-0 nie obsługuje. Wymuszony ISO 20022 XML.
              </p>
            ) : null}
          </div>
        ) : null}

        {companyLoaded && hasCompany && (format === "xml" || format === "elixir") ? (
          <div className="mb-4">
            <label className="block text-xs text-gray-600 mb-1">Data realizacji (opcjonalnie)</label>
            <input
              type="date"
              value={executionDate}
              onChange={(e) => setExecutionDate(e.target.value)}
              disabled={busy}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              data-testid="create-file-execution-date"
            />
            <p className="text-xs text-gray-500 mt-0.5">Domyślnie: jutro (D+1). Format YYYY-MM-DD.</p>
          </div>
        ) : null}

        {/* ── Potwierdzenie zawartości (Basic manual workflow, 2026-02) ─────
             Widok „za co dokładnie płacisz". Pokazuje sumę, liczbę paczek /
             faktur oraz per-paczkowa listę faktur z numerami i kwotami. */}
        {previewLoading ? (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500 flex items-center gap-2" data-testid="export-preview-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Wczytywanie potwierdzenia…
          </div>
        ) : preview ? (
          <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="export-preview-block">
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div>
                <div className="text-gray-500">Paczek</div>
                <div className="font-semibold text-gray-900" data-testid="export-preview-package-count">
                  {preview.package_count}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Przelewów</div>
                <div className="font-semibold text-gray-900" data-testid="export-preview-transfer-count">
                  {preview.transfer_count}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Faktur</div>
                <div className="font-semibold text-gray-900" data-testid="export-preview-invoice-count">
                  {preview.invoice_count}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Łącznie</div>
                <div className="font-semibold text-gray-900" data-testid="export-preview-total">
                  {Number(preview.total_amount || 0).toFixed(2)} {preview.currency || "PLN"}
                </div>
              </div>
              {preview.execution_date ? (
                <div className="col-span-2">
                  <div className="text-gray-500">Data realizacji</div>
                  <div className="font-semibold text-gray-900" data-testid="export-preview-execution-date">
                    {preview.execution_date}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="border-t border-indigo-100 pt-2 max-h-64 overflow-auto space-y-2" data-testid="export-preview-packages">
              {(preview.packages || []).map((pkg) => (
                <div key={pkg.package_id} className="text-xs">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-semibold text-gray-900 truncate">
                      {pkg.vendor_name || pkg.package_name || pkg.package_id}
                    </div>
                    <div className="text-gray-900 shrink-0">
                      {Number(pkg.total_amount || 0).toFixed(2)} {pkg.currency || "PLN"}
                    </div>
                  </div>
                  <div className="text-gray-500 mb-1">
                    {pkg.invoice_count} {pkg.invoice_count === 1 ? "faktura" : "faktur"}
                    {pkg.vendor_nip ? ` · NIP ${pkg.vendor_nip}` : ""}
                  </div>
                  {(pkg.invoices || []).length > 0 ? (
                    <ul className="pl-3 space-y-0.5 text-gray-700 list-disc list-inside">
                      {pkg.invoices.map((inv) => (
                        <li key={inv.invoice_id}>
                          <span className="font-mono text-xs">{inv.invoice_number || inv.invoice_id}</span>
                          {inv.gross_amount != null ? (
                            <span className="text-gray-500"> · {Number(inv.gross_amount).toFixed(2)} {inv.currency || "PLN"}</span>
                          ) : null}
                          {inv.due_date ? (
                            <span className="text-gray-500"> · termin {inv.due_date}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {errors && Array.isArray(errors.invalid_packages) ? (
          <div
            className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs"
            data-testid="bank-export-errors"
          >
            <div className="font-medium text-rose-900 mb-1.5">
              {errors.message || "Nie można wygenerować pliku eksportu. Część paczek ma braki danych."}
            </div>
            <ul className="space-y-1.5 text-rose-900 max-h-48 overflow-auto">
              {errors.invalid_packages.map((it) => {
                // Mapowanie technicznych kodów `missing` na czytelne komunikaty PL.
                const missingLabels = {
                  recipient_iban: "brak rachunku bankowego",
                  recipient_iban_invalid: "IBAN odbiorcy nieprawidłowy (mod 97)",
                  recipient_iban_invalid_nrb: "IBAN odbiorcy nieprawidłowy (nie 26 cyfr NRB PL)",
                  recipient_iban_invalid_checksum: "IBAN odbiorcy nieprawidłowy (suma kontrolna mod 97)",
                  recipient_iban_not_pl: "IBAN odbiorcy nie jest polski (MVP tylko krajowe)",
                  recipient_name: "brak nazwy odbiorcy",
                  amount: "brak kwoty",
                  currency: "brak waluty",
                  currency_not_pln: "waluta ≠ PLN (ELIXIR-0/pain.001 MVP obsługują tylko PLN)",
                  payment_title: "brak tytułu przelewu",
                  iban_conflict: "konflikt IBAN (Agent Faktur ≠ Agent Wyciągów)",
                  ambiguous_iban_no_preferred: "wiele IBAN — wybierz preferowany",
                  not_found_or_forbidden: "paczka nie znaleziona",
                  internal_error: "błąd wewnętrzny",
                  debtor_name: "brak nazwy zleceniodawcy",
                  debtor_iban: "brak IBAN zleceniodawcy",
                  debtor_iban_invalid: "IBAN zleceniodawcy nieprawidłowy (mod 97)",
                  debtor_iban_invalid_nrb: "IBAN zleceniodawcy nieprawidłowy (nie 26 cyfr NRB PL)",
                  debtor_iban_invalid_checksum: "IBAN zleceniodawcy nieprawidłowy (suma kontrolna mod 97)",
                  debtor_iban_not_pl: "IBAN zleceniodawcy nie jest polski (MVP tylko krajowe)",
                };
                const readable = (it.missing || []).map((m) => {
                  if (m.startsWith("invalid_status:")) return `nieprawidłowy status (${m.slice(15)})`;
                  return missingLabels[m] || m;
                });
                // Prezentacja: „Faktura F/002903/26 — HURTOWNIA MANDARYNKA — brak rachunku bankowego"
                const parts = [];
                if (it.invoice_number) parts.push(`Faktura ${it.invoice_number}`);
                else if (it.package_name) parts.push(it.package_name);
                else parts.push(it.package_id);
                if (it.recipient_name) parts.push(it.recipient_name);
                const header = parts.join(" — ");
                // Dodatkowe komunikaty per-pole dla ELIXIR-0 — pełny tekst PL
                // z numerem pola (np. „Pole 7 – NRB kontrahenta nie przechodzi
                // walidacji sumy kontrolnej (mod 97).") zwrócony przez backend
                // z `validate_elixir_row_detailed`.
                const elixirDetails = Array.isArray(it.elixir_errors)
                  ? it.elixir_errors.map((e) => e?.message).filter(Boolean)
                  : [];
                return (
                  <li key={it.package_id}>
                    <span className="font-medium">{header}</span>
                    <span className="text-rose-700"> — {readable.join(", ")}</span>
                    {elixirDetails.length ? (
                      <ul className="mt-1 ml-4 list-disc space-y-0.5 text-rose-800">
                        {elixirDetails.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="create-file-cancel-btn"
          >
            Anuluj
          </Button>
          <Button
            onClick={handleCreate}
            disabled={busy || selectedCount === 0 || missingCompany}
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300"
            data-testid="create-file-confirm-btn"
          >
            {busy
              ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              : <FileText className="w-4 h-4 mr-1.5" />}
            Utwórz plik płatności
          </Button>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// SimpleConfirmDialog — reużywalny modal potwierdzenia (Basic manual workflow).
// --------------------------------------------------------------------------
// Używany do:
//  - potwierdzenia anulowania paczki (variant="danger"),
//  - potwierdzenia pobrania pliku (variant="primary"),
//  - potwierdzenia cofnięcia eksportu (variant="warning").
// Nie zależy od Shadcn Dialog — inline overlay pattern jak w tej stronie.
const SimpleConfirmDialog = ({
  open, onClose, onConfirm, title, body,
  confirmLabel = "Potwierdź", cancelLabel = "Anuluj",
  variant = "primary", testIdPrefix = "confirm", meta = null,
}) => {
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setBusy(false); }, [open]);
  if (!open) return null;

  const confirmClass =
    variant === "danger"
      ? "bg-rose-600 hover:bg-rose-700 text-white"
      : variant === "warning"
      ? "bg-amber-600 hover:bg-amber-700 text-white"
      : variant === "success"
      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
      : "bg-indigo-600 hover:bg-indigo-700 text-white";

  const handleConfirm = async () => {
    setBusy(true);
    try { await onConfirm(); } catch { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid={`${testIdPrefix}-modal`}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-2" data-testid={`${testIdPrefix}-title`}>
          {title}
        </h3>
        <p className="text-sm text-gray-600 mb-3" data-testid={`${testIdPrefix}-body`}>
          {body}
        </p>
        {meta ? (
          <div className="mb-4 p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs space-y-0.5" data-testid={`${testIdPrefix}-meta`}>
            {Object.entries(meta).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <span className="text-gray-500">{k}:</span>
                <span className="font-medium text-gray-900 text-right truncate">{v}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy}
            className={confirmClass}
            data-testid={`${testIdPrefix}-confirm-btn`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};


const PackagesTab = ({ proposals, packages, loading, onAccept, onReject, onCancel, onLock, onUnlock }) => {
  const [busyId, setBusyId] = useState(null);
  // 2026-02-18 (R-PAY-DECISIONS-TAB-DEDUP): stan `showCancelled`/`cancelTarget`
  // usunięty razem z sekcją „Paczki (X)" (przeniesione do zakładki „Eksport →
  // Paczki do eksportu"). `onCancel` prop pozostaje w sygnaturze dla BC —
  // wywoływany z DraftExportRow poza tym komponentem.

  // Round 21: JEDYNY globalny mechanizm (context, jeden Dialog w MainLayout).
  const { openInvoice } = useInvoicePreview();

  const handleOpenInvoicePreview = useCallback((decision) => {
    if (!decision || !decision.invoice_id) return;
    openInvoice({
      invoice_id: decision.invoice_id,
      invoice_number: decision.invoice_number,
      ksef_number: decision.ksef_number,
      seller_name: decision.vendor_name,
      seller_nip: decision.vendor_nip,
      gross_amount: decision.amount,
      currency: decision.currency,
    });
  }, [openInvoice]);

  const wrap = (fn) => async (id) => {
    setBusyId(id);
    try { await fn(id); } finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid="pkg-tab2-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Wczytywanie…
      </div>
    );
  }

  const activeProposals = proposals.filter((p) => p.status === "proposal" || p.status === "needs_review");
  const rejectedProposals = proposals.filter((p) => p.status === "rejected");
  const activePackages = packages.filter((p) => p.status === "draft");

  // "Pusto" — nic aktywnego (proposals + rejected + draft packages).
  // Sekcja „Paczki (X)" została usunięta z tej zakładki (2026-02-18), ale
  // draft packages pozwalają na wyświetlenie hintu „Przejdź do Eksportu".
  const empty =
    activeProposals.length === 0 &&
    rejectedProposals.length === 0 &&
    activePackages.length === 0;

  if (empty) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50" data-testid="pkg-tab2-empty">
        <CardContent className="p-8 text-center text-gray-500">
          <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <>Brak propozycji i paczek. Przejdź do zakładki „Do zapłaty” i uruchom analizę AI.</>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Pakiet A Etap 1 (2026-02-24): sekcja edytowalnych paczek (`draft`).
          Zastępuje dotychczasowy hint „Przejdź do Eksport → Paczki do eksportu".
          Każda paczka renderowana jako `ProposalCard mode="draft"` z pełną
          gamą operacji: +/- na fakturach, apply/unapply korekt, ręczne
          odliczenia. Przycisk „Zablokuj i wyślij do eksportu" przenosi
          paczkę do `ready_for_export` — dopiero potem trafia do zakładki
          Eksport. */}
      {activePackages.length > 0 ? (
        <section data-testid="pkg-section-drafts">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Package className="w-4 h-4 text-indigo-600" />
            Paczki do edycji ({activePackages.length})
          </h3>
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2 text-xs text-indigo-800 flex items-start gap-2 mb-2.5">
            <FileText className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Możesz edytować składniki paczki (+/- na fakturach, uwzględnić korekty, dodać ręczne odliczenia).
              Gdy paczka jest gotowa, kliknij <b>„Zablokuj i wyślij do eksportu”</b> — dopiero wtedy trafi do
              zakładki „Eksport” i będzie mogła zostać zapisana do pliku bankowego.
            </div>
          </div>
          <div className="space-y-2.5 landscape-card-grid" data-testid="pkg-drafts-list">
            {activePackages.map((p) => (
              <ProposalCard
                key={p.package_id}
                proposal={p}
                onAccept={() => {}}
                onReject={() => {}}
                busyId={busyId}
                onInvoicePreview={handleOpenInvoicePreview}
                mode="draft"
                onLock={wrap(onLock || (async () => {}))}
                onUnlock={onUnlock}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Active proposals */}
      {activeProposals.length > 0 ? (
        <section data-testid="pkg-section-proposals">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            Propozycje paczek ({activeProposals.length})
          </h3>
          <div className="space-y-2.5 landscape-card-grid" data-testid="pkg-proposals-list">
            {activeProposals.map((p) => (
              <ProposalCard
                key={p.package_id}
                proposal={p}
                onAccept={wrap(onAccept)}
                onReject={wrap(onReject)}
                busyId={busyId}
                onInvoicePreview={handleOpenInvoicePreview}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Draft / cancelled packages — 2026-02-18 (R-PAY-DECISIONS-TAB-DEDUP):
          sekcja „Paczki (X)" USUNIĘTA z zakładki „Decyzje AI".
          Powód: duplikacja UX + drugie miejsce z uzasadnieniem AI. Paczki
          draft są dostępne w zakładce „Eksport → Paczki do eksportu"
          (DraftsForExportSection) — jedno źródło prawdy dla paczek gotowych
          do eksportu, jedno źródło prawdy dla uzasadnienia AI (w propozycji).
          Karty draft w bazie danych POZOSTAJĄ nietknięte. Hint powyżej
          (pkg-tab-hint-export) kieruje usera do właściwej zakładki. */}

      {/* Rejected — collapsed history */}
      {rejectedProposals.length > 0 ? (
        <section data-testid="pkg-section-rejected">
          <h3 className="text-sm font-semibold text-gray-500 mb-2">
            Odrzucone propozycje ({rejectedProposals.length})
          </h3>
          <div className="space-y-2.5">
            {rejectedProposals.map((p) => (
              <ProposalCard
                key={p.package_id}
                proposal={p}
                onAccept={() => {}}
                onReject={() => {}}
                busyId={busyId}
                onInvoicePreview={handleOpenInvoicePreview}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Modal potwierdzenia anulowania paczki został usunięty z tej zakładki
          (2026-02-18) razem z sekcją „Paczki (X)". Anulowanie draftu odbywa
          się w zakładce „Eksport → Paczki do eksportu" (DraftExportRow). */}

      {/* Round 21: Dialog podglądu faktury renderowany globalnie w
          `InvoicePreviewProvider` (MainLayout). Tu tylko wywołujemy
          `openInvoice(...)` przez `handleOpenInvoicePreview`. */}
    </div>
  );
};

// --------------------------------------------------------------------------
// Placeholder tab (Pozostali / Bank) — 2026-02 UX polish
// --------------------------------------------------------------------------
// Zakładki dodane w ramach szkieletu UX (2026-02). Backend celowo bez zmian
// — dolna warstwa (endpointy, agenci, KSeF, mBank PSD2) będzie dopięta
// w kolejnych iteracjach. Placeholder zostaje ale nie mockuje danych ani
// nie sugeruje niedostępnych funkcji.
const PlaceholderTab = ({ testId, icon: Icon, title, description }) => (
  <Card
    className="border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white"
    data-testid={testId}
  >
    <CardContent className="p-8 sm:p-10 flex flex-col items-center text-center gap-3">
      <div className="p-3 rounded-full bg-indigo-50 border border-indigo-100">
        <Icon className="w-7 h-7 text-indigo-600" />
      </div>
      <div className="space-y-1 max-w-md">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-xs font-medium text-amber-800"
        data-testid={`${testId}-badge`}
      >
        <Hammer className="w-3 h-3" /> W przygotowaniu
      </span>
    </CardContent>
  </Card>
);

// --------------------------------------------------------------------------
// PaymentFilesArchiveTab — zakładka „Eksport": Do pobrania + Archiwum.
// --------------------------------------------------------------------------
// ExportDetailsAccordion — rozwijany panel „Szczegóły eksportu" (2026-02).
// --------------------------------------------------------------------------
// Renderowany pod kartą pliku w Archiwum. Odbiera stan cache'u z parent'a
// (`PaymentFilesArchiveTab.detailsById[fileId]`), sam nie odpowiada za fetch
// — parent zarządza cache'em (kluczowe wg spec: zwinięcie/rozwinięcie NIE
// robi kolejnego requestu, gdy dane już były pobrane).
//
// Stany:
//   - state === undefined lub state.status === 'loading' → spinner + info
//   - state.status === 'error'                          → error + „Spróbuj ponownie"
//   - state.status === 'ready' && brak paczek           → empty state
//   - state.status === 'ready' && są paczki             → podsumowanie + lista
//
// Ważne (mobile): układ 2-kolumnowy oparty o `grid` + `flex-wrap`; na wąskim
// ekranie liczby lądują w drugiej linii bez łamania layoutu.
const ExportDetailsAccordion = ({ fileId, state, onRetry }) => {
  const [expandedPkgIds, setExpandedPkgIds] = useState(() => new Set());

  const togglePkg = (pid) => {
    setExpandedPkgIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  if (!state || state.status === "loading") {
    return (
      <div
        id={`pkg-file-details-panel-${fileId}`}
        className="mt-3 border-t border-gray-100 pt-3 text-sm text-gray-500 flex items-center"
        data-testid={`pkg-file-details-loading-${fileId}`}
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Ładowanie szczegółów eksportu…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div
        id={`pkg-file-details-panel-${fileId}`}
        className="mt-3 border-t border-gray-100 pt-3 flex flex-wrap items-center gap-2"
        data-testid={`pkg-file-details-error-${fileId}`}
      >
        <span className="text-sm text-rose-700">
          Nie udało się pobrać szczegółów eksportu.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-testid={`pkg-file-details-retry-${fileId}`}
        >
          Spróbuj ponownie
        </Button>
      </div>
    );
  }

  const data = state.data || {};
  const packages = Array.isArray(data.packages) ? data.packages : [];
  const exp = data.export || {};

  if (packages.length === 0) {
    return (
      <div
        id={`pkg-file-details-panel-${fileId}`}
        className="mt-3 border-t border-gray-100 pt-3 text-sm text-gray-500"
        data-testid={`pkg-file-details-empty-${fileId}`}
      >
        Brak szczegółów eksportu.
      </div>
    );
  }

  const totalCurrency = exp.currency || packages[0]?.currency || "PLN";
  const statusLabel = (() => {
    switch (exp.status) {
      case "paid_confirmed": return "Zapłacone";
      case "cancelled":      return "Cofnięty";
      case "downloaded":     return "Pobrany";
      case "ready_to_download": return "Do pobrania";
      default: return exp.status || "—";
    }
  })();

  return (
    <div
      id={`pkg-file-details-panel-${fileId}`}
      className="mt-3 border-t border-gray-100 pt-3 space-y-3"
      data-testid={`pkg-file-details-panel-${fileId}`}
    >
      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        Szczegóły eksportu
      </div>

      {/* Podsumowanie */}
      <dl
        className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-2 text-xs"
        data-testid={`pkg-file-details-summary-${fileId}`}
      >
        <div>
          <dt className="text-gray-500">Paczek</dt>
          <dd className="font-semibold text-gray-900 tabular-nums" data-testid={`pkg-file-details-summary-packages-${fileId}`}>
            {exp.package_count ?? packages.length}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Przelewów</dt>
          <dd className="font-semibold text-gray-900 tabular-nums" data-testid={`pkg-file-details-summary-transfers-${fileId}`}>
            {exp.transfers_count ?? packages.length}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Suma</dt>
          <dd className="font-semibold text-gray-900 tabular-nums" data-testid={`pkg-file-details-summary-amount-${fileId}`}>
            {formatAmount(exp.total_amount ?? 0, totalCurrency)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Realizacja</dt>
          <dd className="font-medium text-gray-800 tabular-nums" data-testid={`pkg-file-details-summary-execution-${fileId}`}>
            {exp.execution_date || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Status</dt>
          <dd className="font-medium text-gray-800" data-testid={`pkg-file-details-summary-status-${fileId}`}>
            {statusLabel}
          </dd>
        </div>
      </dl>

      {/* Lista paczek */}
      <div className="space-y-2" data-testid={`pkg-file-details-packages-${fileId}`}>
        {packages.map((p, idx) => {
          const isPkgOpen = expandedPkgIds.has(p.package_id);
          const isUnavailable = p.status === "unavailable" || !p.package_name;
          return (
            <div
              key={p.package_id || `pkg-${idx}`}
              className={`rounded border ${isUnavailable ? "border-amber-200 bg-amber-50/40" : "border-gray-200 bg-gray-50/60"}`}
              data-testid={`pkg-file-details-pkg-${fileId}-${idx}`}
            >
              <button
                type="button"
                onClick={() => p.package_id && togglePkg(p.package_id)}
                disabled={isUnavailable}
                className="w-full text-left p-2.5 flex items-start gap-2 hover:bg-gray-100/60 disabled:cursor-default"
                data-testid={`pkg-file-details-pkg-toggle-${fileId}-${idx}`}
                aria-expanded={isPkgOpen}
              >
                <span className="text-gray-400 shrink-0 mt-0.5">
                  {isUnavailable
                    ? <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                    : (isPkgOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span
                      className="text-sm font-medium text-gray-900 truncate"
                      data-testid={`pkg-file-details-pkg-vendor-${fileId}-${idx}`}
                      title={p.vendor_name || ""}
                    >
                      {p.vendor_name || (isUnavailable ? "Paczka niedostępna" : "—")}
                    </span>
                    {p.vendor_nip ? (
                      <span
                        className="text-xs font-mono text-gray-500"
                        data-testid={`pkg-file-details-pkg-nip-${fileId}-${idx}`}
                      >
                        NIP {p.vendor_nip}
                      </span>
                    ) : null}
                    {p.status && p.status !== "unavailable" ? (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-600 uppercase tracking-wide"
                        data-testid={`pkg-file-details-pkg-status-${fileId}-${idx}`}
                      >
                        {p.status}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="truncate" title={p.package_name || ""}>
                      {p.package_name || "—"}
                    </span>
                    <span>·</span>
                    <span data-testid={`pkg-file-details-pkg-invcount-${fileId}-${idx}`}>
                      {p.invoice_count} {p.invoice_count === 1 ? "faktura" : "faktur"}
                    </span>
                  </div>
                </div>
                <span
                  className="text-sm font-semibold text-gray-900 tabular-nums shrink-0"
                  data-testid={`pkg-file-details-pkg-amount-${fileId}-${idx}`}
                >
                  {formatAmount(p.amount, p.currency || totalCurrency)}
                </span>
              </button>

              {/* Lista faktur w paczce */}
              {isPkgOpen && !isUnavailable ? (
                <div
                  className="border-t border-gray-200 bg-white px-2.5 py-1.5"
                  data-testid={`pkg-file-details-pkg-invoices-${fileId}-${idx}`}
                >
                  {(p.invoices || []).length === 0 ? (
                    <div className="text-xs text-gray-500 py-1">
                      Brak faktur w paczce.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {(p.invoices || []).map((inv, iidx) => (
                        <div
                          key={inv.invoice_id || `inv-${iidx}`}
                          className="py-1.5 grid grid-cols-12 gap-2 text-xs"
                          data-testid={`pkg-file-details-inv-${fileId}-${idx}-${iidx}`}
                        >
                          {/* R-PAY-KNOWLEDGE-001 (Etap 1): pełny numer bez truncate.
                              `break-all` pozwala łamać długie numery (np. F/1/26/060015/2024).
                              Zwinięta karta może pokazywać skrót, rozwinięta MUSI
                              pokazać pełną wartość. */}
                          <div
                            className="col-span-12 sm:col-span-5 text-gray-800 font-medium break-all"
                            title={inv.invoice_number || ""}
                            data-testid={`pkg-file-details-inv-number-${fileId}-${idx}-${iidx}`}
                          >
                            {inv.invoice_number || "(brak numeru)"}
                          </div>
                          <div className="col-span-4 sm:col-span-3 text-gray-600 tabular-nums">
                            {inv.issue_date ? (
                              <span data-testid={`pkg-file-details-inv-issue-${fileId}-${idx}-${iidx}`}>
                                Wyst.: {inv.issue_date}
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </div>
                          <div className="col-span-4 sm:col-span-2 text-gray-600 tabular-nums">
                            {inv.due_date ? (
                              <span data-testid={`pkg-file-details-inv-due-${fileId}-${idx}-${iidx}`}>
                                Term.: {inv.due_date}
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </div>
                          <div
                            className="col-span-4 sm:col-span-2 text-right font-medium tabular-nums text-gray-900"
                            data-testid={`pkg-file-details-inv-amount-${fileId}-${idx}-${iidx}`}
                          >
                            {formatAmount(inv.amount, inv.currency || p.currency || totalCurrency)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Basic manual workflow (2026-02):
//   - Podzakładka „Do pobrania": pliki wygenerowane, nie pobrane.
//   - Podzakładka „Archiwum": pliki po pierwszym pobraniu (`downloaded_at`).
// Backend rozróżnia sekcje przez `payment_files.status` + `downloaded_at`.
// Pobieranie w „Archiwum" nie tworzy nowego pliku, zwiększa `download_count`.
// --------------------------------------------------------------------------
// DraftsForExportSection — nowa sub-sekcja „Paczki do eksportu" (2026-02 UX)
// --------------------------------------------------------------------------
// Wcześniej multi-select + toolbar + modal generacji pliku były w zakładce
// „Paczki". Zgodnie z briefem UX 2026-02: zakładka „Paczki" ma być czysto
// modułem DECYZJI AI, a cała mechanika eksportu (wybór paczek, format,
// data realizacji, utworzenie pliku) trafia do „Eksport → Paczki do
// eksportu". Ten komponent:
//   - fetchuje własne paczki `draft` (`GET /payment-packages`),
//   - obsługuje multi-select (checkbox per karta),
//   - anuluje pojedyncze paczki (menu 3-kropek → SimpleConfirmDialog),
//   - na dole ma przycisk „UTWÓRZ PLIK PŁATNOŚCI",
//   - podpięty do istniejącego `CreatePaymentFileDialog` (1:1 przeniesienie).
// Modal, akordeon uzasadnienia AI, endpointy — wszystko bez zmian.
// --------------------------------------------------------------------------
const DraftsForExportSection = ({ onExportSuccess }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payment-packages`, { withCredentials: true });
      const all = res.data?.packages || [];
      // 2026-06: zakładka „Eksport" pokazuje zarówno wersje robocze (`draft`),
      // jak i paczki zablokowane po „Zablokuj i wyślij do eksportu"
      // (`ready_for_export`). Wcześniej filtr brał tylko `draft`, przez co
      // zablokowane paczki znikały z UI (P0).
      setDrafts(all.filter((p) => p.status === "draft" || p.status === "ready_for_export"));
    } catch (_e) {
      toast.error("Nie udało się pobrać paczek do eksportu.");
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  // Refetch przy globalnym sygnale (np. gdy user zaakceptuje propozycję
  // w zakładce Paczki albo cofnie eksport z Archiwum).
  useEffect(() => {
    const handler = () => { fetchDrafts(); };
    window.addEventListener("pax:packages:refresh", handler);
    return () => window.removeEventListener("pax:packages:refresh", handler);
  }, [fetchDrafts]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const draftIds = new Set(drafts.map((p) => p.package_id));
  const effectiveSelectedIds = [...selectedIds].filter((id) => draftIds.has(id));
  const selectedCount = effectiveSelectedIds.length;

  const handleCancelPackage = async (pkgId) => {
    setBusyId(pkgId);
    try {
      await axios.post(`${API}/payment-packages/${pkgId}/cancel`, {}, { withCredentials: true });
      toast.success("Paczka anulowana.");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(pkgId);
        return next;
      });
      await fetchDrafts();
      // Sygnał dla zakładki „Paczki" żeby zsynchronizowała listę.
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd anulowania.");
    } finally {
      setBusyId(null);
    }
  };

  // 2026-06: „Odblokuj do edycji" — cofa paczkę `ready_for_export → draft`
  // bez anulowania. Faktury pozostają w paczce, paczka wraca do edycji.
  const handleUnlockPackage = async (pkgId) => {
    setBusyId(pkgId);
    try {
      await axios.post(
        `${API}/payment-packages/packages/${pkgId}/unlock-to-draft`,
        {},
        { withCredentials: true },
      );
      toast.success("Paczka odblokowana do edycji.");
      await fetchDrafts();
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się odblokować paczki.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid="pkg-drafts-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Wczytywanie…
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50" data-testid="pkg-drafts-empty">
        <CardContent className="p-8 sm:p-10 flex flex-col items-center text-center gap-3">
          <div className="p-3 rounded-full bg-indigo-50 border border-indigo-100">
            <Package className="w-7 h-7 text-indigo-600" />
          </div>
          <div className="space-y-1 max-w-md">
            <h3 className="text-base font-semibold text-gray-900">Brak paczek gotowych do eksportu</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Przejdź do zakładki „Paczki”, zaakceptuj propozycję Agenta AI —
              nowa paczka pojawi się tutaj.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="pkg-drafts-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <Package className="w-4 h-4 text-gray-600" />
          Paczki do eksportu ({drafts.length})
        </h3>
        <span className="text-xs text-gray-500" data-testid="pkg-drafts-selected-count">
          Zaznaczono: <span className="font-semibold text-gray-900">{selectedCount}</span>
        </span>
      </div>

      <div className="space-y-2.5 landscape-card-grid" data-testid="pkg-drafts-list">
        {drafts.map((pkg) => (
          <DraftExportRow
            key={pkg.package_id}
            pkg={pkg}
            selected={selectedIds.has(pkg.package_id)}
            onToggleSelect={toggleSelect}
            onCancelRequest={() => setCancelTarget(pkg)}
            onUnlockRequest={() => handleUnlockPackage(pkg.package_id)}
            busy={busyId === pkg.package_id}
          />
        ))}
      </div>

      {/* Główny CTA — brief 2026-02 UX: „Na dole listy: UTWÓRZ PLIK PŁATNOŚCI". */}
      <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
        <Button
          onClick={() => setExportOpen(true)}
          disabled={selectedCount === 0}
          className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300 w-full sm:w-auto"
          data-testid="pkg-drafts-create-file-btn"
        >
          <FileText className="w-4 h-4 mr-1.5" />
          UTWÓRZ PLIK PŁATNOŚCI
        </Button>
      </div>

      {/* Modal utworzenia pliku — przeniesiony 1:1 z „Paczki". */}
      <CreatePaymentFileDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        selectedCount={selectedCount}
        selectedIds={effectiveSelectedIds}
        onSuccess={async (exportMeta) => {
          setSelectedIds(new Set());
          await fetchDrafts();
          // Sygnał do zakładki Paczki + parent (przeskok na sub-sekcję Do pobrania).
          window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
          onExportSuccess?.(exportMeta);
        }}
      />

      {/* Modal potwierdzenia anulowania paczki. */}
      <SimpleConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={async () => {
          if (!cancelTarget) return;
          const pkgId = cancelTarget.package_id;
          setCancelTarget(null);
          await handleCancelPackage(pkgId);
        }}
        title="Anulować paczkę?"
        body="Paczka zostanie oznaczona jako anulowana i zniknie z listy do eksportu. Faktury nie zostaną skasowane."
        confirmLabel="Anuluj paczkę"
        cancelLabel="Nie anuluj"
        variant="danger"
        testIdPrefix="pkg-drafts-cancel-confirm"
        meta={cancelTarget ? {
          "Kontrahent": cancelTarget.vendor_name || cancelTarget.name || cancelTarget.package_id,
          "Kwota": formatAmount(cancelTarget.total_amount, cancelTarget.currency),
          "Faktur": (cancelTarget.invoice_ids?.length || 0),
        } : null}
      />
    </div>
  );
};

// DraftExportRow — karta pojedynczej paczki draft w sekcji „Paczki do eksportu".
// Checkbox multi-select + info + 3-dot menu (Anuluj paczkę).
const DraftExportRow = ({ pkg, selected, onToggleSelect, onCancelRequest, onUnlockRequest, busy }) => {
  const isLocked = pkg.status === "ready_for_export";
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 flex flex-col gap-2"
      data-testid={`pkg-drafts-row-${pkg.package_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <label className="flex items-center shrink-0 self-start sm:self-center cursor-pointer pt-0.5 sm:pt-0">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(pkg.package_id)}
            className="w-4 h-4 accent-indigo-600 cursor-pointer"
            data-testid={`pkg-drafts-row-select-${pkg.package_id}`}
            aria-label={`Zaznacz paczkę ${pkg.name || pkg.package_id}`}
          />
        </label>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">
              {pkg.vendor_name || pkg.name || pkg.package_id}
            </span>
            {pkg.status === "ready_for_export" ? (
              <span
                className="text-xs px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"
                data-testid={`pkg-drafts-row-status-${pkg.package_id}`}
              >
                <Lock className="w-3 h-3" />
                Zablokowana
              </span>
            ) : (
              <span
                className="text-xs px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700"
                data-testid={`pkg-drafts-row-status-${pkg.package_id}`}
              >
                Draft
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {pkg.vendor_nip ? <>NIP {pkg.vendor_nip} · </> : null}
            {pkg.invoice_ids?.length || 0} faktur · {formatAmount(pkg.total_amount, pkg.currency)} · utworzono {formatDate(pkg.created_at)}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={busy}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 self-end sm:self-center disabled:opacity-50"
              data-testid={`pkg-drafts-row-menu-${pkg.package_id}`}
              aria-label="Menu paczki"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {isLocked && (
              <DropdownMenuItem
                onClick={() => onUnlockRequest()}
                className="text-indigo-600 focus:text-indigo-700 focus:bg-indigo-50"
                data-testid={`pkg-drafts-row-unlock-${pkg.package_id}`}
              >
                <Unlock className="w-4 h-4 mr-2" />
                Odblokuj do edycji
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => onCancelRequest()}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid={`pkg-drafts-row-cancel-${pkg.package_id}`}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Anuluj paczkę
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// ExportSectionTile — kafel przełącznika sekcji eksportu (2026-02 UX).
// --------------------------------------------------------------------------
// Zastępuje pasek trzech scroll-tabów. Duży, dotykalny cel na mobile
// (min-height 72px), pełna nazwa bez skrótu, wyraźny licznik. Grid parent
// decyduje o układzie (1 kolumna mobile, 3 kolumny sm+).
// --------------------------------------------------------------------------
const ExportSectionTile = ({ active, icon, label, count, onClick, testId }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={`group relative w-full text-left rounded-xl border p-3 sm:p-4 flex items-center gap-3 transition-all
        min-h-[72px] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1
        ${
          active
            ? "border-indigo-600 bg-indigo-50 shadow-sm"
            : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
        }`}
      data-testid={testId}
    >
      <span
        className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors
          ${active ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500 group-hover:bg-indigo-100 group-hover:text-indigo-600"}`}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm sm:text-[15px] font-semibold leading-tight ${active ? "text-indigo-900" : "text-gray-900"}`}>
          {label}
        </span>
        <span
          className={`inline-flex items-center gap-1 mt-0.5 text-xs ${active ? "text-indigo-700" : "text-gray-500"}`}
          data-testid={`${testId}-count`}
        >
          <span className="tabular-nums font-medium">{Number.isFinite(count) ? count : 0}</span>
          <span className="opacity-70">
            {count === 1 ? "pozycja" : count >= 2 && count <= 4 ? "pozycje" : "pozycji"}
          </span>
        </span>
      </span>
      {active ? (
        <span className="absolute inset-x-0 -bottom-px h-0.5 bg-indigo-600 rounded-b-xl hidden sm:block" aria-hidden="true" />
      ) : null}
    </button>
  );
};

const PaymentFilesArchiveTab = ({ initialSection = "drafts_for_export", onExportSuccess }) => {
  const [section, setSection] = useState(initialSection);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Modale (Basic manual workflow, 2026-02):
  //  - downloadTarget: potwierdzenie realnego pobrania pliku (Pobierz / Pobierz ponownie).
  //  - cancelExportTarget: potwierdzenie cofnięcia niepobranego eksportu.
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [cancelExportTarget, setCancelExportTarget] = useState(null);
  // 2026-07 v3: „Oznacz jako zapłacone" / „Cofnij oznaczenie".
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [unmarkPaidTarget, setUnmarkPaidTarget] = useState(null);
  // ── Akordeon „Szczegóły eksportu" (2026-02, round 7) ──────────────────────
  // Zbiór file_id-ów obecnie rozwiniętych w Archiwum. Multi-expand — user
  // może porównywać kilka eksportów jednocześnie.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Cache szczegółów per file_id: { status: 'loading'|'ready'|'error', data, error }.
  // Cache trzyma się po zwinięciu — powtórne rozwinięcie NIE wywołuje kolejnego
  // requestu (spec: „ponowne zwinięcie/rozwinięcie nie robi kolejnego requestu").
  const [detailsById, setDetailsById] = useState({});
  // ── Liczniki kafli (2026-02 UX: tile switcher zamiast tab-scrolla) ────────
  // Aktualizowane przy mount + po każdej zmianie sekcji + po sygnale
  // `pax:packages:refresh`. Osobne od `files` żeby licznik pozostał widoczny
  // gdy user jest w innej sekcji.
  const [counts, setCounts] = useState({ drafts: 0, ready_to_download: 0, archive: 0 });

  const fetchCounts = useCallback(async () => {
    try {
      const [readyRes, archRes, pkgRes] = await Promise.all([
        axios.get(`${API}/payment-files`, { params: { section: "ready_to_download" }, withCredentials: true }),
        axios.get(`${API}/payment-files`, { params: { section: "archive" }, withCredentials: true }),
        axios.get(`${API}/payment-packages`, { withCredentials: true }),
      ]);
      const drafts = (pkgRes.data?.packages || []).filter((p) => p.status === "draft").length;
      setCounts({
        drafts,
        ready_to_download: (readyRes.data?.files || []).length,
        archive: (archRes.data?.files || []).length,
      });
    } catch (_e) {
      // Ciche silent-fail — liczniki są sygnałem informacyjnym, nie krytycznym.
    }
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    const handler = () => { fetchCounts(); };
    window.addEventListener("pax:packages:refresh", handler);
    return () => window.removeEventListener("pax:packages:refresh", handler);
  }, [fetchCounts]);

  // Reset sekcji gdy zmienia się `initialSection` z parent'a (np. po eksporcie).
  useEffect(() => {
    setSection(initialSection);
  }, [initialSection]);

  const fetchFiles = useCallback(async () => {
    // Sekcja „drafts_for_export" nie fetchuje payment-files (renderuje osobny
    // komponent `DraftsForExportSection`, który sam fetchuje paczki draft).
    if (section === "drafts_for_export") {
      setLoading(false);
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payment-files`, {
        params: { section },
        withCredentials: true,
      });
      const list = res.data?.files || [];
      setFiles(list);
      // Zaktualizuj licznik danej sekcji zgodnie z rzeczywistą liczbą.
      setCounts((prev) => ({ ...prev, [section]: list.length }));
    } catch (_e) {
      toast.error("Nie udało się pobrać archiwum plików.");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Realny download po potwierdzeniu modalem.
  const performDownload = async (file) => {
    setBusyId(file.file_id);
    try {
      const res = await axios.get(`${API}/payment-files/${file.file_id}/download`, {
        withCredentials: true, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const name = match ? match[1] : (file.filename || "pax_export");
      // Basic manual workflow (2026-02): jawny MIME w Blob — niektóre
      // przeglądarki (Safari, WebView) blokują pobranie bez `type`.
      const fallbackMime =
        file.format === "xml" ? "application/xml"
        : file.format === "elixir" ? "text/plain;charset=windows-1250"
        : "text/csv";
      const mime =
        (res.headers["content-type"] || "").split(";")[0] || fallbackMime;
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      // Cleanup DOM/URL po krótkiej chwili (link.click() musi zdążyć zadziałać).
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("Plik został pobrany.");
      await fetchFiles();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) toast.error("Plik nie jest już dostępny.");
      else if (status === 404) toast.error("Plik nie znaleziony.");
      else toast.error("Nie udało się pobrać pliku.");
    } finally {
      setBusyId(null);
    }
  };

  // Cofnij eksport (backend: POST /cancel).
  // 2026-07 update: `cancel` dopuszcza także `downloaded` — user może cofnąć
  // eksport z Archiwum (bank odrzucił / zły format / błąd zawartości).
  // Semantyka biznesowa: „Cofnij" znaczy „wyrzuć plik z listy aktywnej i
  // przywróć powiązane paczki do stanu draft". Faktury i paczki NIE są
  // fizycznie kasowane — audit trail zachowany.
  const performCancelExport = async (file) => {
    setBusyId(file.file_id);
    try {
      await axios.post(`${API}/payment-files/${file.file_id}/cancel`, {}, { withCredentials: true });
      toast.success("Eksport cofnięty. Paczki wróciły do edycji.");
      await fetchFiles();
      // Sygnał dla parenta — refetch paczek (draft'y wróciły) i listy kontrahentów.
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error(e?.response?.data?.detail?.message || "Nie można cofnąć eksportu.");
      else toast.error("Nie udało się cofnąć eksportu.");
    } finally {
      setBusyId(null);
    }
  };

  // 2026-07 v3: Oznacz eksport jako zapłacony.
  // Semantyka: user potwierdza że przelewy zostały zaakceptowane w banku.
  // Faktury → status=paid (znikają z Payment Agenta i Manual Buildera),
  // paczki → reconciliation_status=manually_confirmed_paid,
  // plik → status=paid_confirmed (widoczny w Archiwum z badge „Zapłacone").
  const performMarkAsPaid = async (file) => {
    setBusyId(file.file_id);
    try {
      await axios.post(`${API}/payment-files/${file.file_id}/mark-as-paid`, {}, { withCredentials: true });
      toast.success("Oznaczono jako zapłacone. Faktury nie będą już proponowane do zapłaty.");
      await fetchFiles();
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error(e?.response?.data?.detail?.message || "Nie można oznaczyć jako zapłacone.");
      else toast.error("Nie udało się oznaczyć jako zapłacone.");
    } finally {
      setBusyId(null);
    }
  };

  // 2026-07 v3: Cofnij oznaczenie „zapłacone".
  // Przywraca faktury do poprzedniego statusu (albo `to_decide` gdy brak snapshot),
  // paczki do poprzedniego reconciliation_status, plik do `downloaded`.
  const performUnmarkAsPaid = async (file) => {
    setBusyId(file.file_id);
    try {
      await axios.post(`${API}/payment-files/${file.file_id}/unmark-as-paid`, {}, { withCredentials: true });
      toast.success("Cofnięto oznaczenie zapłacone.");
      await fetchFiles();
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error(e?.response?.data?.detail?.message || "Nie można cofnąć oznaczenia.");
      else toast.error("Nie udało się cofnąć oznaczenia.");
    } finally {
      setBusyId(null);
    }
  };

  // ── Akordeon szczegółów (2026-02) ────────────────────────────────────────
  // Fetch szczegółów per plik, tylko przy pierwszym rozwinięciu. Kolejne
  // toggle'e zwijają/rozwijają widok w oparciu o `expandedIds`, bez requestu.
  const fetchExportDetails = useCallback(async (fileId) => {
    setDetailsById((prev) => ({
      ...prev,
      [fileId]: { status: "loading", data: null, error: null },
    }));
    try {
      const res = await axios.get(
        `${API}/payment-files/${fileId}/details`,
        { withCredentials: true }
      );
      setDetailsById((prev) => ({
        ...prev,
        [fileId]: { status: "ready", data: res.data, error: null },
      }));
    } catch (e) {
      const err =
        e?.response?.data?.detail
        || e?.message
        || "Nie udało się pobrać szczegółów eksportu.";
      setDetailsById((prev) => ({
        ...prev,
        [fileId]: { status: "error", data: null, error: String(err) },
      }));
    }
  }, []);

  const toggleExportDetails = useCallback((fileId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
        return next;
      }
      next.add(fileId);
      return next;
    });
    setDetailsById((prev) => {
      // Nie fetchuj ponownie jeśli już w cache (ready lub loading).
      // Fetch odbywa się tylko dla nowego expand LUB gdy poprzedni fetch dał error.
      const current = prev[fileId];
      if (current && (current.status === "ready" || current.status === "loading")) {
        return prev;
      }
      // Zaplanuj fetch mikrozadaniem, żeby setState skończył się przed nim.
      Promise.resolve().then(() => fetchExportDetails(fileId));
      return prev;
    });
  }, [fetchExportDetails]);

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };
  const formatAmount = (amt, currency) => {
    try {
      return new Intl.NumberFormat("pl-PL", {
        style: "currency", currency: currency || "PLN", maximumFractionDigits: 2,
      }).format(amt || 0);
    } catch { return `${amt} ${currency || ""}`; }
  };

  const isReady = section === "ready_to_download";
  const isDrafts = section === "drafts_for_export";

  return (
    <div className="space-y-3" data-testid="pkg-tab-bank-panel">
      {/* 2026-02 UX refactor: pasek trzech przewijanych zakładek zastąpiony
          siatką trzech kafli (mobile-first, brak poziomego scrolla). Każdy
          kafel pokazuje ikonę, nazwę i licznik. Kliknięcie ustawia aktywną
          sekcję — content poniżej renderuje się bez routingu. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"
        data-testid="export-tile-switcher"
        role="tablist"
        aria-label="Sekcje eksportu"
      >
        <ExportSectionTile
          active={isDrafts}
          icon={<Package className="w-5 h-5" />}
          label="Paczki do eksportu"
          count={counts.drafts}
          onClick={() => setSection("drafts_for_export")}
          testId="export-subtab-drafts"
          activeColor="indigo"
        />
        <ExportSectionTile
          active={isReady}
          icon={<Download className="w-5 h-5" />}
          label="Do pobrania"
          count={counts.ready_to_download}
          onClick={() => setSection("ready_to_download")}
          testId="export-subtab-ready"
          activeColor="indigo"
        />
        <ExportSectionTile
          active={section === "archive"}
          icon={<Archive className="w-5 h-5" />}
          label="Archiwum"
          count={counts.archive}
          onClick={() => setSection("archive")}
          testId="export-subtab-archive"
          activeColor="indigo"
        />
      </div>

      <div className="text-xs text-gray-500 sm:text-right sm:pr-1" data-testid="export-hint-manual">
        PAX nie wykonuje przelewów — pliki wgrywasz ręcznie w bankowości.
      </div>

      {isDrafts ? (
        <DraftsForExportSection onExportSuccess={onExportSuccess} />
      ) : loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500" data-testid="pkg-tab-bank-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Wczytywanie…
        </div>
      ) : files.length === 0 ? (
        <Card className="border-dashed border-gray-300 bg-gray-50" data-testid="pkg-tab-bank-empty">
          <CardContent className="p-8 sm:p-10 flex flex-col items-center text-center gap-3">
            <div className="p-3 rounded-full bg-indigo-50 border border-indigo-100">
              <FileText className="w-7 h-7 text-indigo-600" />
            </div>
            <div className="space-y-1 max-w-md">
              <h3 className="text-base font-semibold text-gray-900">
                {isReady ? "Brak plików do pobrania" : "Archiwum jest puste"}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {isReady
                  ? "Aby wygenerować plik, przejdź do sub-zakładki „Paczki do eksportu”, zaznacz paczki i kliknij „UTWÓRZ PLIK PŁATNOŚCI”."
                  : "Pliki po pierwszym pobraniu pojawią się w tej sekcji."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-gray-600" />
              {isReady ? "Do pobrania" : "Archiwum"} ({files.length})
            </h3>
          </div>
          <div className="space-y-2.5 landscape-card-grid" data-testid="pkg-archive-list">
          {files.map((f) => {
            const isCancelled = f.status === "cancelled";
            const isPaidConfirmed = f.status === "paid_confirmed";
            const canRevert = !isCancelled && !isPaidConfirmed;
            const canMarkAsPaid = !isCancelled && !isPaidConfirmed && !isReady;
            return (
            <Card
              key={f.file_id}
              className={
                "border transition-colors " +
                (isCancelled
                  ? "border-gray-200 bg-gray-50 opacity-80"
                  : isPaidConfirmed
                    ? "border-emerald-200 bg-emerald-50/30"
                    : "border-gray-200 hover:border-indigo-200")
              }
              data-testid={`pkg-file-row-${f.file_id}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {f.format}
                      </span>
                      {isCancelled ? (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-amber-50 text-amber-800 border border-amber-200"
                          data-testid={`pkg-file-badge-cancelled-${f.file_id}`}
                        >
                          Cofnięty
                        </span>
                      ) : null}
                      {isPaidConfirmed ? (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase bg-emerald-100 text-emerald-800 border border-emerald-200"
                          data-testid={`pkg-file-badge-paid-${f.file_id}`}
                        >
                          ✓ Zapłacone
                        </span>
                      ) : null}
                      <span className={`text-sm font-medium truncate ${isCancelled ? "text-gray-500 line-through" : "text-gray-900"}`} title={f.filename}>{f.filename}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      <span>Utworzono: {formatDate(f.generated_at)}</span>
                      {f.execution_date ? (<><span>·</span><span>Realizacja: {f.execution_date}</span></>) : null}
                      <span>·</span>
                      <span>{f.package_count || (f.package_ids?.length || 0)} paczek</span>
                      <span>·</span>
                      <span>{f.transfers_count} {f.transfers_count === 1 ? "przelew" : "przelewów"}</span>
                      <span>·</span>
                      <span className="font-medium text-gray-700">{formatAmount(f.total_amount, f.currency)}</span>
                      <span>·</span>
                      <span>{formatBytes(f.size_bytes)}</span>
                      {!isReady ? (
                        <>
                          <span>·</span>
                          <span data-testid={`pkg-file-downloaded-${f.file_id}`}>
                            Pobrano: {formatDate(f.downloaded_at)}
                          </span>
                          <span>·</span>
                          <span data-testid={`pkg-file-download-count-${f.file_id}`}>
                            {f.download_count}× pobrany
                          </span>
                        </>
                      ) : null}
                      {isCancelled && f.cancelled_at ? (
                        <>
                          <span>·</span>
                          <span className="text-amber-700" data-testid={`pkg-file-cancelled-at-${f.file_id}`}>
                            Cofnięty: {formatDate(f.cancelled_at)}
                          </span>
                        </>
                      ) : null}
                      {isPaidConfirmed && f.payment_confirmed_at ? (
                        <>
                          <span>·</span>
                          <span className="text-emerald-700 font-medium" data-testid={`pkg-file-paid-at-${f.file_id}`}>
                            Zapłacone: {formatDate(f.payment_confirmed_at)}
                          </span>
                        </>
                      ) : null}
                      {!f.content_available ? (
                        <>
                          <span>·</span>
                          <span className="text-amber-600">niedostępny</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0 sm:flex-nowrap justify-end w-full sm:w-auto" data-testid={`pkg-file-actions-${f.file_id}`}>
                    {!isReady ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleExportDetails(f.file_id)}
                        disabled={busyId === f.file_id}
                        data-testid={`pkg-file-details-toggle-${f.file_id}`}
                        aria-expanded={expandedIds.has(f.file_id)}
                        aria-controls={`pkg-file-details-panel-${f.file_id}`}
                        title={expandedIds.has(f.file_id) ? "Zwiń szczegóły" : "Pokaż szczegóły eksportu"}
                      >
                        {expandedIds.has(f.file_id)
                          ? <ChevronUp className="w-4 h-4 mr-1" />
                          : <ChevronDown className="w-4 h-4 mr-1" />}
                        Szczegóły
                      </Button>
                    ) : null}
                    {!isCancelled ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDownloadTarget(f)}
                        disabled={!f.content_available || busyId === f.file_id}
                        data-testid={`pkg-file-download-${f.file_id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        {isReady ? "Pobierz" : "Pobierz ponownie"}
                      </Button>
                    ) : null}
                    {canMarkAsPaid ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMarkPaidTarget(f)}
                        disabled={busyId === f.file_id}
                        className="text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50 border-emerald-300"
                        data-testid={`pkg-file-mark-paid-${f.file_id}`}
                        title="Oznacz przelewy jako zapłacone (po imporcie w banku)"
                      >
                        {busyId === f.file_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <>✓ Oznacz jako zapłacone</>}
                      </Button>
                    ) : null}
                    {isPaidConfirmed ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            disabled={busyId === f.file_id}
                            data-testid={`pkg-file-menu-${f.file_id}`}
                            aria-label="Menu eksportu"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onClick={() => setUnmarkPaidTarget(f)}
                            data-testid={`pkg-file-menu-unmark-paid-${f.file_id}`}
                          >
                            Cofnij oznaczenie zapłacone
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                    {canRevert ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCancelExportTarget(f)}
                        disabled={busyId === f.file_id}
                        className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200"
                        data-testid={
                          isReady
                            ? `pkg-file-delete-${f.file_id}`
                            : `pkg-file-revert-${f.file_id}`
                        }
                        title={
                          isReady
                            ? "Usuń eksport i przywróć paczki do edycji"
                            : "Cofnij eksport i przywróć paczki do edycji"
                        }
                      >
                        {busyId === f.file_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : (isReady ? "Usuń" : "Cofnij eksport")}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {!isReady && expandedIds.has(f.file_id) ? (
                  <ExportDetailsAccordion
                    fileId={f.file_id}
                    state={detailsById[f.file_id]}
                    onRetry={() => fetchExportDetails(f.file_id)}
                  />
                ) : null}
              </CardContent>
            </Card>
          );})}
          </div>
        </>
      )}

      {/* Modal potwierdzenia pobrania pliku (Basic manual workflow, 2026-02).
          Kliknięcie „Pobierz" / „Pobierz ponownie" NIE pobiera od razu — user
          musi potwierdzić w tym modalu. Bez tego user tracił szansę na
          świadomy krok „to jest twoja ostateczna wersja". */}
      <SimpleConfirmDialog
        open={!!downloadTarget}
        onClose={() => setDownloadTarget(null)}
        onConfirm={async () => {
          const target = downloadTarget;
          setDownloadTarget(null);
          if (target) await performDownload(target);
        }}
        title={isReady ? "Pobrać plik płatności?" : "Pobrać plik ponownie?"}
        body={
          isReady
            ? "Plik zostanie zapisany na dysku i przeniesiony do Archiwum. Będziesz mógł/mogła pobrać go ponownie w każdej chwili."
            : "Plik zostanie zapisany na dysku. Licznik pobrań wzrośnie o 1."
        }
        meta={
          downloadTarget
            ? {
                "Plik": downloadTarget.filename,
                "Format": (downloadTarget.format || "").toUpperCase(),
                "Paczek": downloadTarget.package_count || (downloadTarget.package_ids?.length || 0),
                "Kwota": formatAmount(downloadTarget.total_amount, downloadTarget.currency),
              }
            : null
        }
        confirmLabel={isReady ? "Pobierz" : "Pobierz ponownie"}
        cancelLabel="Anuluj"
        variant="primary"
        testIdPrefix="pkg-file-download-confirm"
      />

      {/* Modal potwierdzenia usunięcia eksportu (Basic manual workflow, 2026-02).
          Dawniej „Cofnij eksport". Zmiana nazewnictwa na „Usuń" (2026-02):
          - user testuje flow cofania statusów po usunięciu paczki eksportu;
          - biznesowo: usuwa plik z Do pobrania i przywraca paczki do edycji;
          - NIE usuwa paczek fizycznie, NIE usuwa faktur, backend zachowuje
            audit trail (`status="cancelled"`). */}
      <SimpleConfirmDialog
        open={!!cancelExportTarget}
        onClose={() => setCancelExportTarget(null)}
        onConfirm={async () => {
          const target = cancelExportTarget;
          setCancelExportTarget(null);
          if (target) await performCancelExport(target);
        }}
        title={isReady ? "Usunąć eksport?" : "Cofnąć eksport?"}
        body={
          isReady
            ? "To usunie gotowy plik z listy Do pobrania i przywróci powiązane paczki do edycji. Paczki i faktury nie zostaną skasowane."
            : "To przywróci powiązane paczki do edycji i pozwoli wygenerować plik ponownie, np. w innym formacie. Faktury nie zostaną usunięte ani oznaczone jako zapłacone."
        }
        meta={
          cancelExportTarget
            ? {
                "Plik": cancelExportTarget.filename,
                "Format": (cancelExportTarget.format || "").toUpperCase(),
                "Paczek": cancelExportTarget.package_count || (cancelExportTarget.package_ids?.length || 0),
                "Kwota": formatAmount(cancelExportTarget.total_amount, cancelExportTarget.currency),
              }
            : null
        }
        confirmLabel={isReady ? "Usuń" : "Cofnij eksport"}
        cancelLabel="Anuluj"
        variant="danger"
        testIdPrefix={isReady ? "pkg-file-delete-confirm" : "pkg-file-revert-confirm"}
      />

      {/* 2026-07 v3: Modal „Oznacz jako zapłacone" */}
      <SimpleConfirmDialog
        open={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={async () => {
          const target = markPaidTarget;
          setMarkPaidTarget(null);
          if (target) await performMarkAsPaid(target);
        }}
        title="Oznaczyć przelewy jako zapłacone?"
        body={
          "Potwierdzasz, że plik został zaimportowany do banku, a przelewy zostały zaakceptowane lub wykonane. "
          + "Powiązane faktury zostaną oznaczone jako zapłacone i nie będą już proponowane do kolejnych paczek płatności."
        }
        meta={
          markPaidTarget
            ? {
                "Plik": markPaidTarget.filename,
                "Format": (markPaidTarget.format || "").toUpperCase(),
                "Paczek": markPaidTarget.package_count || (markPaidTarget.package_ids?.length || 0),
                "Przelewów": markPaidTarget.transfers_count,
                "Kwota": formatAmount(markPaidTarget.total_amount, markPaidTarget.currency),
              }
            : null
        }
        confirmLabel="Oznacz jako zapłacone"
        cancelLabel="Anuluj"
        variant="success"
        testIdPrefix="pkg-file-mark-paid-confirm"
      />

      {/* 2026-07 v3: Modal „Cofnij oznaczenie zapłacone" */}
      <SimpleConfirmDialog
        open={!!unmarkPaidTarget}
        onClose={() => setUnmarkPaidTarget(null)}
        onConfirm={async () => {
          const target = unmarkPaidTarget;
          setUnmarkPaidTarget(null);
          if (target) await performUnmarkAsPaid(target);
        }}
        title="Cofnąć oznaczenie zapłacone?"
        body={
          "To przywróci powiązane faktury do obsługi płatności. "
          + "Używaj tylko wtedy, gdy oznaczenie było pomyłką."
        }
        meta={
          unmarkPaidTarget
            ? {
                "Plik": unmarkPaidTarget.filename,
                "Zapłacone": formatDate(unmarkPaidTarget.payment_confirmed_at),
              }
            : null
        }
        confirmLabel="Cofnij oznaczenie"
        cancelLabel="Anuluj"
        variant="danger"
        testIdPrefix="pkg-file-unmark-paid-confirm"
      />
    </div>
  );
};

// --------------------------------------------------------------------------
// Main page
// --------------------------------------------------------------------------
export default function PaymentPackagesPage() {
  // Pakiet 1: najgęstsza strona aplikacji — szerszy kontener + kontrolowana szerokość.
  usePageContainer("wide");
  // Sprint 2 / E5: Global Readiness Gate — backend `GET /api/payment-agent/readiness`
  // jest jedynym źródłem prawdy dla stanu workflow. Hook fetche + polling +
  // integracja z `pax:packages:refresh` event bus.
  const {
    readiness: paxReadiness,
    loading: paxReadinessLoading,
    error: paxReadinessError,
    refetch: refetchPaxReadiness,
  } = usePaymentAgentReadiness();
  const [tab, setTab] = useState("contractors");
  // 2026-02 UX refactor: domyślnie w zakładce Eksport user widzi
  // „Paczki do eksportu". Po realnym utworzeniu pliku parent przełącza
  // na „ready_to_download".
  const [exportPushedSection, setExportPushedSection] = useState("drafts_for_export");
  const [contractors, setContractors] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(true);
  const [loadingTab2, setLoadingTab2] = useState(true);
  // 2026-07 v5: Trwały job analizy Payment Agenta.
  // Frontend czyta status z backendu (`GET /analyze-jobs/current` co 2s).
  // Po refreshu strony pobieramy stan przy mount — user widzi progress
  // bar i nie może odpalić drugiej analizy dopóki pierwsza trwa.
  const [currentJob, setCurrentJob] = useState(null);  // {job_id, status, done_count, total_count, ...}
  const analyzing = !!currentJob && ["queued", "running"].includes(currentJob.status);
  const [lastAnalysisDebugInfo, setLastAnalysisDebugInfo] = useState(null);
  // Sprint 2 / E5.1 (final cleanup): Readiness dla badge „Analiza AI płatności"
  // pochodzi WYŁĄCZNIE z unified `usePaymentAgentReadiness` (via `paxReadiness`)
  // przetworzonego przez `derivePaxBadgeReadiness`. Legacy fetch
  // `/payment-packages/readiness` został usunięty — cały UI Payment Agenta
  // korzysta z jednego backendowego endpointu.
  const readiness = useMemo(
    () => derivePaxBadgeReadiness(paxReadiness),
    [paxReadiness],
  );

  const fetchContractors = useCallback(async () => {
    setLoadingContractors(true);
    try {
      const res = await axios.get(`${API}/payment-packages/contractors`, { withCredentials: true });
      setContractors(res.data?.contractors || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd pobierania kontrahentów.");
      setContractors([]);
    } finally {
      setLoadingContractors(false);
    }
  }, []);

  const fetchTab2 = useCallback(async () => {
    setLoadingTab2(true);
    try {
      const [propRes, pkgRes] = await Promise.all([
        axios.get(`${API}/payment-packages/proposals`, { withCredentials: true }),
        axios.get(`${API}/payment-packages`, { withCredentials: true }),
      ]);
      setProposals(propRes.data?.proposals || []);
      setPackages(pkgRes.data?.packages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd pobierania danych.");
      setProposals([]);
      setPackages([]);
    } finally {
      setLoadingTab2(false);
    }
  }, []);

  useEffect(() => {
    fetchContractors();
    fetchTab2();
  }, [fetchContractors, fetchTab2]);

  // Fix 2026-02: nasłuchujemy globalnego eventu `pax:packages:refresh`.
  // Emitowany przez `PaymentFilesArchiveTab.performCancelExport` po usunięciu
  // eksportu — paczki wracają do `draft` i muszą być odświeżone w widoku
  // Paczek. Bez tego listenera user musiał ręcznie przeładować stronę.
  // E5.1: readiness leci przez `usePaymentAgentReadiness` (własny polling +
  // event bus), więc tutaj nie trzeba manualnego refetch.
  useEffect(() => {
    const handler = () => {
      fetchTab2();
      refetchPaxReadiness();
    };
    window.addEventListener("pax:packages:refresh", handler);
    return () => window.removeEventListener("pax:packages:refresh", handler);
  }, [fetchTab2, refetchPaxReadiness]);

  // ── Payment Agent Jobs (2026-07 v5) ──────────────────────────────────────
  // Fetch aktualnego joba z backendu — pojedynczy strzał (dla mount + toast trigger).
  const fetchCurrentJob = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payment-packages/analyze-jobs/current`, { withCredentials: true });
      const j = r.data?.job || null;
      setCurrentJob(j);
      return j;
    } catch (_e) {
      setCurrentJob(null);
      return null;
    }
  }, []);

  // Mount: pobierz aktualny stan joba — jeśli user odświeżył podczas analizy,
  // UI wznawia progress bar bez utraty stanu.
  useEffect(() => {
    fetchCurrentJob();
  }, [fetchCurrentJob]);

  // Polling co 2s podczas aktywnego joba. Kończy się gdy job w terminal state.
  useEffect(() => {
    if (!analyzing) return;
    const iv = setInterval(async () => {
      const j = await fetchCurrentJob();
      if (j && ["completed", "failed", "cancelled"].includes(j.status)) {
        // Job właśnie się zakończył — odśwież listy + toast + tab switch.
        clearInterval(iv);
        const parts = [];
        if ((j.success_count || 0) > 0) parts.push(`utworzono ${j.success_count} propozycji`);
        if ((j.needs_review_count || 0) > 0) parts.push(`${j.needs_review_count} wymaga przeglądu`);
        if ((j.error_count || 0) > 0) parts.push(`${j.error_count} błędów`);
        // Pakiet A 2026-02: policz skipy z powodu bramki XML — pokażemy
        // je jako osobny fragment podsumowania. `skipped_vendors[]`
        // zawiera obiekty {vendor_nip, reason, reason_details}.
        const xmlSkipped = Array.isArray(j.skipped_vendors)
          ? j.skipped_vendors.filter((sv) => [
              "pending_xml_enrichment",
              "legacy_schema_unsupported",
              "xml_enrichment_failed",
            ].includes(sv?.reason))
          : [];
        if (xmlSkipped.length > 0) {
          const pendingN = xmlSkipped.filter((sv) => sv.reason === "pending_xml_enrichment").length;
          const legacyN = xmlSkipped.filter((sv) => sv.reason === "legacy_schema_unsupported").length;
          const failedN = xmlSkipped.filter((sv) => sv.reason === "xml_enrichment_failed").length;
          const bits = [];
          if (pendingN > 0) bits.push(`${pendingN} czeka na dane KSeF`);
          if (legacyN > 0) bits.push(`${legacyN} w niewspieranym schemacie`);
          if (failedN > 0) bits.push(`${failedN} z błędem KSeF`);
          if (bits.length > 0) parts.push(`pominięto: ${bits.join(", ")}`);
        }
        if (j.status === "cancelled") {
          toast.info(`Analiza anulowana (przeanalizowano ${j.done_count} z ${j.total_count}).`);
        } else if (parts.length > 0) {
          toast.success(`Analiza zakończona — ${parts.join(", ")}.`);
        }
        await Promise.all([fetchContractors(), fetchTab2()]);
        if ((j.success_count || 0) > 0 || (j.needs_review_count || 0) > 0) {
          setTab("packages");
        }
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [analyzing, fetchCurrentJob, fetchContractors, fetchTab2]);

  const handleAnalyze = async (vendor_nips) => {
    // 2026-07 v5: Zamiast sekwencyjnego loop na froncie — jeden POST tworzący
    // backend job. Analiza działa w tle (`asyncio.create_task`), UI polluje
    // co 2s przez `useEffect` powyżej. Refresh nie gubi stanu.
    const list = Array.isArray(vendor_nips) ? vendor_nips : [vendor_nips];
    if (list.length === 0) return;

    // Sprint 2 / E5: sprawdź readiness PRZED requestem — unikamy niepotrzebnego
    // 423 i pokazujemy user user-friendly komunikat od razu. Backend nadal
    // jest ostatecznym guardrailem (server-side validation).
    if (paxReadiness && !paxReadiness.ready) {
      const { pickPrimaryReason } = await import("@/lib/paymentAgentReadinessMessages");
      const primary = pickPrimaryReason(paxReadiness.blocking_reasons || []);
      const msg = primary?.message || "Analiza jest chwilowo niedostępna.";
      toast.warning(msg);
      return;
    }

    try {
      const r = await axios.post(
        `${API}/payment-packages/analyze-jobs`,
        { vendor_nips: list },
        { withCredentials: true },
      );
      setCurrentJob(r.data);
      toast.info(`Analiza rozpoczęta (${list.length} ${list.length === 1 ? "kontrahent" : "kontrahentów"}).`);
      // E5: refresh readiness — job właśnie stał się aktywny, statuas ma to odzwierciedlać.
      refetchPaxReadiness();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Job już aktywny — sync stan i pokaż komunikat.
        const existing = e.response.data?.detail?.job;
        if (existing) setCurrentJob(existing);
        toast.warning(e.response.data?.detail?.message || "Analiza już trwa.");
      } else if (status === 423) {
        const detail = e.response.data?.detail || {};
        toast.warning(detail.message || "Paczka chwilowo zablokowana.");
        refetchPaxReadiness();
      } else {
        toast.error(e?.response?.data?.detail?.message || "Nie udało się uruchomić analizy.");
      }
    }
  };

  const handleCancelAnalysis = async () => {
    if (!currentJob) return;
    try {
      await axios.post(
        `${API}/payment-packages/analyze-jobs/${currentJob.job_id}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.info("Anulowanie w toku…");
      await fetchCurrentJob();
    } catch (_e) {
      toast.error("Nie udało się anulować analizy.");
    }
  };

  // ── Autoanaliza nowych kontrahentów (2026-02 round 9) ─────────────────
  // Backend jest jedynym Source of Truth. Frontend:
  //  1. Pobiera ustawienie z `/api/agent-settings` przy mount.
  //  2. Renderuje checkbox z aktualnym stanem.
  //  3. On toggle → PUT do settingu, po włączeniu → POST `/analyze-jobs/auto-run`
  //     (idempotentny fire-and-forget — backend sam sprawdza blockery
  //     i zwraca `status: disabled|skipped_active_job|skipped_workflow_busy|
  //     up_to_date|started`).
  //  4. Po `pax:packages:refresh` (np. import KSeF) → jeśli setting ON,
  //     ponowne wywołanie `auto-run` (backend znajdzie nowych kontrahentów).
  //
  // Nie duplikujemy logiki decyzyjnej we froncie — Reactowi wolno tylko
  // pokazywać stan i pingować backend.
  const [autoAnalyzeEnabled, setAutoAnalyzeEnabled] = useState(false);
  const [autoAnalyzeBusy, setAutoAnalyzeBusy] = useState(true);

  const fetchAutoAnalyzeSetting = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agent-settings`, { withCredentials: true });
      const val = !!(r.data?.payment_agent?.automatically_analyze_new_contractors);
      setAutoAnalyzeEnabled(val);
    } catch (_e) {
      // Silent — brak ustawień = OFF (default z backendu).
      setAutoAnalyzeEnabled(false);
    } finally {
      setAutoAnalyzeBusy(false);
    }
  }, []);

  // Idempotentny trigger — bezpieczny do wielokrotnego wywołania.
  const triggerAutoRun = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API}/payment-packages/analyze-jobs/auto-run`,
        {},
        { withCredentials: true },
      );
      const status = r.data?.status;
      if (status === "started" && r.data.job) {
        setCurrentJob(r.data.job);
        toast.info(
          `Autoanaliza rozpoczęta — nowych kontrahentów: ${r.data.count}.`,
        );
      }
      // Pozostałe statusy (disabled / up_to_date / skipped_*) — cicho.
    } catch (_e) {
      // Silent — endpoint jest fire-and-forget; nie chcemy alertować usera.
    }
  }, []);

  const handleToggleAutoAnalyze = useCallback(async (checked) => {
    setAutoAnalyzeBusy(true);
    // Optimistic — od razu przełącz UI, żeby user widział feedback.
    setAutoAnalyzeEnabled(!!checked);
    try {
      await axios.put(
        `${API}/agent-settings`,
        { payment_agent: { automatically_analyze_new_contractors: !!checked } },
        { withCredentials: true },
      );
      if (checked) {
        // Włączenie → od razu spróbuj przeanalizować nowych.
        await triggerAutoRun();
      }
    } catch (_e) {
      // Rollback state — zapis nie udał się.
      setAutoAnalyzeEnabled(!checked);
      toast.error("Nie udało się zapisać ustawienia.");
    } finally {
      setAutoAnalyzeBusy(false);
    }
  }, [triggerAutoRun]);

  // Fetch settingu na mount.
  useEffect(() => {
    fetchAutoAnalyzeSetting();
  }, [fetchAutoAnalyzeSetting]);

  // Po każdym `pax:packages:refresh` (np. import KSeF, zmiana metody
  // płatności) — jeśli setting ON, wywołaj auto-run. Backend sam sprawdzi
  // czy są nowi kontrahenci i czy nie ma blockerów.
  useEffect(() => {
    if (!autoAnalyzeEnabled) return;
    const onRefresh = () => { triggerAutoRun(); };
    window.addEventListener("pax:packages:refresh", onRefresh);
    return () => window.removeEventListener("pax:packages:refresh", onRefresh);
  }, [autoAnalyzeEnabled, triggerAutoRun]);

  // Po mount z włączonym settingiem — spróbuj auto-run (może są nowi
  // kontrahenci z poprzedniej sesji).
  useEffect(() => {
    if (autoAnalyzeEnabled && !autoAnalyzeBusy) {
      triggerAutoRun();
    }
  }, [autoAnalyzeEnabled, autoAnalyzeBusy, triggerAutoRun]);

  // Optimistic update po zmianie metody płatności z inline-panelu w
  // ContractorsTab — odświeża `effective_payment_method` w state bez
  // czekania na pełny refetch. Pełny refetch i tak jest wywoływany po
  // pętli analizy / po zamknięciu dialogu.
  //
  // 2026-02 round 8: dispatch globalnego eventu `pax:packages:refresh` —
  // sygnał dla InvoicesPage (i wszystkich innych modułów) że globalny
  // `workflow_status` kontrahenta się zmienił. Bez tego faktury tego
  // kontrahenta zostają w widoku „Do zapłaty" mimo zmiany metody
  // (backend zwraca poprawnie odfiltrowaną listę, ale frontend jeszcze
  // trzyma poprzedni response). Event jest już nasłuchiwany w:
  //   - InvoicesPage (fetchInvoices, fetchMonthlyPaid),
  //   - PaymentPackagesPage.PaymentFilesArchiveTab (refetch listy).
  const handleMethodChanged = useCallback((nip, method) => {
    setContractors((prev) => prev.map((c) =>
      c.vendor_nip === nip
        ? {
            ...c,
            effective_payment_method: method,
            effective_payment_method_source: "manual_override",
            is_user_confirmed: true,
          }
        : c
    ));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("pax:packages:refresh"));
    }
  }, []);

  // Callback po ręcznym utworzeniu paczki w `ManualPackageBuilder`.
  // Faktury zmieniają status na `in_batch`, więc pełny refetch kontrahentów
  // + tab2 (żeby paczka draft pojawiła się w zakładce Paczki).
  const handlePackageCreated = useCallback(async () => {
    await Promise.all([fetchContractors(), fetchTab2()]);
  }, [fetchContractors, fetchTab2]);

  const handleAccept = async (packageId) => {
    try {
      await axios.post(`${API}/payment-packages/proposals/${packageId}/accept`, {}, { withCredentials: true });
      toast.success("Propozycja zaakceptowana — paczka utworzona.");
      await Promise.all([fetchContractors(), fetchTab2()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd akceptacji.");
    }
  };

  const handleReject = async (packageId) => {
    try {
      await axios.post(`${API}/payment-packages/proposals/${packageId}/reject`, {}, { withCredentials: true });
      toast.success("Propozycja odrzucona.");
      await Promise.all([fetchContractors(), fetchTab2()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd odrzucania.");
    }
  };

  const handleCancel = async (packageId) => {
    try {
      await axios.post(`${API}/payment-packages/${packageId}/cancel`, {}, { withCredentials: true });
      toast.success("Paczka anulowana.");
      await Promise.all([fetchContractors(), fetchTab2()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd anulowania.");
    }
  };

  // Pakiet A Etap 1 (2026-02-24): Transition `draft → ready_for_export`.
  // Blokuje paczkę do edycji, przygotowuje do wygenerowania pliku bankowego.
  // Backend waliduje że total_amount > 0 i payable_sum > 0.
  const handleLockForExport = async (packageId) => {
    try {
      const r = await axios.post(
        `${API}/payment-packages/packages/${packageId}/lock-for-export`,
        {},
        { withCredentials: true },
      );
      toast.success("Paczka zablokowana i gotowa do eksportu.");
      await Promise.all([fetchContractors(), fetchTab2()]);
      return r.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Nie udało się zablokować paczki.";
      toast.error(typeof msg === "string" ? msg : "Nie udało się zablokować paczki.");
    }
  };

  // Pakiet A Etap 1: Cofnięcie `ready_for_export → draft`. Idempotentne
  // gdy paczka już w `draft`. Niedostępne po `exported` (backend zwróci 409).
  const handleUnlockToDraft = async (packageId) => {
    try {
      await axios.post(
        `${API}/payment-packages/packages/${packageId}/unlock-to-draft`,
        {},
        { withCredentials: true },
      );
      toast.success("Paczka odblokowana do edycji.");
      await Promise.all([fetchContractors(), fetchTab2()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się odblokować paczki.");
    }
  };

  // Liczniki potrzebne wyłącznie do labelek tabsów / sterowania widokiem.
  // KPI bar usunięty w 2026-02 UI polish — statystyki przeniesione na Dashboard.
  // Filtracja per zakładka (2026-02): Przelewy = TRANSFER_METHODS,
  // Pozostali = OTHERS_METHODS. Podział na dwie grupy jest wyłącznie
  // UI-owy — po stronie backendu nadal trzymamy pełną listę.
  // 2026-02 unify: klasyfikacja Kontrahenci/Pozostali używa `isInOthersTab`,
  // który uwzględnia `workflow_status` (regułę „wszystko zapłacone w KSeF")
  // OBOK `effective_payment_method` — bez modyfikacji `payment_method`.
  // 2026-02-22 (PART 1-4 „Do zapłaty"): zakładka to KOLEJKA PRACY —
  // pokazuje wyłącznie kontrahentów z `requires_payment_action=true`
  // (co najmniej jedna faktura wymaga działania). Kontrahent bez aktywnych
  // faktur znika z „Do zapłaty" (ale nie z bazy) — wraca automatycznie
  // po imporcie nowej faktury z KSeF.
  const contractorsTransfer = useMemo(
    () => contractors.filter((c) => !isInOthersTab(c) && (c?.requires_payment_action === true)),
    [contractors],
  );
  const contractorsOthers = useMemo(
    () => contractors.filter((c) => isInOthersTab(c)),
    [contractors],
  );
  const transferCount = contractorsTransfer.length;
  const othersCount = contractorsOthers.length;
  const activeProposals = proposals.filter((p) => p.status === "proposal" || p.status === "needs_review");
  const draftPackages = packages.filter((p) => p.status === "draft");

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="payment-packages-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="pkg-page-title">
          <Package className="w-6 h-6 text-indigo-600" />
          Paczki płatności
        </h1>
      </div>

      {/* Sprint 2 / E5: Global Readiness Gate — status widoczny nad zakładkami.
          Backend jest jedynym źródłem prawdy (`GET /api/payment-agent/readiness`).
          Frontend wyłącznie prezentuje kontrakt. */}
      <PaymentAgentReadinessStatus
        readiness={paxReadiness}
        loading={paxReadinessLoading}
        error={paxReadinessError}
        className="mb-4"
      />

      {/* Nawigacja kaflowa — 4 zakładki (2026-02 UX polish):
          Przelewy | Pozostali | Paczki | Bank.
          Mobile: siatka 2×2 (wszystkie 4 kafle widoczne bez scrolla).
          Desktop: jeden rząd 4 kafli. Sam tekst, bez ikon w kaflu. */}
      <div
        role="tablist"
        aria-label="Sekcje Paczek Płatności"
        data-testid="pkg-tabs"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4"
      >
        <button
          onClick={() => setTab("contractors")}
          role="tab"
          aria-selected={tab === "contractors"}
          className={`min-h-[92px] sm:min-h-[56px] rounded-xl border-2 px-4 py-3 text-base font-semibold text-center transition-colors ${
            tab === "contractors"
              ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm"
              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
          }`}
          data-testid="pkg-tab-contractors"
        >
          Do zapłaty{transferCount > 0 ? ` (${transferCount})` : ""}
        </button>
        <button
          onClick={() => setTab("others")}
          role="tab"
          aria-selected={tab === "others"}
          className={`min-h-[92px] sm:min-h-[56px] rounded-xl border-2 px-4 py-3 text-base font-semibold text-center transition-colors ${
            tab === "others"
              ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm"
              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
          }`}
          data-testid="pkg-tab-others"
        >
          Pozostali{othersCount > 0 ? ` (${othersCount})` : ""}
        </button>
        <button
          onClick={() => setTab("packages")}
          role="tab"
          aria-selected={tab === "packages"}
          className={`min-h-[92px] sm:min-h-[56px] rounded-xl border-2 px-4 py-3 text-base font-semibold text-center transition-colors ${
            tab === "packages"
              ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm"
              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
          }`}
          data-testid="pkg-tab-packages"
        >
          Paczki{(activeProposals.length + draftPackages.length) > 0 ? ` (${activeProposals.length + draftPackages.length})` : ""}
        </button>
        <button
          onClick={() => setTab("bank")}
          role="tab"
          aria-selected={tab === "bank"}
          className={`min-h-[92px] sm:min-h-[56px] rounded-xl border-2 px-4 py-3 text-base font-semibold text-center transition-colors ${
            tab === "bank"
              ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm"
              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
          }`}
          data-testid="pkg-tab-bank"
        >
          Eksport
        </button>
      </div>

      {tab === "contractors" ? (
        <ContractorsTab
          mode="transfer"
          contractors={contractorsTransfer}
          loading={loadingContractors}
          onAnalyze={handleAnalyze}
          analyzing={analyzing}
          lastAnalysisDebugInfo={lastAnalysisDebugInfo}
          onMethodChanged={handleMethodChanged}
          onPackageCreated={handlePackageCreated}
          readiness={readiness}
          currentJob={currentJob}
          onCancelAnalysis={handleCancelAnalysis}
          autoAnalyzeEnabled={autoAnalyzeEnabled}
          autoAnalyzeBusy={autoAnalyzeBusy}
          onToggleAutoAnalyze={handleToggleAutoAnalyze}
        />
      ) : tab === "others" ? (
        <ContractorsTab
          mode="others"
          contractors={contractorsOthers}
          loading={loadingContractors}
          onAnalyze={handleAnalyze}
          analyzing={false}
          lastAnalysisDebugInfo={null}
          onMethodChanged={handleMethodChanged}
        />
      ) : tab === "packages" ? (
        <PackagesTab
          proposals={proposals}
          packages={packages}
          loading={loadingTab2}
          onAccept={handleAccept}
          onReject={handleReject}
          onCancel={handleCancel}
          onLock={handleLockForExport}
          onUnlock={handleUnlockToDraft}
        />
      ) : (
        <PaymentFilesArchiveTab
          initialSection={exportPushedSection}
          onExportSuccess={async () => {
            // 2026-02 UX refactor: po utworzeniu pliku w sub-zakładce
            // „Paczki do eksportu" → automatycznie przeskoczyć na
            // sub-zakładkę „Do pobrania". Backend zamienił draft → exported,
            // paczek już nie ma w liście draftów.
            await fetchTab2();
            setExportPushedSection("ready_to_download");
          }}
        />
      )}
    </div>
  );
}
